import { useState, useCallback, useMemo, useRef, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, message, Modal } from 'antd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
    ReactFlow,
    ReactFlowProvider,
    Background,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    applyEdgeChanges,
    addEdge,
    ConnectionLineType,
    useOnSelectionChange,
    getNodesBounds,
} from '@xyflow/react';
import Sidebar from './Sidebar';
import ProjectHeader from './components/ProjectHeader';
import ZoomToolbar from './components/ZoomToolbar';
import CanvasToolbar from './components/CanvasToolbar';
import CanvasEmpty from './components/CanvasEmpty';
import CanvasContextMenu from './components/CanvasContextMenu';
import GroupActionToolbar from './components/GroupActionToolbar';
import GenerationHistoryModal from './components/GenerationHistoryModal';
import MaterialLibraryModal from './components/MaterialLibraryModal';
import TemplateLibraryModal from './components/TemplateLibraryModal';
import { EXAMPLE_CARD_SAMPLE_IMAGE_BASE_URL } from '@view/page_v3/CreativeToImage/components/ExampleCards';
import { ImageNode, FunctionNode, GroupNode, FlowEdge } from './shapes';
import { FUNCTION_LABELS, FUNCTION_NODE_GROUPS } from './components/AddNodePopover';
import {
    applyTemplateToCanvas,
    batchUpdateFlowCanvas,
    createFlowProject,
    deleteFlowProject,
    duplicateFlowProject,
    executeFlowGroup,
    executeFlowNode,
    getFlowExecutionBatch,
    getFlowProjectDetail,
    getFlowProjectPage,
    getFlowTemplateDetail,
    pinFlowProject,
    resumeFlowExecution,
    saveFlowProject,
    saveFlowTemplate,
    selectFlowExecutionPrimaryOutput,
    sortFlowProject,
    uploadFlowImage, 
} from './execution';
import lookTemplate from './hooks/look.json';
import tryonTemplate from './hooks/tryon.json';
import {
    getFunctionNodeEdgeSlotKeys,
    getFunctionNodeEdgeSlotOptions,
    reconcileEdgeSlotAssignment,
} from './constants/edgeInputSlots';
import styles from './index.module.scss';
import '@xyflow/react/dist/style.css';
import { AppContext } from '@utils/AppContext';
import eventBus from '@utils/eventBus';
import LoggedHead from '@view/compoents/head/loggedHead/loggedHead';

const FREE_CANVAS_ICON_BASE_URL = 'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas';
const IMAGE_NODE_WIDTH = 240; // 图片节点默认宽度
const IMAGE_NODE_HEIGHT = 320; // 图片节点默认高度
// 图片节点标题前缀
const IMAGE_NODE_PREFIX = '图';
const MIN_ZOOM_PERCENT = 10; // 画布最小缩放百分比
const MAX_ZOOM_PERCENT = 200; // 画布最大缩放百分比
const WHEEL_ZOOM_STEP = 20; // 滚轮缩放步进
const WHEEL_PAN_SPEED = 0.35; // 滚轮平移速度
const UPLOAD_IMAGE_ACCEPT = 'image/*'; // 图片上传类型限制
const IMAGE_NODE_GAP = 24; // 批量插入图片节点时的间距
const HISTORY_LIMIT = 50; // 撤销历史最大保留数量
const PASTE_OFFSET_STEP = 32; // 连续粘贴时的位移步进
const CONNECTED_NODE_OFFSET_X = 320; // 快速追加节点时的横向偏移
const CONNECTED_NODE_OFFSET_Y = 0; // 快速追加节点时的纵向偏移
const MANUAL_ADD_OFFSET_STEP = 36; // 手动新增节点的错位步进
const MANUAL_ADD_OFFSET_WRAP = 6; // 手动新增节点换行前的数量
const DUPLICATE_OFFSET_STEP = 40; // 复制节点时的位移步进
const GROUP_PADDING_X = 24; // 分组左右内边距
const GROUP_PADDING_BOTTOM = 24; // 分组底部内边距
const GROUP_HEADER_HEIGHT = 56; // 分组头部高度
const GROUP_MIN_WIDTH = 320; // 分组最小宽度
const GROUP_MIN_HEIGHT = 220; // 分组最小高度
const DEFAULT_VIEWPORT = { x: 0, y: 0, zoom: 1 }; // 画布默认视口
const PROJECT_PAGE_SIZE = 20; // 项目列表分页大小
// 各功能节点允许接入的最大输入数量
const LAST_ACTIVE_PROJECT_STORAGE_KEY = 'free_canvas_last_active_project_id';
const LAST_ACTIVE_ORG_KEY_STORAGE_KEY = 'free_canvas_last_active_org_key';
const RUN_GROUP_SKIP_SELECT_STORAGE_KEY = 'free_canvas_run_group_skip_select_v2';
const TEMPLATE_PREVIEW_PADDING = 24;
const TEMPLATE_PREVIEW_EXPORT_SCALE = 2;
const TEMPLATE_EXPORT_BACKGROUND = '#F3F3F5';
const TEMPLATE_RENDER_PLACEHOLDER_IMAGE_URLS = new Set([
    'https://photostudio-ai-oss.arcsoft.com.cn//photostudio-opshub/FreeCanvas/node-image.svg',
    'https://alioss-ai.arcsoft.com.cn//photostudio-opshub/FreeCanvas/node-image.svg',
]);

const sanitizeTemplateCoverData = (value, fallbackValue = null) => {
    try {
        return JSON.parse(JSON.stringify(value ?? fallbackValue));
    } catch (error) {
        return fallbackValue;
    }
};

const buildTemplateCoverPayload = ({ groupNodeId, nodes, edges, nodeMap }) => {
    if (!nodes.length) {
        throw new Error('未找到可保存的分组内容');
    }

    const groupNode = nodeMap.get(groupNodeId) || null;
    const bounds = getNodesBounds(nodes);
    const width = Math.max(1, Math.round(bounds.width + TEMPLATE_PREVIEW_PADDING * 2));
    const height = Math.max(1, Math.round(bounds.height + TEMPLATE_PREVIEW_PADDING * 2));

    return {
        schema: 'free-canvas-preview-v1',
        groupNodeId: String(groupNodeId),
        background: TEMPLATE_EXPORT_BACKGROUND,
        width,
        height,
        padding: TEMPLATE_PREVIEW_PADDING,
        bounds: {
            x: Math.round(bounds.x),
            y: Math.round(bounds.y),
            width: Math.round(bounds.width),
            height: Math.round(bounds.height),
        },
        group: groupNode ? {
            id: String(groupNode.id),
            label: groupNode?.data?.label || groupNode?.data?.nodeName || '',
            width: Math.round(getNodeSize(groupNode, GROUP_MIN_WIDTH, GROUP_MIN_HEIGHT).width),
            height: Math.round(getNodeSize(groupNode, GROUP_MIN_WIDTH, GROUP_MIN_HEIGHT).height),
        } : null,
        nodes: nodes.map((node) => {
            const { width: nodeWidth, height: nodeHeight } = getNodeSize(node);

            return {
                id: String(node.id),
                type: node.type,
                position: {
                    x: Math.round(node.position.x - bounds.x + TEMPLATE_PREVIEW_PADDING),
                    y: Math.round(node.position.y - bounds.y + TEMPLATE_PREVIEW_PADDING),
                },
                width: Math.round(nodeWidth),
                height: Math.round(nodeHeight),
                data: sanitizeTemplateCoverData(node.data, {}),
            };
        }),
        edges: edges.map((edge) => ({
            id: String(edge.id),
            type: edge.type,
            source: String(edge.source),
            target: String(edge.target),
            data: sanitizeTemplateCoverData(edge.data, {}),
        })),
    };
};

const getTemplateNodePreviewImage = (node) => {
    const data = node?.data || {};
    const latestOutput = Array.isArray(data?.latestExecution?.outputs)
        ? data.latestExecution.outputs.find((item) => item?.type === 'image' && item?.url)
        : null;
    const candidates = [
        latestOutput?.url,
        data?.src,
        data?.inputParams?.output,
        data?.inputParams?.srcImage,
    ].filter(Boolean);

    return candidates.find((url) => !TEMPLATE_RENDER_PLACEHOLDER_IMAGE_URLS.has(url)) || '';
};

const loadCanvasImage = async (url) => {
    if (!url) return null;

    try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) return null;

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        try {
            const image = await new Promise((resolve, reject) => {
                const nextImage = new Image();
                nextImage.onload = () => resolve(nextImage);
                nextImage.onerror = () => reject(new Error('image load failed'));
                nextImage.src = objectUrl;
            });

            return image;
        } finally {
            URL.revokeObjectURL(objectUrl);
        }
    } catch (error) {
        return null;
    }
};

const drawRoundedRectPath = (context, x, y, width, height, radius) => {
    const nextRadius = Math.min(radius, width / 2, height / 2);
    context.beginPath();
    context.moveTo(x + nextRadius, y);
    context.arcTo(x + width, y, x + width, y + height, nextRadius);
    context.arcTo(x + width, y + height, x, y + height, nextRadius);
    context.arcTo(x, y + height, x, y, nextRadius);
    context.arcTo(x, y, x + width, y, nextRadius);
    context.closePath();
};

const drawTemplateEdgeLabel = (context, label, centerX, centerY) => {
    if (!label) return;

    const width = 68;
    const height = 24;
    const x = centerX - width / 2;
    const y = centerY - height / 2;

    context.save();
    drawRoundedRectPath(context, x, y, width, height, 12);
    context.fillStyle = 'rgba(255, 255, 255, 0.92)';
    context.fill();
    context.strokeStyle = '#E6EBF2';
    context.lineWidth = 1;
    context.stroke();
    context.fillStyle = '#7A8495';
    context.font = '11px sans-serif';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(String(label).slice(0, 8), centerX, centerY + 0.5);
    context.restore();
};

const wouldCreateCycle = (edges = [], sourceNodeId = '', targetNodeId = '') => {
    const normalizedSourceId = String(sourceNodeId || '');
    const normalizedTargetId = String(targetNodeId || '');
    if (!normalizedSourceId || !normalizedTargetId) return false;
    if (normalizedSourceId === normalizedTargetId) return true;

    const adjacencyMap = edges.reduce((acc, edge) => {
        const fromId = String(edge?.source || '');
        const toId = String(edge?.target || '');
        if (!fromId || !toId) return acc;
        if (!acc.has(fromId)) {
            acc.set(fromId, []);
        }
        acc.get(fromId).push(toId);
        return acc;
    }, new Map());

    const visited = new Set();
    const stack = [normalizedTargetId];

    while (stack.length > 0) {
        const currentNodeId = stack.pop();
        if (!currentNodeId || visited.has(currentNodeId)) continue;
        if (currentNodeId === normalizedSourceId) return true;
        visited.add(currentNodeId);

        const nextNodeIds = adjacencyMap.get(currentNodeId) || [];
        nextNodeIds.forEach((nextNodeId) => {
            if (!visited.has(nextNodeId)) {
                stack.push(nextNodeId);
            }
        });
    }

    return false;
};

const renderTemplateCoverToFile = async (payload, fileName) => {
    const width = Math.max(1, Math.round((payload?.width || IMAGE_NODE_WIDTH) * TEMPLATE_PREVIEW_EXPORT_SCALE));
    const height = Math.max(1, Math.round((payload?.height || IMAGE_NODE_HEIGHT) * TEMPLATE_PREVIEW_EXPORT_SCALE));
    const scale = TEMPLATE_PREVIEW_EXPORT_SCALE;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
        throw new Error('模板封面生成失败');
    }

    context.scale(scale, scale);
    context.fillStyle = payload?.background || TEMPLATE_EXPORT_BACKGROUND;
    context.fillRect(0, 0, payload?.width || IMAGE_NODE_WIDTH, payload?.height || IMAGE_NODE_HEIGHT);

    const nodeMap = new Map((payload?.nodes || []).map((node) => [String(node.id), node]));

    (payload?.edges || []).forEach((edge) => {
        const sourceNode = nodeMap.get(String(edge.source));
        const targetNode = nodeMap.get(String(edge.target));
        if (!sourceNode || !targetNode) return;

        const sourceWidth = sourceNode.width || IMAGE_NODE_WIDTH;
        const sourceHeight = sourceNode.height || IMAGE_NODE_HEIGHT;
        const targetHeight = targetNode.height || IMAGE_NODE_HEIGHT;
        const startX = sourceNode.position.x + sourceWidth;
        const startY = sourceNode.position.y + sourceHeight / 2;
        const endX = targetNode.position.x;
        const endY = targetNode.position.y + targetHeight / 2;
        const deltaX = Math.max((endX - startX) * 0.5, 72);
        const label = getFunctionNodeEdgeSlotOptions(targetNode?.data?.functionKey)
            .find((item) => item.key === (edge?.data?.inputSlotKey || ''))?.label || '';

        context.save();
        context.beginPath();
        context.moveTo(startX, startY);
        context.bezierCurveTo(startX + deltaX, startY, endX - deltaX, endY, endX, endY);
        context.strokeStyle = '#D0D7E2';
        context.lineWidth = 2.5;
        context.lineCap = 'round';
        context.stroke();
        context.restore();

        drawTemplateEdgeLabel(context, label, (startX + endX) / 2, (startY + endY) / 2);
    });

    for (const node of payload?.nodes || []) {
        const nodeX = node.position.x;
        const nodeY = node.position.y;
        const nodeWidth = node.width || IMAGE_NODE_WIDTH;
        const nodeHeight = node.height || IMAGE_NODE_HEIGHT;
        const previewImageUrl = getTemplateNodePreviewImage(node);

        context.save();
        context.fillStyle = '#737A84';
        context.font = '12px sans-serif';
        context.textBaseline = 'top';
        context.fillText(
            String(node?.data?.label || node?.data?.imageSequenceLabel || '节点').slice(0, 14),
            nodeX + 8,
            Math.max(0, nodeY - 22),
        );

        drawRoundedRectPath(context, nodeX, nodeY, nodeWidth, nodeHeight, 16);
        context.fillStyle = '#FFFFFF';
        context.fill();
        context.strokeStyle = '#DEE2E8';
        context.lineWidth = 1;
        context.stroke();
        context.restore();

        const image = await loadCanvasImage(previewImageUrl);
        if (image) {
            context.save();
            drawRoundedRectPath(context, nodeX, nodeY, nodeWidth, nodeHeight, 16);
            context.clip();
            context.drawImage(image, nodeX, nodeY, nodeWidth, nodeHeight);
            context.restore();
            continue;
        }

        context.save();
        context.fillStyle = '#FFFFFF';
        context.fillRect(nodeX + 1, nodeY + 1, Math.max(0, nodeWidth - 2), Math.max(0, nodeHeight - 2));
        context.fillStyle = '#737A84';
        context.font = '14px sans-serif';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(node.type === 'imageNode' ? '待上传' : '待生成', nodeX + nodeWidth / 2, nodeY + nodeHeight / 2);
        context.restore();
    }

    const blob = await canvasToBlob(canvas);
    return new File([blob], fileName, { type: 'image/png' });
};

const getStoredLastActiveProject = () => {
    if (typeof window === 'undefined') {
        return {
            projectId: '',
            pageIndex: 1,
        };
    }

    const rawValue = window.localStorage.getItem(LAST_ACTIVE_PROJECT_STORAGE_KEY) || '';
    if (!rawValue) {
        return {
            projectId: '',
            pageIndex: 1,
        };
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        return {
            projectId: typeof parsedValue?.projectId === 'string' ? parsedValue.projectId : '',
            pageIndex: Number.isFinite(Number(parsedValue?.pageIndex)) && Number(parsedValue.pageIndex) > 0
                ? Number(parsedValue.pageIndex)
                : 1,
        };
    } catch (error) {
        return {
            projectId: rawValue,
            pageIndex: 1,
        };
    }
};

const FUNCTION_NODE_INPUT_LIMITS = {
    templateReplace: 14,
    templateReplaceExpt: 3,
    materialHD: 2,
    skinHD: 1,
    faceSwap: 2,
    imageUpscale: 1,
    creativeImg2Img: 14,
    creativeTxt2Img: 0,
    shoesTryon: 4,
    shoesRefiner: 5,
    repose: 2,
    relight: 3,
};
const MULTI_IMAGE_FUNCTION_KEYS = new Set(['creativeImg2Img', 'templateReplace']);
// 前端节点类型到后端节点类型的映射
const NODE_TYPE_MAP = {
    imageNode: 'resource',
    functionNode: 'function',
    groupNode: 'group',
};
// 资源节点对应的资源类型映射
const RESOURCE_TYPE_MAP = {
    imageNode: 'image',
};
// 默认输入槽位与参数字段的映射
const DEFAULT_SLOT_PARAM_KEY_MAP = {
    source: 'srcImage',
    commodity: 'commodityImage',
    match: 'matchImage',
    reference: 'refImage',
    otherView1: 'otherView1Image',
    otherView2: 'otherView2Image',
};
const isMultiImageFunctionKey = (functionKey) => MULTI_IMAGE_FUNCTION_KEYS.has(functionKey);
// 特定功能节点的输入槽位字段覆盖配置
const FUNCTION_SLOT_PARAM_KEY_MAP = {
    materialHD: {
        reference: 'refImage',
        source: 'srcImage',
    },
    shoesTryon: {
        source: 'srcImage',
        reference: 'refImage',
        otherView1: 'angle1',
        otherView2: 'angle2',
    },
    shoesRefiner: {
        source: 'srcImage',
        commodity: 'commodityImage',
        otherView1: 'angle1',
        otherView2: 'angle2',
    },
    relight: {
        source: 'refImage',
        reference: 'srcImage',
        match: 'matchImage',
    },
    creativeImg2Img: {
        source: 'imageUrls',
    },
};
// 各功能节点必填输入槽位配置
const FUNCTION_REQUIRED_INPUTS_MAP = {
    templateReplace: [
        { key: 'source', label: '模特图' },
        { key: 'reference', label: '参考图' },
    ],
    templateReplaceExpt: [
        { key: 'source', label: '源素材' },
        { key: 'reference', label: '参考图' },
    ],
    materialHD: [
        { key: 'source', label: '面料平铺图' },
        { key: 'reference', label: '模特/服装图' },
    ],
    skinHD: [
        { key: 'source', label: '源素材' },
    ],
    imageUpscale: [
        { key: 'source', label: '输入图' },
    ],
    faceSwap: [
        { key: 'source', label: '源素材' },
        { key: 'reference', label: '参考图' },
    ],
    shoesTryon: [
        { key: 'source', label: '源素材' },
        { key: 'reference', label: '参考图' },
    ],
    shoesRefiner: [
        { key: 'source', label: '源素材' },
        { key: 'commodity', label: '商品图' },
        { key: 'reference', label: '参考图' },
    ],
    repose: [
        { key: 'source', label: '源素材' },
    ],
    relight: [
        { key: 'source', label: '源素材' },
    ],
};
// 前端功能标识与后端功能编码的映射
const FUNCTION_CODE_MAP = {
    templateReplace: 'edit_part_psai',
    templateReplaceExpt: 'sim_try_on_expt',
    materialHD: 'fabric_transfer_psai',
    skinHD: 'image_h_pro',
    faceSwap: 'change_model',
    imageUpscale: 'image_finer_psai',
    creativeImg2Img: 'creative_image_to_image_psai',
    creativeTxt2Img: 'creative_text_to_image',
    shoesTryon: 'shoes_tryon',
    shoesRefiner: 'shoes_refiner',
    repose: 'pose_fantasy',
    relight: 'change_color_psai',
};
// 后端功能编码反查前端功能标识
const FUNCTION_KEY_BY_CODE_MAP = Object.fromEntries(
    Object.entries({
        ...FUNCTION_CODE_MAP,
        creative_txt2img: 'creativeTxt2Img',
        image_finer: 'imageUpscale',
        relight: 'relight',
        sim_try_on: 'templateReplace',
        creative_image_to_image: 'creativeImg2Img',
        try_on_spro_fabric: 'materialHD',
    }).map(([functionCode, functionKey]) => [functionCode, functionKey]),
);
// 后端节点类型到前端节点类型的映射
const SERVER_NODE_TYPE_MAP = {
    resource: 'imageNode',
    function: 'functionNode',
    group: 'groupNode',
};
const EXECUTION_STATUS_TERMINAL = new Set(['success', 'succeeded', 'completed', 'failed', 'stopped', 'canceled', 'cancelled']); // 执行结束态集合
const SKIP_AUTO_SELECT_QUERY_KEY = 'skip_auto_select'; // 跳过默认选中项目的查询参数
const CREATE_PROJECT_QUERY_KEY = 'create_project'; // 进入页面后自动创建新项目
const TARGET_PROJECT_ID_QUERY_KEY = 'project_id'; // 目标项目 ID 的查询参数
const TARGET_PROJECT_NAME_QUERY_KEY = 'project_name'; // 目标项目名称的查询参数
const TARGET_EXECUTION_ID_QUERY_KEY = 'execution_id'; // 目标执行记录 ID 的查询参数
const TARGET_PROJECT_MEMBER_USER_ID_QUERY_KEY = 'project_member_user_id'; // 企业成员项目所属用户 ID
const PM_VIEW_QUERY_KEY = 'pm_view'; // PM 只读查看模式的查询参数
const FORCE_PERSONAL_MEMBER_QUERY_KEY = 'force_personal_member'; // 强制使用个人项目列表
const FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY = 'free_canvas_creative_node_bootstrap';
const FREE_CANVAS_CREATIVE_BOOTSTRAP_MAX_AGE = 5 * 60 * 1000;
const DEFAULT_CANVAS_PROJECT_NAME = '未命名画布';
const CREATIVE_IMG_ALLOWED_RATIOS = new Set(['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9', '21:9']);
const CREATIVE_IMG_ALLOWED_COUNTS = new Set([1, 2, 3, 4]);
const getSlotParamKey = (functionKey, slotKey) => (
    FUNCTION_SLOT_PARAM_KEY_MAP[functionKey]?.[slotKey]
    || DEFAULT_SLOT_PARAM_KEY_MAP[slotKey]
    || ''
);
const normalizeCreativeBootstrapPayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.source !== 'creativeToImage') return null;

    const createdAt = Number(payload.createdAt || 0);
    const expiresAt = Number(payload.expiresAt || 0);
    const now = Date.now();
    if ((expiresAt && expiresAt < now) || (createdAt && now - createdAt > FREE_CANVAS_CREATIVE_BOOTSTRAP_MAX_AGE)) {
        return null;
    }

    const imageUrls = Array.isArray(payload.imageUrls)
        ? payload.imageUrls.filter(Boolean).slice(0, 14)
        : [];
    const prompt = String(payload.prompt || '');
    const aspectRatio = CREATIVE_IMG_ALLOWED_RATIOS.has(payload.aspectRatio) ? payload.aspectRatio : '3:4';
    const imageSize = payload.imageSize === '2K' ? '2K' : '4K';
    const nextNum = Number(payload.num || 4);
    const num = CREATIVE_IMG_ALLOWED_COUNTS.has(nextNum) ? nextNum : 4;

    return {
        prompt,
        imageUrls,
        aspectRatio,
        imageSize,
        num,
        shouldAutoGenerate: !!payload.shouldAutoGenerate && (prompt.trim().length > 0 || imageUrls.length > 0),
    };
};

const buildCreativeBootstrapNodePatch = (payload) => {
    const imageUrls = Array.isArray(payload?.imageUrls) ? payload.imageUrls.filter(Boolean).slice(0, 14) : [];
    const prompt = String(payload?.prompt || '');
    const aspectRatio = CREATIVE_IMG_ALLOWED_RATIOS.has(payload?.aspectRatio) ? payload.aspectRatio : '3:4';
    const imageSize = payload?.imageSize === '2K' ? '2K' : '4K';
    const nextNum = Number(payload?.num || 4);
    const num = CREATIVE_IMG_ALLOWED_COUNTS.has(nextNum) ? nextNum : 4;
    const uploadItems = imageUrls.map((url) => ({ type: 'manual', url }));
    const uploads = imageUrls.map((url) => ({ url, source: 'upload' }));

    return {
        inputParams: {
            imageUrls: uploads,
            prompt,
            aspectRatio,
            imageSize,
            num,
        },
        panelState: {
            manualUploads: imageUrls,
            linkedUploads: [],
            uploadItems,
            uploads,
            prompt,
            model: 'NanoPro',
            aspectRatio,
            imageSize,
            num,
            skipSelect: false,
            materialSelections: [],
        },
    };
};

const getParamKeySlotMap = (functionKey) => Object.entries({
    ...DEFAULT_SLOT_PARAM_KEY_MAP,
    ...(FUNCTION_SLOT_PARAM_KEY_MAP[functionKey] || {}),
}).reduce((acc, [slotKey, paramKey]) => ({
    ...acc,
    [paramKey]: slotKey,
}), {});

const inferFunctionKeyFromNodePatch = (patch = {}) => {
    if (patch?.functionKey) return String(patch.functionKey);

    const inputParams = (
        patch?.inputParams
        && typeof patch.inputParams === 'object'
        && !Array.isArray(patch.inputParams)
    ) ? patch.inputParams : {};

    if (Array.isArray(inputParams.imageUrls)) {
        if (
            Object.prototype.hasOwnProperty.call(inputParams, 'srcImage')
            || Object.prototype.hasOwnProperty.call(inputParams, 'refImage')
        ) {
            return 'templateReplace';
        }
        return 'creativeImg2Img';
    }

    if (
        Object.prototype.hasOwnProperty.call(inputParams, 'fastMode')
        && Object.prototype.hasOwnProperty.call(inputParams, 'clothType')
        && Object.prototype.hasOwnProperty.call(inputParams, 'mode')
    ) {
        return 'materialHD';
    }

    return '';
};

// 判断当前元素是否是可输入区域
const isEditableTarget = (target) => {
    if (!(target instanceof HTMLElement)) return false;

    const tagName = target.tagName;
    return (
        target.isContentEditable
        || tagName === 'INPUT'
        || tagName === 'TEXTAREA'
        || tagName === 'SELECT'
        || !!target.closest('input, textarea, select, [contenteditable="true"], [role="textbox"]')
    );
};

// 统一拦截输入态下的快捷键事件
const isEditableEvent = (event) => {
    if (event?.isComposing) return true;
    if (isEditableTarget(event?.target) || isEditableTarget(document.activeElement)) return true;

    const activeElement = document.activeElement;
    if (
        activeElement instanceof HTMLElement
        && activeElement.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover')
    ) {
        return true;
    }

    if (
        event?.target instanceof HTMLElement
        && event.target.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover')
    ) {
        return true;
    }

    const eventPath = typeof event?.composedPath === 'function' ? event.composedPath() : [];
    return eventPath.some((item) => (
        isEditableTarget(item)
        || (item instanceof HTMLElement && item.closest('[data-free-canvas-panel-root="true"], .ant-modal, .ant-popover'))
    ));
};

// 工具切换前，判断是否需要避免打断面板输入
const shouldBlockToolSwitchShortcut = () => {
    const activeElement = document.activeElement;
    if (
        activeElement instanceof HTMLElement
        && activeElement.closest(
            'input, textarea, select, [contenteditable="true"], [role="textbox"], [data-free-canvas-panel-root="true"], .ant-modal, .ant-popover',
        )
    ) {
        return true;
    }

    return !!document.querySelector(
        [
            '[data-free-canvas-panel-root="true"] textarea',
            '[data-free-canvas-panel-root="true"] input',
            '[data-free-canvas-panel-root="true"] [role="textbox"]',
            '.ant-modal textarea',
            '.ant-modal input',
            '.ant-modal [role="textbox"]',
        ].join(', '),
    );
};

const cloneValue = (value) => {
    if (typeof structuredClone === 'function') return structuredClone(value);
    return JSON.parse(JSON.stringify(value));
};

const createUuid = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/[-_]/g, '');
    }

    return `${Date.now()}${Math.random().toString(16).slice(2)}`;
};

const createNodeId = (nodeType) => `${nodeType}-${createUuid()}`;
const createEdgeId = () => `edge-${createUuid()}`;
const resolveExecutionIdentifier = (execution) => String(
    execution?.id
    || execution?.executionId
    || execution?.flowExecutionId
    || execution?.data?.id
    || execution?.data?.executionId
    || '',
);
const MIME_EXTENSION_MAP = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'image/gif': 'gif',
    'image/bmp': 'bmp',
    'image/svg+xml': 'svg',
};
const FLOW_LINK_PLACEHOLDER_ICON = 'https://alioss-ai.arcsoft.com.cn//photostudio-opshub/FreeCanvas/node-image.svg';

const createDuplicatedNodeId = (node) => {
    if (node?.type === 'functionNode') return createNodeId('func');
    if (node?.type === 'groupNode') return createNodeId('group-node');
    return createNodeId('image-node');
};

const getNodeImageSequence = (node) => {
    const sequence = Number(
        node?.data?.imageSequence
        ?? node?.renderData?.data?.imageSequence
        ?? 0,
    );
    return Number.isFinite(sequence) && sequence > 0 ? sequence : 0;
};

const getImageSequenceLabel = (sequence) => {
    const normalizedSequence = Number(sequence ?? 0);
    return Number.isFinite(normalizedSequence) && normalizedSequence > 0
        ? `图${normalizedSequence}`
        : '';
};

const isDefaultImageSequenceLabel = (label = '') => (
    /^图\s*\d+$/.test(String(label || '')) || /^Image\s*\d+$/.test(String(label || ''))
);

const withNodeImageSequence = (node, imageSequence) => {
    if (!node || node.type === 'groupNode') return node;

    const nextSequence = Number(imageSequence ?? 0);
    if (!Number.isFinite(nextSequence) || nextSequence <= 0) return node;

    const nextLabel = getImageSequenceLabel(nextSequence);
    const currentImageSequenceLabel = node.data?.imageSequenceLabel;
    const currentNodeLabel = node.data?.label || '';
    const shouldOverrideImageNodeLabel = !currentNodeLabel || isDefaultImageSequenceLabel(currentNodeLabel);

    return {
        ...node,
        data: {
            ...(node.data || {}),
            ...(node.type === 'imageNode' && shouldOverrideImageNodeLabel ? { label: nextLabel } : {}),
            imageSequence: nextSequence,
            imageSequenceLabel: currentImageSequenceLabel || nextLabel,
        },
    };
};

const normalizeDuplicatedLatestExecution = (latestExecution, selectedOutputId) => {
    if (!latestExecution || typeof latestExecution !== 'object') return null;

    const nextExecution = cloneValue(latestExecution);
    const outputs = normalizeExecutionOutputs(nextExecution.outputs);
    if (!outputs.length) return null;
    const nextSelectedOutputId = resolveExecutionSelectedOutputId(outputs, selectedOutputId);

    return {
        ...nextExecution,
        id: '',
        executionId: '',
        nodeExecutionId: '',
        execSerial: undefined,
        taskSerial: undefined,
        progress: 100,
        status: nextExecution.status === 'processing' ? 'succeeded' : (nextExecution.status || 'succeeded'),
        errCode: '',
        errMsg: '',
        outputs: markExecutionPrimaryOutput(outputs, nextSelectedOutputId),
        selectedOutputId: nextSelectedOutputId,
    };
};

const appendDuplicateSuffix = (value, fallbackValue = '节点') => {
    const normalizedValue = String(value || '').trim() || fallbackValue;
    return `${normalizedValue}副本`;
};

const remapDuplicatedPanelState = (panelState = {}, nodeIdMap = new Map(), edgeIdMap = new Map()) => {
    if (!panelState || typeof panelState !== 'object') return panelState;

    const nextPanelState = cloneValue(panelState);

    if (Array.isArray(nextPanelState.linkedUploads)) {
        nextPanelState.linkedUploads = nextPanelState.linkedUploads.map((item) => ({
            ...item,
            edgeId: edgeIdMap.get(item?.edgeId) || item?.edgeId,
            sourceNodeId: nodeIdMap.get(item?.sourceNodeId) || item?.sourceNodeId,
        }));
    }

    if (
        nextPanelState.linkedSlotSources
        && typeof nextPanelState.linkedSlotSources === 'object'
        && !Array.isArray(nextPanelState.linkedSlotSources)
    ) {
        nextPanelState.linkedSlotSources = Object.fromEntries(
            Object.entries(nextPanelState.linkedSlotSources).map(([slotKey, value]) => [
                slotKey,
                value && typeof value === 'object'
                    ? {
                        ...value,
                        edgeId: edgeIdMap.get(value?.edgeId) || value?.edgeId,
                        sourceNodeId: nodeIdMap.get(value?.sourceNodeId) || value?.sourceNodeId,
                    }
                    : value,
            ]),
        );
    }

    return nextPanelState;
};

const normalizeDuplicatedNodeData = (data, options = {}) => {
    const {
        nodeIdMap = new Map(),
        edgeIdMap = new Map(),
    } = options;
    const nextData = cloneValue(data || {});

    if (!nextData || typeof nextData !== 'object') return {};

    const duplicatedLatestExecution = normalizeDuplicatedLatestExecution(
        nextData.latestExecution,
        nextData.selectedOutputId,
    );
    const nextSelectedOutputId = duplicatedLatestExecution?.selectedOutputId || nextData.selectedOutputId || '';
    const nextExecutionStatus = duplicatedLatestExecution?.status || nextData.executionStatus || 'idle';
    const nextDisplayMode = normalizeExecutionOutputs(duplicatedLatestExecution?.outputs || []).length > 0
        ? 'stacked'
        : (nextData.displayMode || 'empty');

    return {
        ...nextData,
        panelState: remapDuplicatedPanelState(nextData.panelState || {}, nodeIdMap, edgeIdMap),
        activeExecutionId: '',
        groupExecutionLocked: false,
        latestExecution: duplicatedLatestExecution,
        executionStatus: nextExecutionStatus,
        selectedOutputId: nextSelectedOutputId,
        displayMode: nextDisplayMode,
    };
};

const normalizeDuplicatedProjectNode = (node) => {
    const nextNode = cloneValue(node || {});
    const renderData = nextNode?.renderData && typeof nextNode.renderData === 'object'
        ? nextNode.renderData
        : {};
    const duplicatedRenderNodeData = {
        ...normalizeDuplicatedNodeData(renderData?.data),
        activeExecutionId: '',
        groupExecutionLocked: false,
    };

    return {
        ...nextNode,
        latestExecution: normalizeDuplicatedLatestExecution(
            nextNode?.latestExecution,
            duplicatedRenderNodeData.selectedOutputId,
        ),
        renderData: {
            ...renderData,
            data: duplicatedRenderNodeData,
        },
    };
};

const TEMPLATE_IMAGE_INPUT_PARAM_KEYS = new Set([
    'imageUrls',
    'srcImage',
    'refImage',
    'commodityImage',
    'matchImage',
    'angle1',
    'angle2',
    'otherView1Image',
    'otherView2Image',
    'output',
]);

const sanitizeTemplateInputParamsForCanvas = (inputParams = {}) => {
    if (!inputParams || typeof inputParams !== 'object') return {};

    const nextInputParams = { ...inputParams };

    TEMPLATE_IMAGE_INPUT_PARAM_KEYS.forEach((key) => {
        if (!(key in nextInputParams)) return;
        nextInputParams[key] = key === 'imageUrls' ? [] : '';
    });

    return nextInputParams;
};

const sanitizeTemplatePanelStateForCanvas = (panelState = {}) => {
    if (!panelState || typeof panelState !== 'object') return {};

    const nextPanelState = { ...panelState };

    if (Array.isArray(nextPanelState.uploads)) {
        nextPanelState.uploads = [];
    } else if (nextPanelState.uploads && typeof nextPanelState.uploads === 'object') {
        nextPanelState.uploads = Object.fromEntries(
            Object.keys(nextPanelState.uploads).map((key) => [key, '']),
        );
    }

    if (Array.isArray(nextPanelState.manualUploads)) {
        nextPanelState.manualUploads = [];
    }

    if (Array.isArray(nextPanelState.linkedUploads)) {
        nextPanelState.linkedUploads = [];
    }

    if (
        nextPanelState.linkedSlotSources
        && typeof nextPanelState.linkedSlotSources === 'object'
        && !Array.isArray(nextPanelState.linkedSlotSources)
    ) {
        nextPanelState.linkedSlotSources = Object.fromEntries(
            Object.entries(nextPanelState.linkedSlotSources).map(([slotKey, value]) => [
                slotKey,
                value && typeof value === 'object'
                    ? {
                        ...value,
                        url: '',
                    }
                    : value,
            ]),
        );
    }

    return nextPanelState;
};

const sanitizeTemplateNodeForCanvas = (node) => {
    if (!node || node.type === 'groupNode') return node;

    const nextInputParams = sanitizeTemplateInputParamsForCanvas(node.data?.inputParams || {});
    const nextPanelState = sanitizeTemplatePanelStateForCanvas(node.data?.panelState || {});

    if (node.type === 'imageNode') {
        return {
            ...node,
            data: {
                ...node.data,
                src: '',
                inputParams: nextInputParams,
                panelState: nextPanelState,
                latestExecution: null,
                executionStatus: 'idle',
                selectedOutputId: '',
            },
        };
    }

    return {
        ...node,
        data: {
            ...node.data,
            inputParams: nextInputParams,
            panelState: nextPanelState,
            latestExecution: null,
            executionStatus: 'idle',
            selectedOutputId: '',
            displayMode: 'empty',
        },
    };
};

const stripGroupedClipboardExecution = (clipboard) => {
    if (!clipboard?.nodes?.length) return clipboard;

    const containsGroupNode = clipboard.nodes.some((node) => node?.type === 'groupNode');
    if (!containsGroupNode) return clipboard;

    return {
        ...clipboard,
        nodes: clipboard.nodes.map((node) => {
            if (node?.type === 'groupNode') return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    activeExecutionId: '',
                    groupExecutionLocked: false,
                },
            };
        }),
    };
};

const copyTextToClipboard = async (text) => {
    if (!text) return false;

    if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.top = '-9999px';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    } catch (error) {
        document.body.removeChild(textarea);
        return false;
    }
};

const canvasToBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) {
            resolve(blob);
            return;
        }

        reject(new Error('Canvas toBlob failed'));
    }, 'image/png');
});

const getExtensionFromBlob = (blob, fallbackUrl = '') => {
    const mimeExtension = MIME_EXTENSION_MAP[blob?.type];
    if (mimeExtension) return mimeExtension;

    const urlPath = fallbackUrl.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const urlExtension = lastSegment.includes('.') ? lastSegment.split('.').pop() : '';
    return urlExtension || 'png';
};

const getDownloadFileName = (url, blob, prefix = 'group-image') => {
    const urlPath = url.split('?')[0] || '';
    const lastSegment = urlPath.split('/').pop() || '';
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(lastSegment);

    if (lastSegment && hasExtension) {
        return lastSegment;
    }

    return `${prefix}-${Date.now()}.${getExtensionFromBlob(blob, url)}`;
};

const toPositiveNumber = (value) => {
    const normalizedValue = typeof value === 'string' ? Number.parseFloat(value) : Number(value);
    return Number.isFinite(normalizedValue) && normalizedValue > 0 ? normalizedValue : null;
};

const getNodeSize = (node, fallbackWidth = IMAGE_NODE_WIDTH, fallbackHeight = IMAGE_NODE_HEIGHT) => ({
    width: toPositiveNumber(node?.width)
        ?? toPositiveNumber(node?.style?.width)
        ?? toPositiveNumber(node?.measured?.width)
        ?? fallbackWidth,
    height: toPositiveNumber(node?.height)
        ?? toPositiveNumber(node?.style?.height)
        ?? toPositiveNumber(node?.measured?.height)
        ?? fallbackHeight,
});

const getNodeAbsolutePosition = (node, nodeMap) => {
    let x = node.position.x;
    let y = node.position.y;
    let parentId = node.parentId;

    while (parentId) {
        const parentNode = nodeMap.get(parentId);
        if (!parentNode) break;
        x += parentNode.position.x;
        y += parentNode.position.y;
        parentId = parentNode.parentId;
    }

    return { x, y };
};

const getDescendantNodeIds = (nodeIds, nodes) => {
    const targetIds = new Set(nodeIds);
    let keepExpanding = true;

    while (keepExpanding) {
        keepExpanding = false;
        for (const node of nodes) {
            if (node.parentId && targetIds.has(node.parentId) && !targetIds.has(node.id)) {
                targetIds.add(node.id);
                keepExpanding = true;
            }
        }
    }

    return targetIds;
};

const getFunctionNodeInputLimit = (functionKey) => (
    Object.prototype.hasOwnProperty.call(FUNCTION_NODE_INPUT_LIMITS, functionKey)
        ? FUNCTION_NODE_INPUT_LIMITS[functionKey]
        : Number.POSITIVE_INFINITY
);

const sortNodesByParentDepth = (nodes) => {
    const nodeMap = new Map(nodes.map((node) => [node.id, node]));
    const getDepth = (node) => {
        let depth = 0;
        let parentId = node.parentId;

        while (parentId) {
            const parentNode = nodeMap.get(parentId);
            if (!parentNode) break;
            depth += 1;
            parentId = parentNode.parentId;
        }

        return depth;
    };

    return [...nodes].sort((a, b) => getDepth(a) - getDepth(b));
};

const sanitizeIdentifier = (value) => String(value ?? '').replace(/_/g, '');

const toSerializableData = (value) => {
    if (value === undefined) return undefined;

    return JSON.parse(JSON.stringify(value, (key, currentValue) => (
        typeof currentValue === 'function' ? undefined : currentValue
    )));
};

const buildNodeRenderData = (node, canvasViewport = DEFAULT_VIEWPORT) => {
    const { width, height } = getNodeSize(node);

    return toSerializableData({
        type: node.type,
        position: node.position,
        style: {
            ...node.style,
            ...(node.type === 'groupNode' ? { width, height } : {}),
        },
        width,
        height,
        parentId: node.parentId,
        extent: node.extent,
        expandParent: node.expandParent,
        data: {
            label: node.data?.label,
            imageSequence: node.data?.imageSequence,
            imageSequenceLabel: node.data?.imageSequenceLabel,
            src: node.data?.src,
            functionKey: node.data?.functionKey,
            displayMode: node.data?.displayMode,
            selectedOutputId: node.data?.selectedOutputId,
            executionStatus: node.data?.executionStatus,
            groupExecutionLocked: !!node.data?.groupExecutionLocked,
            activeExecutionId: node.data?.activeExecutionId || '',
            inputParams: node.data?.inputParams,
            panelState: node.data?.panelState,
            latestExecution: node.data?.latestExecution || null,
        },
        canvasViewport,
    });
};

const buildEdgeRenderData = (edge) => toSerializableData({
    type: edge.type,
    inputSlotKey: edge.data?.inputSlotKey,
});

const buildCreativeImg2ImgBindings = (node, edges) => {
    const targetEdges = edges.filter((edge) => edge.target === node.id);
    if (targetEdges.length === 0) return [];

    const edgeMap = new Map(targetEdges.map((edge) => [String(edge.id), edge]));
    // 优先从 panelState.uploadItems 中提取 linked 项的顺序，
    // 保证 paramBindings 与 imageUrls 中 source:"line" 项的顺序一致。
    const uploadItems = Array.isArray(node.data?.panelState?.uploadItems)
        ? node.data.panelState.uploadItems
        : [];
    const linkedUploads = Array.isArray(node.data?.panelState?.linkedUploads)
        ? node.data.panelState.linkedUploads
        : [];
    const orderedBindings = [];
    const usedEdgeIds = new Set();

    const tryAddBinding = (edgeId) => {
        if (!edgeId) return;
        const edgeKey = String(edgeId);
        if (usedEdgeIds.has(edgeKey)) return;
        const matchedEdge = edgeMap.get(edgeKey);
        if (!matchedEdge) return;
        usedEdgeIds.add(edgeKey);
        orderedBindings.push({
            paramKey: 'imageUrls',
            sourceNodeId: sanitizeIdentifier(matchedEdge.source),
            sourceOutputKey: '',
        });
    };

    // 按 uploadItems 中 linked 项顺序添加 binding
    uploadItems.forEach((item) => {
        if (item?.type === 'linked') tryAddBinding(item.edgeId);
    });

    // 如果 uploadItems 为空，倒退到原来的 linkedUploads 顺序
    if (orderedBindings.length === 0) {
        linkedUploads.forEach((item) => tryAddBinding(item?.edgeId));
    }

    // 将不在以上源中的 edges 追加到末尾（兆底）
    targetEdges.forEach((edge) => tryAddBinding(edge.id));

    return orderedBindings.filter((binding) => binding.sourceNodeId);
};

const buildNodePayload = (node, edges, canvasViewport = DEFAULT_VIEWPORT) => {
    const nodeType = NODE_TYPE_MAP[node.type];
    const functionKey = node.data?.functionKey;
    const skipSelectValue = node.data?.panelState?.skipSelect ?? node.data?.inputParams?.skipSelect;
    const manual = !skipSelectValue;
    const resolveBindingParamKey = (edge) => (
        getSlotParamKey(functionKey, edge.data?.inputSlotKey)
        || (isMultiImageFunctionKey(functionKey) ? 'imageUrls' : '')
    );
    const incomingBindings = nodeType === 'function'
        ? (
            isMultiImageFunctionKey(functionKey)
                ? buildCreativeImg2ImgBindings(node, edges)
                : edges
                    .filter((edge) => edge.target === node.id)
                    .map((edge) => ({
                        paramKey: resolveBindingParamKey(edge),
                        sourceNodeId: sanitizeIdentifier(edge.source),
                    }))
                    .filter((binding) => binding.paramKey && binding.sourceNodeId)
                    .sort((a, b) => (
                        a.paramKey.localeCompare(b.paramKey) || a.sourceNodeId.localeCompare(b.sourceNodeId)
                    ))
        )
        : [];

    const nodeName = node.data?.imageSequenceLabel || node.data?.label || '';

    return {
        id: sanitizeIdentifier(node.id),
        nodeName,
        manual,
        nodeType,
        parentId: node.parentId ? sanitizeIdentifier(node.parentId) : undefined,
        resourceType: RESOURCE_TYPE_MAP[node.type],
        functionCode: nodeType === 'function'
            ? (FUNCTION_CODE_MAP[functionKey] || functionKey || '')
            : undefined,
        functionName: nodeType === 'function'
            ? (node.data?.label || FUNCTION_LABELS[functionKey] || '')
            : undefined,
        inputParams: nodeType === 'function' || nodeType === 'resource'
            ? toSerializableData(node.data?.inputParams || {})
            : undefined,
        paramBindings: nodeType === 'function' ? incomingBindings : undefined,
        groupConfig: nodeType === 'group' ? {} : undefined,
        renderData: buildNodeRenderData(node, canvasViewport),
    };
};

const buildEdgePayload = (edge) => ({
    id: sanitizeIdentifier(edge.id),
    sourceNodeId: sanitizeIdentifier(edge.source),
    targetNodeId: sanitizeIdentifier(edge.target),
    renderData: buildEdgeRenderData(edge),
});

// 对比前后画布数据，生成后端批量更新指令
const buildCanvasBatchOps = ({
    beforeNodes = [],
    beforeEdges = [],
    afterNodes = [],
    afterEdges = [],
    beforeViewport = DEFAULT_VIEWPORT,
    afterViewport = DEFAULT_VIEWPORT,
}) => {
    const beforeNodeMap = new Map(beforeNodes.map((node) => {
        const payload = buildNodePayload(node, beforeEdges, beforeViewport);
        return [payload.id, payload];
    }));
    const afterNodeMap = new Map(afterNodes.map((node) => {
        const payload = buildNodePayload(node, afterEdges, afterViewport);
        return [payload.id, payload];
    }));
    const beforeEdgeMap = new Map(beforeEdges.map((edge) => {
        const payload = buildEdgePayload(edge);
        return [payload.id, payload];
    }));
    const afterEdgeMap = new Map(afterEdges.map((edge) => {
        const payload = buildEdgePayload(edge);
        return [payload.id, payload];
    }));
    const nodeOps = [];
    const edgeOps = [];

    beforeNodeMap.forEach((node) => {
        if (!afterNodeMap.has(node.id)) {
            nodeOps.push({ op: 'delete', node });
        }
    });

    afterNodeMap.forEach((node) => {
        const prevNode = beforeNodeMap.get(node.id);
        if (!prevNode) {
            nodeOps.push({ op: 'add', node });
            return;
        }

        if (JSON.stringify(prevNode) !== JSON.stringify(node)) {
            nodeOps.push({ op: 'update', node });
        }
    });

    beforeEdgeMap.forEach((edge) => {
        if (!afterEdgeMap.has(edge.id)) {
            edgeOps.push({ op: 'delete', edge });
        }
    });

    afterEdgeMap.forEach((edge) => {
        const prevEdge = beforeEdgeMap.get(edge.id);
        if (!prevEdge) {
            edgeOps.push({ op: 'add', edge });
            return;
        }

        if (JSON.stringify(prevEdge) !== JSON.stringify(edge)) {
            edgeOps.push({ op: 'update', edge });
        }
    });

    return { nodeOps, edgeOps };
};

const parseLabelSequence = (label, prefix) => {
    if (typeof label !== 'string') return 0;

    const pattern = new RegExp(`^${prefix}\\s*(\\d+)$`);
    const match = label.match(pattern);
    return match ? Number(match[1]) : 0;
};

const getNextNodeImageSequenceNumber = (nodes) => (
    nodes.reduce((max, node) => (
        Math.max(
            max,
            getNodeImageSequence(node),
            parseLabelSequence(node.data?.label, '图'),
            parseLabelSequence(node.data?.label, 'Image'),
        )
    ), 0) + 1
);

const normalizeNodeImageSequences = (nodes) => {
    let nextSequence = 1;
    const usedSequences = new Set();

    return (nodes || []).map((node) => {
        if (!node || node.type === 'groupNode') return node;

        const currentSequence = getNodeImageSequence(node)
            || parseLabelSequence(node.data?.label, '图')
            || parseLabelSequence(node.data?.label, 'Image');
        const assignedSequence = (
            currentSequence > 0 && !usedSequences.has(currentSequence)
        )
            ? currentSequence
            : nextSequence;
        usedSequences.add(assignedSequence);
        nextSequence = Math.max(nextSequence, assignedSequence + 1);

        return withNodeImageSequence(node, assignedSequence);
    });
};

const getRestoredGroupNodeNumber = (nodes) => (
    nodes.reduce((max, node) => (
        Math.max(max, parseLabelSequence(node.data?.label, 'Group'))
    ), 0) + 1
);

const getProjectViewport = (projectDetail) => {
    const viewport = projectDetail?.nodes?.find((node) => (
        node?.renderData?.canvasViewport
    ))?.renderData?.canvasViewport;

    if (!viewport) return null;

    return {
        x: Number(viewport.x ?? DEFAULT_VIEWPORT.x),
        y: Number(viewport.y ?? DEFAULT_VIEWPORT.y),
        zoom: Number(viewport.zoom ?? DEFAULT_VIEWPORT.zoom),
    };
};

const CANVAS_VERSION_CONFLICT_CODE = -5004;
const CANVAS_VERSION_CONFLICT_MESSAGE = '画布版本已变更,请刷新后重试';
const getErrorMessage = (error, fallback = '操作失败') => error?.message || fallback;
const isCanvasVersionConflictError = (error) => (
    Number(error?.code) === CANVAS_VERSION_CONFLICT_CODE
    || String(error?.message || '').includes('画布版本已变更')
);

const getVersionNumber = (value) => {
    const version = Number(value ?? 0);
    return Number.isFinite(version) && version > 0 ? version : 0;
};

const mergeProjectRecord = (currentProject, incomingProject) => {
    if (!currentProject) return incomingProject;
    if (!incomingProject) return currentProject;

    return {
        ...currentProject,
        ...incomingProject,
        version: Math.max(
            getVersionNumber(currentProject.version),
            getVersionNumber(incomingProject.version),
        ),
    };
};

const mergeProjectCollections = (currentProjects, incomingProjects, append = false) => {
    const mergedMap = new Map(
        (append ? currentProjects : []).map((project) => [project.id, project]),
    );

    incomingProjects.forEach((project) => {
        const existingProject = mergedMap.get(project.id);
        mergedMap.set(project.id, mergeProjectRecord(existingProject, project));
    });

    if (!append) {
        return incomingProjects.map((project) => mergedMap.get(project.id) || project);
    }

    const orderedProjects = [...currentProjects];
    incomingProjects.forEach((project) => {
        if (!orderedProjects.some((item) => item.id === project.id)) {
            orderedProjects.push(project);
        }
    });

    return orderedProjects.map((project) => mergedMap.get(project.id) || project);
};

const reorderProjectCollections = (projects, oldIdx, newIdx) => {
    if (!Array.isArray(projects) || oldIdx === newIdx) return projects;
    if (oldIdx < 0 || newIdx < 0 || oldIdx >= projects.length || newIdx >= projects.length) return projects;

    const nextProjects = [...projects];
    const [movedProject] = nextProjects.splice(oldIdx, 1);
    nextProjects.splice(newIdx, 0, movedProject);
    return nextProjects;
};

// 将服务端节点结构还原成前端画布节点
const restoreNodeFromProject = (node) => {
    const renderData = node?.renderData || {};
    const renderNodeData = renderData?.data || {};
    const type = renderData?.type || SERVER_NODE_TYPE_MAP[node?.nodeType] || 'imageNode';
    const functionKey = renderNodeData?.functionKey || FUNCTION_KEY_BY_CODE_MAP[node?.functionCode] || '';
    const fallbackNodeSize = type === 'groupNode'
        ? { width: GROUP_MIN_WIDTH, height: GROUP_MIN_HEIGHT }
        : { width: IMAGE_NODE_WIDTH, height: IMAGE_NODE_HEIGHT };
    const width = toPositiveNumber(renderData?.width)
        ?? toPositiveNumber(renderData?.style?.width)
        ?? fallbackNodeSize.width;
    const height = toPositiveNumber(renderData?.height)
        ?? toPositiveNumber(renderData?.style?.height)
        ?? fallbackNodeSize.height;
    const position = {
        x: Number(renderData?.position?.x ?? 0),
        y: Number(renderData?.position?.y ?? 0),
    };

    return withNodeImageSequence({
        id: String(node?.id ?? ''),
        type,
        position,
        parentId: node?.parentId || renderData?.parentId || undefined,
        extent: renderData?.extent,
        expandParent: renderData?.expandParent,
        style: {
            ...(renderData?.style || {}),
            ...(type === 'groupNode' ? { width, height } : {}),
        },
        width,
        height,
        selected: false,
        data: {
            ...renderNodeData,
            label: renderNodeData?.label || node?.nodeName || node?.functionName || '节点',
            src: renderNodeData?.src || '',
            functionKey,
            selectedOutputId: renderNodeData?.selectedOutputId,
            activeExecutionId: renderNodeData?.activeExecutionId || renderNodeData?.latestExecution?.executionId || node?.latestExecution?.executionId || '',
            inputParams: node?.inputParams || renderNodeData?.inputParams || {},
            panelState: renderNodeData?.panelState || {},
            displayMode: renderNodeData?.displayMode || 'empty',
            latestExecution: (
                renderNodeData?.latestExecution && typeof renderNodeData.latestExecution === 'object'
                    ? renderNodeData.latestExecution
                    : (node?.latestExecution && typeof node.latestExecution === 'object' ? node.latestExecution : null)
            ),
            executionStatus: (() => {
                const latestStatus = String(renderNodeData?.latestExecution?.status || node?.latestExecution?.status || '').toLowerCase();
                // latestExecution 已是终态时以它为准，修正 renderData 里可能残留的过时 processing 状态
                if (latestStatus && EXECUTION_STATUS_TERMINAL.has(latestStatus)) {
                    return latestStatus;
                }
                return renderNodeData?.executionStatus || latestStatus || 'idle';
            })(),
            groupExecutionLocked: !!renderNodeData?.groupExecutionLocked,
        },
    }, getNodeImageSequence({
        data: renderNodeData,
        renderData,
    }) || (type !== 'groupNode'
        ? (
            parseLabelSequence(renderNodeData?.label || node?.nodeName || '', '图')
            || parseLabelSequence(renderNodeData?.label || node?.nodeName || '', 'Image')
        )
        : 0));
};

const restoreEdgeFromProject = (edge, nodeMap) => {
    const renderData = edge?.renderData || {};
    const targetNode = nodeMap.get(edge?.targetNodeId);
    const targetFunctionKey = FUNCTION_KEY_BY_CODE_MAP[targetNode?.functionCode] || targetNode?.renderData?.data?.functionKey || '';
    const paramKeySlotMap = getParamKeySlotMap(targetFunctionKey);
    const fallbackSlotKey = targetNode?.paramBindings?.find((binding) => (
        binding?.sourceNodeId === edge?.sourceNodeId
    ))?.paramKey;

    return {
        id: String(edge?.id ?? ''),
        source: String(edge?.sourceNodeId ?? ''),
        target: String(edge?.targetNodeId ?? ''),
        type: renderData?.type || 'flowEdge',
        selected: false,
        data: {
            ...(renderData?.inputSlotKey || fallbackSlotKey ? {
                inputSlotKey: renderData?.inputSlotKey || paramKeySlotMap[fallbackSlotKey] || undefined,
            } : {}),
        },
    };
};

const wrapTemplateNodesIntoGroup = (nodes, templateName = '') => {
    const templateNodes = (nodes || []).filter(Boolean).map((node) => ({
        ...cloneValue(node),
        selected: false,
    }));
    if (!templateNodes.length) return [];

    const detachedNodes = templateNodes.map((node) => ({
        ...node,
        parentId: undefined,
        extent: undefined,
        expandParent: undefined,
    }));
    const detachedNodeMap = new Map(detachedNodes.map((node) => [node.id, node]));
    const bounds = detachedNodes.reduce((acc, node) => {
        const absolutePosition = getNodeAbsolutePosition(node, detachedNodeMap);
        const { width, height } = getNodeSize(node);

        return {
            minX: Math.min(acc.minX, absolutePosition.x),
            minY: Math.min(acc.minY, absolutePosition.y),
            maxX: Math.max(acc.maxX, absolutePosition.x + width),
            maxY: Math.max(acc.maxY, absolutePosition.y + height),
        };
    }, {
        minX: Infinity,
        minY: Infinity,
        maxX: -Infinity,
        maxY: -Infinity,
    });

    const groupId = `template-group-${createUuid()}`;
    const groupPosition = {
        x: bounds.minX - GROUP_PADDING_X,
        y: bounds.minY - GROUP_HEADER_HEIGHT,
    };
    const groupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
    const groupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);
    const groupLabel = String(templateName || '').trim() || `组合${Date.now()}`;

    const groupNode = {
        id: groupId,
        type: 'groupNode',
        position: groupPosition,
        width: groupWidth,
        height: groupHeight,
        style: {
            width: groupWidth,
            height: groupHeight,
        },
        data: {
            label: groupLabel,
        },
        selected: false,
    };

    const childNodes = detachedNodes.map((node) => {
        const absolutePosition = getNodeAbsolutePosition(node, detachedNodeMap);

        return {
            ...node,
            parentId: groupId,
            extent: 'parent',
            expandParent: true,
            position: {
                x: absolutePosition.x - groupPosition.x,
                y: absolutePosition.y - groupPosition.y,
            },
        };
    });

    return sortNodesByParentDepth([groupNode, ...childNodes]);
};

const buildTemplateClipboardFromDetail = (templateDetail) => {
    const sourceNodes = Array.isArray(templateDetail?.nodes) ? templateDetail.nodes : [];
    const sourceEdges = Array.isArray(templateDetail?.edges) ? templateDetail.edges : [];
    if (!sourceNodes.length) {
        throw new Error('模板中没有可用节点');
    }

    const serverNodeMap = new Map(sourceNodes.map((node) => [String(node?.id ?? ''), node]));
    const restoredBaseNodes = normalizeNodeImageSequences(sortNodesByParentDepth(
        sourceNodes.map((node) => sanitizeTemplateNodeForCanvas(restoreNodeFromProject(node))),
    ));
    const restoredEdges = sourceEdges.map((edge) => restoreEdgeFromProject(edge, serverNodeMap));
    const syncedNodes = syncConnectedTargetNodes(restoredBaseNodes, restoredEdges)
        .map((node) => ({ ...node, selected: false }));
    const wrappedNodes = syncedNodes.some((node) => node?.type === 'groupNode')
        ? syncedNodes
        : wrapTemplateNodesIntoGroup(syncedNodes, templateDetail?.templateName || '');

    return stripGroupedClipboardExecution({
        nodes: wrappedNodes,
        edges: restoredEdges.map((edge) => ({ ...edge, selected: false })),
    });
};

const getNodeImageOutputs = (node) => (
    (node?.data?.latestExecution?.outputs || []).filter((item) => item?.type === 'image' && item?.url)
);

const normalizeExecutionOutputs = (outputs = []) => (
    Array.isArray(outputs) ? outputs.filter((item) => item?.url) : []
);

const getPrimaryOutputId = (outputs = []) => (
    normalizeExecutionOutputs(outputs).find((item) => item?.primary && item?.url)?.id || ''
);

const resolveExecutionSelectedOutputId = (outputs = [], fallbackSelectedOutputId = '') => {
    const normalizedOutputs = normalizeExecutionOutputs(outputs);
    if (!normalizedOutputs.length) return '';

    const primaryOutputId = getPrimaryOutputId(normalizedOutputs);
    if (primaryOutputId) return primaryOutputId;

    const hasFallbackOutput = normalizedOutputs.some((item) => (
        String(item?.id ?? '') === String(fallbackSelectedOutputId ?? '')
    ));
    if (hasFallbackOutput) return fallbackSelectedOutputId;

    return normalizedOutputs[0]?.id || '';
};

const resolveNodeExecutionSelectedOutputId = (nodeExecution, fallbackSelectedOutputId = '') => {
    const nextStatus = String(nodeExecution?.status || '').toLowerCase();

    if (nextStatus === 'manual') {
        return '';
    }

    return resolveExecutionSelectedOutputId(nodeExecution?.outputs, fallbackSelectedOutputId);
};

const GROUP_ACTIVE_STATUS_PRIORITY = {
    processing: 3,
    manual: 2,
    queued: 1,
    pending: 1,
};

const getExecutionStatusPriority = (status) => (
    GROUP_ACTIVE_STATUS_PRIORITY[String(status || '').toLowerCase()] || 0
);

const inferActiveGroupExecutionMeta = (groupNodeId, projectNodes = []) => {
    const normalizedGroupNodeId = String(groupNodeId || '');
    if (!normalizedGroupNodeId) return null;

    const groupNode = projectNodes.find((node) => String(node?.id || '') === normalizedGroupNodeId) || null;
    const groupRenderData = groupNode?.renderData?.data || {};
    const groupChildNodes = projectNodes.filter((node) => (
        String(node?.parentId || '') === normalizedGroupNodeId
    ));
    if (!groupChildNodes.length) return null;

    const groupFunctionNodeIds = groupChildNodes
        .filter((node) => node?.nodeType === 'function')
        .map((node) => String(node?.id || ''))
        .filter(Boolean);
    const groupChildNodeIds = groupChildNodes
        .map((node) => String(node?.id || ''))
        .filter(Boolean);

    if (!groupFunctionNodeIds.length) return null;

    const executionCandidates = groupChildNodes
        .map((node) => ({
            nodeId: String(node?.id || ''),
            executionId: String(
                node?.latestExecution?.executionId
                || node?.renderData?.data?.activeExecutionId
                || '',
            ),
            status: String(
                node?.latestExecution?.status
                || node?.renderData?.data?.executionStatus
                || '',
            ).toLowerCase(),
            completedAt: String(node?.latestExecution?.completedAt || ''),
        }))
        .filter((item) => item.executionId)
        .sort((a, b) => {
            const priorityDiff = getExecutionStatusPriority(b.status) - getExecutionStatusPriority(a.status);
            if (priorityDiff !== 0) return priorityDiff;
            return String(b.completedAt).localeCompare(String(a.completedAt));
        });

    const activeExecutionCandidate = executionCandidates.find((item) => (
        getExecutionStatusPriority(item.status) > 0
    ));

    const fallbackGroupExecutionId = String(groupRenderData?.activeExecutionId || '');
    const isGroupLocked = !!groupRenderData?.groupExecutionLocked;
    const resolvedExecutionId = activeExecutionCandidate?.executionId || (
        isGroupLocked ? fallbackGroupExecutionId : ''
    );

    if (!resolvedExecutionId) return null;

    return {
        groupNodeId: normalizedGroupNodeId,
        executionId: resolvedExecutionId,
        groupFunctionNodeIds,
        groupChildNodeIds,
    };
};

const buildActiveGroupExecutionMetaMap = (projectNodes = []) => {
    const groupMetaMap = new Map();

    projectNodes
        .filter((node) => String(node?.nodeType || '') === 'group')
        .forEach((groupNode) => {
            const meta = inferActiveGroupExecutionMeta(groupNode?.id, projectNodes);
            if (meta?.executionId) {
                groupMetaMap.set(String(meta.groupNodeId), meta);
            }
        });

    return groupMetaMap;
};

const markExecutionPrimaryOutput = (outputs = [], selectedOutputId = '') => {
    const normalizedOutputs = normalizeExecutionOutputs(outputs);
    if (!normalizedOutputs.length) return [];

    const normalizedSelectedOutputId = String(selectedOutputId ?? '');
    const hasSelectedOutput = normalizedOutputs.some((item) => (
        String(item?.id ?? '') === normalizedSelectedOutputId
    ));
    if (!hasSelectedOutput) return normalizedOutputs;

    return normalizedOutputs.map((item) => ({
        ...item,
        primary: String(item?.id ?? '') === normalizedSelectedOutputId,
    }));
};

const getPreferredNodeImageUrl = (node) => {
    if (!node) return '';
    if (node.type === 'imageNode') return node.data?.src || '';

    const outputs = getNodeImageOutputs(node);
    const primaryOutput = outputs.find((item) => item?.primary && item?.url);
    if (primaryOutput?.url) return primaryOutput.url;

    const selectedOutput = outputs.find((item) => String(item?.id ?? '') === String(node.data?.selectedOutputId ?? ''));
    if (selectedOutput?.url) return selectedOutput.url;

    return outputs[0]?.url || '';
};

const getNodeImageSequenceLabel = (node) => (
    node?.type === 'groupNode'
        ? ''
        : (
            node?.data?.imageSequenceLabel
            || getImageSequenceLabel(getNodeImageSequence(node))
            || (node?.type === 'imageNode' ? node?.data?.label || '' : '')
        )
);

const getNodeDownloadImageItems = (node) => {
    if (!node) return [];

    if (node.type === 'imageNode') return [];

    const outputs = getNodeImageOutputs(node);
    return outputs.map((item, index) => ({
        url: item.url,
        nodeId: node.id,
        nodeLabel: node.data?.label || 'node',
        outputId: item.id || '',
        index,
    }));
};

const getNodeSourceImageItems = (node) => {
    if (!node) return [];

    if (node.type === 'imageNode') return [];

    const inputParams = node.data?.inputParams || {};
    const uploadUrls = [
        ...(Array.isArray(inputParams.imageUrls) ? inputParams.imageUrls : []),
        inputParams.srcImage,
        inputParams.refImage,
        inputParams.commodityImage,
        inputParams.matchImage,
        inputParams.angle1,
        inputParams.angle2,
    ];

    return uploadUrls
        .filter((url) => typeof url === 'string' && url)
        .map((url, index) => ({
            url,
            nodeId: node.id,
            nodeLabel: node.data?.label || 'node',
            index,
            category: 'source',
        }));
};

const getNodeResultImageItems = (node) => (
    getNodeDownloadImageItems(node).map((item) => ({
        ...item,
        category: 'result',
    }))
);

const getLockedGroupIdSet = (nodes) => {
    const lockedGroupIds = new Set();

    nodes.forEach((node) => {
        if (node.type === 'groupNode' && node.data?.groupExecutionLocked) {
            lockedGroupIds.add(String(node.id));
            return;
        }

        if (!node.parentId) return;

        const latestExecutionStatus = String(
            node.data?.latestExecution?.status
            || node.data?.executionStatus
            || '',
        ).toLowerCase();
        const isExecutingChild = node.data?.groupExecutionLocked || latestExecutionStatus === 'processing';

        if (isExecutingChild) {
            lockedGroupIds.add(String(node.parentId));
        }
    });

    return lockedGroupIds;
};

const isNodeLockedByGroup = (node, lockedGroupIds) => {
    if (!node) return false;

    const nodeId = String(node.id || '');
    const parentId = String(node.parentId || '');

    return lockedGroupIds.has(nodeId) || (!!parentId && lockedGroupIds.has(parentId));
};

const isGroupNode = (node) => node?.type === 'groupNode';

const validateGroupFlowStructure = (nodes = [], edges = []) => {
    const normalizedNodes = Array.isArray(nodes) ? nodes.filter(Boolean) : [];

    if (normalizedNodes.length < 2) {
        return {
            valid: false,
            message: '至少选择两个节点后才能打组',
        };
    }

    const nodeIdSet = new Set(normalizedNodes.map((node) => String(node.id || '')));
    const adjacencyMap = new Map();

    normalizedNodes.forEach((node) => {
        adjacencyMap.set(String(node.id || ''), new Set());
    });

    (Array.isArray(edges) ? edges : []).forEach((edge) => {
        const sourceId = String(edge?.source || '');
        const targetId = String(edge?.target || '');

        if (!nodeIdSet.has(sourceId) || !nodeIdSet.has(targetId)) return;
        adjacencyMap.get(sourceId)?.add(targetId);
        adjacencyMap.get(targetId)?.add(sourceId);
    });

    const [firstNode] = normalizedNodes;
    const queue = [String(firstNode.id || '')];
    const visited = new Set(queue);

    while (queue.length) {
        const currentId = queue.shift();
        const linkedNodeIds = adjacencyMap.get(currentId) || new Set();

        linkedNodeIds.forEach((linkedNodeId) => {
            if (visited.has(linkedNodeId)) return;
            visited.add(linkedNodeId);
            queue.push(linkedNodeId);
        });
    }

    if (visited.size !== nodeIdSet.size) {
        return {
            valid: false,
            message: '一个分组内只能包含一条完整流程，不能混入其他流程或单独节点',
        };
    }

    return {
        valid: true,
        message: '',
    };
};

const getTopLevelNodeId = (node, nodeMap) => {
    if (!node) return '';

    let currentNode = node;
    let parentId = currentNode.parentId;

    while (parentId) {
        const parentNode = nodeMap.get(String(parentId));
        if (!parentNode) break;
        currentNode = parentNode;
        parentId = parentNode.parentId;
    }

    return String(currentNode?.id || node.id || '');
};

const mergeCreativeImgUploads = (manualUploads = [], linkedUploads = [], preferredOrder = []) => {
    const normalizedManualUploads = manualUploads.filter(Boolean);
    const normalizedLinkedUploads = linkedUploads
        .map((item) => item?.url || '')
        .filter(Boolean);
    const manualQueue = normalizedManualUploads.slice();
    const linkedQueue = normalizedLinkedUploads.slice();
    const mergedUploads = [];
    const consumeUrl = (queue, url) => {
        const index = queue.findIndex((item) => item === url);
        if (index < 0) return false;
        queue.splice(index, 1);
        mergedUploads.push(url);
        return true;
    };
    const consumeLinkedUrl = (edgeId) => {
        const linkedIndex = linkedUploads.findIndex((item) => String(item?.edgeId || '') === String(edgeId || ''));
        if (linkedIndex < 0) return false;
        const linkedUrl = linkedUploads[linkedIndex]?.url || '';
        const queueIndex = linkedQueue.findIndex((item) => item === linkedUrl);
        if (queueIndex < 0) return false;
        linkedQueue.splice(queueIndex, 1);
        mergedUploads.push(linkedUrl);
        return true;
    };

    (Array.isArray(preferredOrder) ? preferredOrder : []).filter(Boolean).forEach((orderItem) => {
        if (mergedUploads.length >= FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) return;

        if (orderItem && typeof orderItem === 'object') {
            if (orderItem.type === 'linked' && consumeLinkedUrl(orderItem.edgeId)) return;
            if (orderItem.type === 'manual' && consumeUrl(manualQueue, orderItem.url)) return;
            if (consumeUrl(manualQueue, orderItem.url)) return;
            consumeUrl(linkedQueue, orderItem.url);
            return;
        }

        if (consumeUrl(manualQueue, orderItem)) return;
        consumeUrl(linkedQueue, orderItem);
    });

    manualQueue.concat(linkedQueue).forEach((url) => {
        if (mergedUploads.length < FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) {
            mergedUploads.push(url);
        }
    });

    if (mergedUploads.length <= FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img) {
        return mergedUploads;
    }

    return [
        ...mergedUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img - 1),
        mergedUploads[mergedUploads.length - 1],
    ];
};

const extractCreativeImgManualUploads = (storedUploads = [], linkedUploads = []) => {
    const linkedUrlCountMap = linkedUploads.reduce((acc, item) => {
        const url = item?.url || '';
        if (!url) return acc;
        acc.set(url, (acc.get(url) || 0) + 1);
        return acc;
    }, new Map());

    return (Array.isArray(storedUploads) ? storedUploads : [])
        .filter(Boolean)
        .filter((url) => {
            const remainCount = linkedUrlCountMap.get(url) || 0;
            if (remainCount <= 0) return true;
            linkedUrlCountMap.set(url, remainCount - 1);
            return false;
        })
        .slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
};

const getPanelStateObject = (node) => (
    node?.data?.panelState && typeof node.data.panelState === 'object'
        ? node.data.panelState
        : {}
);

const getCreativeImgOccupiedCount = (node, edges = []) => {
    if (!node || !isMultiImageFunctionKey(node?.data?.functionKey)) {
        return 0;
    }

    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        const linkedSlotSources = (
            panelState.linkedSlotSources
            && typeof panelState.linkedSlotSources === 'object'
            && !Array.isArray(panelState.linkedSlotSources)
        ) ? panelState.linkedSlotSources : {};
        const manualSourceCount = linkedSlotSources.source?.edgeId ? 0 : (manualUploads[0] ? 1 : 0);
        const manualReferenceCount = manualUploads.slice(1).filter(Boolean).length;
        const incomingEdgeCount = (Array.isArray(edges) ? edges : []).filter((edge) => edge.target === node.id).length;

        return manualSourceCount + manualReferenceCount + incomingEdgeCount;
    }

    const panelState = getPanelStateObject(node);
    const linkedUploads = Array.isArray(panelState.linkedUploads) ? panelState.linkedUploads : [];
    const storedUploads = Array.isArray(panelState.manualUploads)
        ? panelState.manualUploads
        : Array.isArray(panelState.uploads)
            ? panelState.uploads
            : Array.isArray(node?.data?.inputParams?.imageUrls)
                ? node.data.inputParams.imageUrls
                : [];
    const manualUploadCount = extractCreativeImgManualUploads(storedUploads, linkedUploads).length;
    const incomingEdgeCount = (Array.isArray(edges) ? edges : []).filter((edge) => edge.target === node.id).length;

    return manualUploadCount + incomingEdgeCount;
};

const getLinkedSlotSources = (node) => {
    const linkedSlotSources = getPanelStateObject(node).linkedSlotSources;
    return (
        linkedSlotSources
        && typeof linkedSlotSources === 'object'
        && !Array.isArray(linkedSlotSources)
    )
        ? linkedSlotSources
        : {};
};

const getSlotUploadValue = (node, slotKey) => {
    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        if (slotKey === 'source') {
            return manualUploads[0] || node?.data?.inputParams?.srcImage || '';
        }
        if (slotKey === 'reference') {
            return manualUploads[1] || node?.data?.inputParams?.refImage || '';
        }
    }

    const panelState = getPanelStateObject(node);
    const uploads = panelState.uploads && typeof panelState.uploads === 'object'
        ? panelState.uploads
        : {};
    const paramKey = getSlotParamKey(node?.data?.functionKey, slotKey);

    return uploads[slotKey] || (paramKey ? node?.data?.inputParams?.[paramKey] : '') || '';
};

const getTemplateReplaceReferenceManualCount = (node) => {
    const panelState = getPanelStateObject(node);
    const manualUploads = Array.isArray(panelState.manualUploads)
        ? panelState.manualUploads
        : [];

    return manualUploads.slice(1).filter(Boolean).length;
};

const getTemplateReplaceReferenceEdgeCount = (nodeId, edges = []) => (
    (Array.isArray(edges) ? edges : []).filter((edge) => (
        String(edge.target) === String(nodeId)
        && String(edge.data?.inputSlotKey || '') === 'reference'
    )).length
);

const getNodeValidationError = (node) => {
    if (!node) return '';

    if (node.type === 'imageNode') {
        return node?.data?.src ? '' : `${node?.data?.label || '图片节点'}缺少图片`;
    }

    if (node.type !== 'functionNode') {
        return '';
    }

    const functionKey = node?.data?.functionKey || '';
    const nodeLabel = node?.data?.label || '功能节点';
    const inputParams = node?.data?.inputParams || {};

    if (functionKey === 'creativeTxt2Img') {
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'creativeImg2Img') {
        const hasPrompt = String(inputParams.prompt || '').trim().length > 0;
        const hasImageUrls = Array.isArray(inputParams.imageUrls) && inputParams.imageUrls.filter(Boolean).length > 0;
        return (hasPrompt || hasImageUrls) ? '' : `${nodeLabel}缺少提示词或参考图`;
    }

    if (functionKey === 'templateReplace') {
        const imageUrls = Array.isArray(inputParams.imageUrls)
            ? inputParams.imageUrls.filter(Boolean)
            : [];
        if (imageUrls.length === 0) {
            return `${nodeLabel}缺少模特图`;
        }
        if (imageUrls.length < 2) {
            return `${nodeLabel}缺少后面的图`;
        }
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'repose') {
        const sourceValue = getSlotUploadValue(node, 'source');
        const poseMode = inputParams.mode || node?.data?.panelState?.mode || 'ref';
        if (!sourceValue) {
            return `${nodeLabel}缺少源素材`;
        }
        if (poseMode === 'ref') {
            return getSlotUploadValue(node, 'reference') ? '' : `${nodeLabel}缺少参考图`;
        }
        return String(inputParams.prompt || '').trim() ? '' : `${nodeLabel}缺少提示词`;
    }

    if (functionKey === 'relight') {
        const sourceValue = getSlotUploadValue(node, 'source');
        const referenceValue = getSlotUploadValue(node, 'reference');
        const matchValue = getSlotUploadValue(node, 'match');
        const relightMode = inputParams.mode || node?.data?.panelState?.mode || 'single';
        if (!sourceValue) {
            return `${nodeLabel}缺少源素材`;
        }
        if (relightMode === 'suit') {
            if (!referenceValue && !matchValue) {
                return `${nodeLabel}缺少上装或下装`;
            }
        } else if (!referenceValue) {
            return relightMode === 'material' ? `${nodeLabel}缺少面料图` : `${nodeLabel}缺少参考图`;
        }
        if (relightMode === 'material' && !String(inputParams.targetArea || '').trim()) {
            return `${nodeLabel}缺少换色区域`;
        }
        return '';
    }

    if (functionKey === 'shoesRefiner') {
        if (!getSlotUploadValue(node, 'source')) {
            return `${nodeLabel}缺少源素材`;
        }
        if (!getSlotUploadValue(node, 'commodity')) {
            return `${nodeLabel}缺少商品图`;
        }
        return '';
    }

    const requiredInputs = FUNCTION_REQUIRED_INPUTS_MAP[functionKey] || [];
    const missingLabels = requiredInputs
        .filter((item) => !getSlotUploadValue(node, item.key))
        .map((item) => item.label);

    return missingLabels.length > 0
        ? `${nodeLabel}缺少${missingLabels.join('、')}`
        : '';
};

const isSlotManuallyOccupied = (node, slotKey) => {
    if (node?.data?.functionKey === 'templateReplace') {
        const panelState = getPanelStateObject(node);
        const manualUploads = Array.isArray(panelState.manualUploads)
            ? panelState.manualUploads
            : [];
        if (slotKey === 'source') {
            return !!manualUploads[0];
        }
        if (slotKey === 'reference') {
            return getTemplateReplaceReferenceManualCount(node) >= (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1);
        }
    }

    const linkedState = getLinkedSlotSources(node)[slotKey];
    if (linkedState?.edgeId) return false;

    const uploadValue = getSlotUploadValue(node, slotKey);
    return !!uploadValue && uploadValue !== FLOW_LINK_PLACEHOLDER_ICON;
};

const getAvailableEdgeSlotKeyForTarget = ({
    edges = [],
    targetNode,
    slotKeys = [],
    excludeEdgeId = null,
    preferredSlotKey = null,
}) => {
    const normalizedSlotKeys = slotKeys.filter(Boolean);
    if (normalizedSlotKeys.length === 0 || !targetNode) return null;

    if (targetNode?.data?.functionKey === 'templateReplace') {
        const filteredEdges = (Array.isArray(edges) ? edges : []).filter((edge) => edge.id !== excludeEdgeId);
        const sourceOccupied = filteredEdges.some((edge) => (
            String(edge.target) === String(targetNode.id)
            && String(edge.data?.inputSlotKey || '') === 'source'
        )) || isSlotManuallyOccupied(targetNode, 'source');
        const referenceOccupiedCount = getTemplateReplaceReferenceEdgeCount(targetNode.id, filteredEdges)
            + getTemplateReplaceReferenceManualCount(targetNode);

        if (!sourceOccupied && normalizedSlotKeys.includes('source')) {
            return 'source';
        }

        if (
            normalizedSlotKeys.includes('reference')
            && referenceOccupiedCount < (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1)
        ) {
            return 'reference';
        }

        return null;
    }

    const occupiedSlotKeys = new Set(
        edges
            .filter((edge) => edge.target === targetNode.id && edge.id !== excludeEdgeId)
            .map((edge) => edge.data?.inputSlotKey)
            .filter(Boolean),
    );

    normalizedSlotKeys.forEach((slotKey) => {
        if (isSlotManuallyOccupied(targetNode, slotKey)) {
            occupiedSlotKeys.add(slotKey);
        }
    });

    if (preferredSlotKey && normalizedSlotKeys.includes(preferredSlotKey) && !occupiedSlotKeys.has(preferredSlotKey)) {
        return preferredSlotKey;
    }

    return normalizedSlotKeys.find((slotKey) => !occupiedSlotKeys.has(slotKey)) || null;
};

// 同步连线目标节点的输入数据
const syncConnectedTargetNodes = (nodes, edges, options = {}) => {
    const { sourceNodeId = null, targetNodeId = null } = options;
    const targetNodeIds = new Set(
        edges
            .filter((edge) => (
                (!sourceNodeId || edge.source === sourceNodeId)
                && (!targetNodeId || edge.target === targetNodeId)
            ))
            .map((edge) => edge.target),
    );
    if (targetNodeId) {
        targetNodeIds.add(targetNodeId);
    }

    if (targetNodeIds.size === 0) return nodes;

    const nodeMap = new Map(nodes.map((node) => [node.id, node]));

    return nodes.map((node) => {
        if (!targetNodeIds.has(node.id) || node.type !== 'functionNode') return node;

        const targetEdges = edges.filter((edge) => edge.target === node.id);
        if (isMultiImageFunctionKey(node.data?.functionKey)) {
            const previousPanelState = node.data?.panelState || {};
            if (node.data?.functionKey === 'templateReplace') {
                const previousLinkedSlotSources = (
                    previousPanelState.linkedSlotSources
                    && typeof previousPanelState.linkedSlotSources === 'object'
                    && !Array.isArray(previousPanelState.linkedSlotSources)
                )
                    ? previousPanelState.linkedSlotSources
                    : {};
                const previousManualUploads = Array.isArray(previousPanelState.manualUploads)
                    ? previousPanelState.manualUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace)
                    : [
                        node.data?.inputParams?.srcImage || '',
                        node.data?.inputParams?.refImage || '',
                    ];
                const nextManualUploads = previousManualUploads.slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace);
                const nextLinkedSlotSources = {};
                const getEdgeLinkedState = (edge) => {
                    const sourceNode = nodeMap.get(edge.source);
                    const actualUrl = getPreferredNodeImageUrl(sourceNode);
                    return {
                        edgeId: String(edge.id),
                        sourceNodeId: String(edge.source),
                        url: actualUrl || FLOW_LINK_PLACEHOLDER_ICON,
                        imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
                    };
                };
                const sourceEdge = targetEdges.find((edge) => String(edge.data?.inputSlotKey || '') === 'source');
                const referenceEdges = targetEdges.filter((edge) => String(edge.data?.inputSlotKey || '') === 'reference');
                const primaryReferenceEdge = referenceEdges[0];
                const extraReferenceEdges = referenceEdges.slice(1);
                const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
                    ? previousPanelState.linkedUploads
                    : [];
                const previousLinkedUploadMap = new Map(
                    previousLinkedUploads
                        .filter((item) => item?.edgeId)
                        .map((item) => [String(item.edgeId), item]),
                );
                const nextLinkedUploads = extraReferenceEdges.map((edge) => {
                    const previousLinkedUpload = previousLinkedUploadMap.get(String(edge.id));
                    const linkedState = getEdgeLinkedState(edge);

                    return {
                        ...linkedState,
                        url: linkedState.url || previousLinkedUpload?.url || FLOW_LINK_PLACEHOLDER_ICON,
                    };
                });

                if (sourceEdge) {
                    nextLinkedSlotSources.source = getEdgeLinkedState(sourceEdge);
                }
                if (primaryReferenceEdge) {
                    nextLinkedSlotSources.reference = getEdgeLinkedState(primaryReferenceEdge);
                }

                const sourceValue = nextLinkedSlotSources.source?.url || nextManualUploads[0] || '';
                const manualReferenceValues = nextManualUploads.slice(1).filter(Boolean);
                const linkedReferenceValues = [
                    nextLinkedSlotSources.reference?.url || '',
                    ...nextLinkedUploads.map((item) => item?.url || ''),
                ].filter(Boolean);
                const referenceHeadValue = linkedReferenceValues[0] || manualReferenceValues[0] || '';
                const extraReferenceValues = [
                    ...linkedReferenceValues.slice(1),
                    ...(linkedReferenceValues.length > 0 ? manualReferenceValues : manualReferenceValues.slice(1)),
                ].filter(Boolean);
                const normalizedManualUploads = [
                    nextLinkedSlotSources.source?.url ? '' : (nextManualUploads[0] || ''),
                    ...manualReferenceValues,
                ].slice(0, FUNCTION_NODE_INPUT_LIMITS.templateReplace);
                const mergedUploads = [
                    sourceValue ? { url: sourceValue, source: nextLinkedSlotSources.source ? 'line' : 'upload' } : null,
                    referenceHeadValue ? { url: referenceHeadValue, source: nextLinkedSlotSources.reference ? 'line' : 'upload' } : null,
                    ...extraReferenceValues.map((url, index) => ({ url, source: index < (linkedReferenceValues.length - 1) ? 'line' : 'upload' })),
                ].filter(Boolean);
                const mergedUploadItems = [
                    sourceValue
                        ? (nextLinkedSlotSources.source
                            ? { type: 'linked', edgeId: nextLinkedSlotSources.source.edgeId, url: sourceValue }
                            : { type: 'manual', url: sourceValue })
                        : null,
                    referenceHeadValue
                        ? (nextLinkedSlotSources.reference
                            ? { type: 'linked', edgeId: nextLinkedSlotSources.reference.edgeId, url: referenceHeadValue }
                            : { type: 'manual', url: referenceHeadValue })
                        : null,
                    ...nextLinkedUploads.map((item) => ({ type: 'linked', edgeId: item.edgeId, url: item.url })),
                    ...manualReferenceValues.map((url) => ({ type: 'manual', url })),
                ].filter(Boolean);
                const hasChanged = (
                    JSON.stringify(previousPanelState.linkedSlotSources || {}) !== JSON.stringify(nextLinkedSlotSources)
                    || JSON.stringify(previousPanelState.linkedUploads || []) !== JSON.stringify(nextLinkedUploads)
                    || JSON.stringify(previousPanelState.manualUploads || []) !== JSON.stringify(normalizedManualUploads)
                    || JSON.stringify(previousPanelState.uploads || []) !== JSON.stringify(mergedUploads)
                    || JSON.stringify(previousPanelState.uploadItems || []) !== JSON.stringify(mergedUploadItems)
                    || JSON.stringify(node.data?.inputParams?.imageUrls || []) !== JSON.stringify(mergedUploads)
                    || String(node.data?.inputParams?.srcImage || '') !== String(sourceValue || '')
                    || String(node.data?.inputParams?.refImage || '') !== String(referenceHeadValue || '')
                );

                if (!hasChanged) return node;

                return {
                    ...node,
                    data: {
                        ...node.data,
                        inputParams: {
                            ...(node.data?.inputParams || {}),
                            imageUrls: mergedUploads,
                            srcImage: sourceValue,
                            refImage: referenceHeadValue,
                        },
                        panelState: {
                            ...previousPanelState,
                            manualUploads: normalizedManualUploads,
                            linkedUploads: nextLinkedUploads,
                            linkedSlotSources: nextLinkedSlotSources,
                            uploadItems: mergedUploadItems,
                            uploads: mergedUploads,
                        },
                    },
                };
            }

            const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
                ? previousPanelState.linkedUploads
                : [];
            const previousLinkedUploadMap = new Map(
                previousLinkedUploads
                    .filter((item) => item?.edgeId)
                    .map((item) => [String(item.edgeId), item]),
            );
            const linkedUploads = targetEdges.map((edge) => {
                const sourceNode = nodeMap.get(edge.source);
                const previousLinkedUpload = previousLinkedUploadMap.get(String(edge.id));
                const actualUrl = getPreferredNodeImageUrl(sourceNode);
                return {
                    edgeId: String(edge.id),
                    sourceNodeId: edge.source,
                    url: actualUrl || previousLinkedUpload?.url || FLOW_LINK_PLACEHOLDER_ICON,
                    imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
                };
            });
            const manualUploads = Array.isArray(previousPanelState.manualUploads)
                ? previousPanelState.manualUploads.filter(Boolean)
                : extractCreativeImgManualUploads(
                    Array.isArray(previousPanelState.uploads)
                        ? previousPanelState.uploads
                        : (Array.isArray(node.data?.inputParams?.imageUrls)
                            ? node.data.inputParams.imageUrls
                            : []),
                    linkedUploads,
                );
            const previousOrder = Array.isArray(previousPanelState.uploadItems)
                ? previousPanelState.uploadItems
                : Array.isArray(previousPanelState.uploads)
                ? previousPanelState.uploads.filter(Boolean)
                : [];
            const nextLinkedUploadMap = new Map(
                linkedUploads
                    .filter((item) => item?.edgeId)
                    .map((item) => [String(item.edgeId), item]),
            );
            const preferredOrder = previousOrder.map((url) => {
                const matchedPreviousLinkedUpload = previousLinkedUploads.find((item) => item?.url === url);
                const nextLinkedUpload = matchedPreviousLinkedUpload?.edgeId
                    ? nextLinkedUploadMap.get(String(matchedPreviousLinkedUpload.edgeId))
                    : null;

                return nextLinkedUpload?.url || url;
            });
            linkedUploads.forEach((item) => {
                if (!item?.edgeId || previousLinkedUploadMap.has(String(item.edgeId))) return;
                preferredOrder.push({
                    type: 'linked',
                    edgeId: item.edgeId,
                    url: item.url,
                });
            });
            const mergedUploads = mergeCreativeImgUploads(manualUploads, linkedUploads, preferredOrder);
            const mergedUploadItems = preferredOrder
                .map((item) => {
                    if (item && typeof item === 'object') {
                        if (item.type === 'linked') {
                            const nextLinkedUpload = nextLinkedUploadMap.get(String(item.edgeId || ''));
                            return nextLinkedUpload
                                ? { type: 'linked', edgeId: nextLinkedUpload.edgeId, url: nextLinkedUpload.url }
                                : null;
                        }
                        if (item.type === 'manual' && item.url) {
                            return { type: 'manual', url: item.url };
                        }
                    }
                    return item ? { type: 'manual', url: item } : null;
                })
                .filter(Boolean);
            const hasChanged = (
                JSON.stringify(previousLinkedUploads) !== JSON.stringify(linkedUploads)
                || JSON.stringify(previousPanelState.uploads || []) !== JSON.stringify(mergedUploads)
                || JSON.stringify(previousPanelState.uploadItems || []) !== JSON.stringify(mergedUploadItems)
                || JSON.stringify(node.data?.inputParams?.imageUrls || []) !== JSON.stringify(mergedUploads)
            );

            if (!hasChanged) return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: {
                        ...(node.data?.inputParams || {}),
                        imageUrls: mergedUploads,
                        srcImage: mergedUploads[0] || '',
                        refImage: mergedUploads[1] || '',
                    },
                    panelState: {
                        ...previousPanelState,
                        manualUploads,
                        linkedUploads,
                        uploadItems: mergedUploadItems,
                        uploads: mergedUploads,
                    },
                },
            };
        }

        const previousPanelState = node.data?.panelState || {};
        const previousLinkedSlotSources = (
            previousPanelState.linkedSlotSources
            && typeof previousPanelState.linkedSlotSources === 'object'
            && !Array.isArray(previousPanelState.linkedSlotSources)
        )
            ? previousPanelState.linkedSlotSources
            : {};
        const managedSlotKeys = getFunctionNodeEdgeSlotKeys(node.data?.functionKey, node);
        const nextUploads = {
            ...(previousPanelState.uploads || {}),
        };
        const nextInputParams = {
            ...(node.data?.inputParams || {}),
        };
        const nextLinkedSlotSources = {
            ...previousLinkedSlotSources,
        };
        let changed = false;

        managedSlotKeys.forEach((slotKey) => {
            const paramKey = getSlotParamKey(node.data?.functionKey, slotKey);
            if (!paramKey) return;
            const targetEdge = targetEdges.find((edge) => edge.data?.inputSlotKey === slotKey);
            const currentValue = nextUploads[slotKey] || nextInputParams[paramKey] || '';
            const previousLinkedState = previousLinkedSlotSources[slotKey];
            const previousLinkedUrl = previousLinkedState?.url || '';
            const wasLinkedControlled = !!previousLinkedUrl && currentValue === previousLinkedUrl;

            if (!targetEdge) {
                if (wasLinkedControlled) {
                    if ((nextUploads[slotKey] || '') !== '') {
                        nextUploads[slotKey] = '';
                        changed = true;
                    }

                    if ((nextInputParams[paramKey] || '') !== '') {
                        nextInputParams[paramKey] = '';
                        changed = true;
                    }
                }

                if (slotKey in nextLinkedSlotSources) {
                    delete nextLinkedSlotSources[slotKey];
                    changed = true;
                }
                return;
            }

            const sourceNode = nodeMap.get(targetEdge.source);
            const actualUrl = getPreferredNodeImageUrl(sourceNode);
            const nextUrl = actualUrl || FLOW_LINK_PLACEHOLDER_ICON;
            const nextLinkedState = {
                edgeId: String(targetEdge.id),
                sourceNodeId: String(targetEdge.source),
                url: nextUrl,
                imageSequenceLabel: getNodeImageSequenceLabel(sourceNode),
            };

            if ((nextUploads[slotKey] || '') !== nextUrl) {
                nextUploads[slotKey] = nextUrl;
                changed = true;
            }

            if ((nextInputParams[paramKey] || '') !== nextUrl) {
                nextInputParams[paramKey] = nextUrl;
                changed = true;
            }

            if (JSON.stringify(previousLinkedState || null) !== JSON.stringify(nextLinkedState)) {
                nextLinkedSlotSources[slotKey] = nextLinkedState;
                changed = true;
            }
        });

        if (!changed) return node;

        return {
            ...node,
            data: {
                ...node.data,
                inputParams: nextInputParams,
                panelState: {
                    ...previousPanelState,
                    uploads: nextUploads,
                    linkedSlotSources: nextLinkedSlotSources,
                },
            },
        };
    });
};

const FreeCanvasInner = () => {
    const { member, globalUser, setGlobalUser, globalOrgInfo } = useContext(AppContext);

    // FreeCanvas 隐藏了全局 header，需要在此自行初始化 globalUser，
    // 否则 loadProjectList 因 resolvedProjectMember 为空而始终跳过请求。
    useEffect(() => {
        if (globalUser) return;
        // 用 setTimeout 0 确保 App.js 的 eventBus 监听已完成注册
        const timer = setTimeout(() => {
            eventBus.emit('updateUserInfo');
        }, 0);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const navigate = useNavigate();
    const location = useLocation();
    const targetProjectId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_PROJECT_ID_QUERY_KEY) || '';
    }, [location.search]);
    const targetProjectName = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_PROJECT_NAME_QUERY_KEY) || '';
    }, [location.search]);
    const targetExecutionId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_EXECUTION_ID_QUERY_KEY) || '';
    }, [location.search]);
    const targetProjectMemberUserId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(TARGET_PROJECT_MEMBER_USER_ID_QUERY_KEY) || '';
    }, [location.search]);
    const isPmView = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(PM_VIEW_QUERY_KEY) === '1';
    }, [location.search]);
    const forcePersonalMember = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(FORCE_PERSONAL_MEMBER_QUERY_KEY) === '1';
    }, [location.search]);
    const skipAutoSelect = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(SKIP_AUTO_SELECT_QUERY_KEY) === '1' || !!targetProjectId;
    }, [location.search, targetProjectId]);
    const createProjectRequested = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get(CREATE_PROJECT_QUERY_KEY) === '1';
    }, [location.search]);
    const canvasRef = useRef(null);
    const reactFlowRef = useRef(null);
    const uploadInputRef = useRef(null);
    const nodesRef = useRef([]);
    const edgesRef = useRef([]);
    const nextImageNodeNumberRef = useRef(1);
    const nextGroupNodeNumberRef = useRef(1);
    const historyRef = useRef([]);
    const redoHistoryRef = useRef([]);
    const clipboardRef = useRef(null);
    const pasteCountRef = useRef(0);
    const dragSnapshotRef = useRef(null);
    const altDragDuplicateRef = useRef(null);
    const viewportRef = useRef(DEFAULT_VIEWPORT);
    const persistedViewportRef = useRef(DEFAULT_VIEWPORT);
    const viewportPersistTimerRef = useRef(null);
    const viewportInteractionTimerRef = useRef(null);
    const executionPollerRef = useRef({});
    const executionRequestAbortRef = useRef({});
    const pageActiveRef = useRef(true);
    const manualAddCountRef = useRef(0);
    const projectListLoadingRef = useRef(false);
    const projectPageIndexRef = useRef(1);
    const projectKeywordRef = useRef('');
    const projectRequestIdRef = useRef(0);
    const projectDetailRequestIdRef = useRef(0);
    const projectVersionMapRef = useRef({});
    const currentProjectVersionRef = useRef(0);
    const activeProjectRef = useRef(null);
    const storedLastActiveProjectRef = useRef(getStoredLastActiveProject());
    const lastActiveProjectIdRef = useRef(storedLastActiveProjectRef.current.projectId || '');
    const lastActiveProjectPageIndexRef = useRef(storedLastActiveProjectRef.current.pageIndex || 1);
    const hasHydratedLastActiveProjectRef = useRef(false);
    const createProjectHandledRef = useRef(false);
    const creativeBootstrapHandledRef = useRef(false);
    const canvasExampleCreatingRef = useRef(false);
    const skipProjectDetailLoadRef = useRef('');
    const targetProjectHandledRef = useRef('');
    const targetExecutionHandledRef = useRef('');
    const batchUpdateQueueRef = useRef(Promise.resolve());
    const pendingFunctionSwitchRef = useRef(new Map());
    const [activeId, setActiveId] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
    const [projects, setProjects] = useState([]);
    const [projectListLoading, setProjectListLoading] = useState(false);
    const [projectListHasMore, setProjectListHasMore] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [materialLibraryOpen, setMaterialLibraryOpen] = useState(false);
    const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
    const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
    const [saveTemplateGroupId, setSaveTemplateGroupId] = useState('');
    const [saveTemplateName, setSaveTemplateName] = useState('');
    const [publishTemplateSync, setPublishTemplateSync] = useState(false);
    const [saveTemplateSubmitting, setSaveTemplateSubmitting] = useState(false);
    const [zoom, setZoom] = useState(100);
    const [viewportInteracting, setViewportInteracting] = useState(false);
    const [activeTool, setActiveTool] = useState('select');
    const [isSpacePressed, setIsSpacePressed] = useState(false);
    const [nextImageNodeNumber, setNextImageNodeNumber] = useState(1);
    const [nextGroupNodeNumber, setNextGroupNodeNumber] = useState(1);
    const [activeGroupId, setActiveGroupId] = useState(null);
    const [runGroupSkipSelect, setRunGroupSkipSelect] = useState(() => {
        if (typeof window === 'undefined') return true;

        const rawValue = window.localStorage.getItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY);
        if (rawValue !== 'true' && rawValue !== 'false') {
            window.localStorage.setItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY, 'true');
            return true;
        }
        return rawValue !== 'false';
    });
    const [panelDismissToken, setPanelDismissToken] = useState(0);
    const [selectedNodeIds, setSelectedNodeIds] = useState([]);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [contextMenu, setContextMenu] = useState({
        open: false,
        scope: 'pane',
        x: 0,
        y: 0,
        nodeId: null,
        flowPosition: null,
    });
    const [toolbarLoadingKey, setToolbarLoadingKey] = useState('');

    useEffect(() => {
        if (typeof window === 'undefined') return;
        window.localStorage.setItem(RUN_GROUP_SKIP_SELECT_STORAGE_KEY, String(runGroupSkipSelect));
    }, [runGroupSkipSelect]);

    useEffect(() => {
        if (!isPmView) return;
        setActiveTool('drag');
    }, [isPmView]);

    useEffect(() => {
        const handleRechargeOpen = () => {
            setPanelDismissToken((prev) => prev + 1);
            setContextMenu((prev) => ({ ...prev, open: false }));
            setHistoryModalOpen(false);
            setMaterialLibraryOpen(false);
            setTemplateLibraryOpen(false);
        };

        eventBus.on('recharge', handleRechargeOpen);
        return () => {
            eventBus.off('recharge', handleRechargeOpen);
        };
    }, []);

    // 获取当前激活项目
    const activeProject = useMemo(() => projects.find((project) => project.id === activeId), [projects, activeId]);
    const canvasEmpty = nodes.length === 0;
    const selectedNodeCount = selectedNodeIds.length;
    // 统计当前处于执行锁定中的分组
    const lockedGroupIds = useMemo(() => getLockedGroupIdSet(nodes), [nodes]);
    const isCanvasExecutionLocked = lockedGroupIds.size > 0;
    // 判断分组是否处于执行锁定状态
    const isGroupLocked = useCallback((groupId) => (
        !!groupId && lockedGroupIds.has(String(groupId))
    ), [lockedGroupIds]);
    // 判断节点是否因所属分组执行而被锁定
    const isNodeLocked = useCallback((nodeId) => {
        const node = nodesRef.current.find((item) => item.id === nodeId);
        return isNodeLockedByGroup(node, lockedGroupIds);
    }, [lockedGroupIds]);
    // 判断节点当前是否允许删除
    const isNodeDeletable = useCallback((nodeId) => {
        const node = nodesRef.current.find((item) => item.id === nodeId);
        if (!node) return false;

        return isGroupNode(node) || !isNodeLockedByGroup(node, lockedGroupIds);
    }, [lockedGroupIds]);
    // 批量判断节点中是否存在锁定项
    const areNodesLocked = useCallback((nodeIds) => (
        (nodeIds || []).some((nodeId) => isNodeLocked(nodeId))
    ), [isNodeLocked]);
    // 连线两端任一节点被锁定时，当前连线也不可编辑
    const isEdgeLocked = useCallback((edgeId) => {
        const edge = edgesRef.current.find((item) => item.id === edgeId);
        if (!edge) return false;

        return isNodeLocked(edge.source) || isNodeLocked(edge.target);
    }, [isNodeLocked]);

    useOnSelectionChange({
        onChange: ({ nodes: selectedNodes = [] }) => {
            setSelectedNodeIds(selectedNodes.map((node) => node.id));
        },
    });

    useEffect(() => {
        nodesRef.current = nodes;
    }, [nodes]);

    useEffect(() => {
        edgesRef.current = edges;
    }, [edges]);

    useEffect(() => {
        nextImageNodeNumberRef.current = nextImageNodeNumber;
    }, [nextImageNodeNumber]);

    useEffect(() => {
        nextGroupNodeNumberRef.current = nextGroupNodeNumber;
    }, [nextGroupNodeNumber]);

    useEffect(() => {
        projectListLoadingRef.current = projectListLoading;
    }, [projectListLoading]);

    useEffect(() => {
        projectVersionMapRef.current = projects.reduce((acc, project) => ({
            ...acc,
            [project.id]: Math.max(
                getVersionNumber(acc[project.id]),
                getVersionNumber(project.version),
            ),
        }), { ...projectVersionMapRef.current });
    }, [projects]);

    useEffect(() => {
        activeProjectRef.current = activeProject || null;
        if (activeProject) {
            currentProjectVersionRef.current = Math.max(
                getVersionNumber(currentProjectVersionRef.current),
                getVersionNumber(activeProject.version),
            );
        }
    }, [activeProject]);

    useEffect(() => () => {
        pageActiveRef.current = false;
        if (viewportPersistTimerRef.current) {
            clearTimeout(viewportPersistTimerRef.current);
        }
        Object.values(executionPollerRef.current).forEach((timer) => clearTimeout(timer));
        executionPollerRef.current = {};
        Object.values(executionRequestAbortRef.current).forEach((controller) => controller?.abort?.());
        executionRequestAbortRef.current = {};
    }, [skipAutoSelect]);

    // 记录当前画布快照，供撤销/重做使用
    const getSnapshot = useCallback(() => cloneValue({
        nodes: nodesRef.current,
        edges: edgesRef.current,
        nextImageNodeNumber: nextImageNodeNumberRef.current,
        nextGroupNodeNumber: nextGroupNodeNumberRef.current,
        activeGroupId,
        selectedNodeIds,
    }), [activeGroupId, selectedNodeIds]);

    // 恢复撤销/重做时保存的画布快照
    const restoreSnapshot = useCallback((snapshot) => {
        setNodes(cloneValue(snapshot.nodes));
        setEdges(cloneValue(snapshot.edges));
        setNextImageNodeNumber(snapshot.nextImageNodeNumber);
        setNextGroupNodeNumber(snapshot.nextGroupNodeNumber ?? 1);
        setActiveGroupId(snapshot.activeGroupId ?? null);
        setSelectedNodeIds(snapshot.selectedNodeIds ?? []);
    }, [setEdges, setNodes]);

    // 推入历史栈，并按需清空重做栈
    const pushHistory = useCallback((snapshot = getSnapshot(), options = {}) => {
        const { clearRedo = true } = options;
        historyRef.current.push(snapshot);
        if (historyRef.current.length > HISTORY_LIMIT) {
            historyRef.current.shift();
        }
        if (clearRedo) {
            redoHistoryRef.current = [];
        }
    }, [getSnapshot]);

    // 同步项目版本号，避免后续增量保存版本落后
    const syncProjectVersion = useCallback((projectId, version) => {
        const normalizedVersion = getVersionNumber(version);
        const currentVersion = getVersionNumber(projectVersionMapRef.current[projectId]);
        const nextVersion = Math.max(currentVersion, normalizedVersion);

        projectVersionMapRef.current = {
            ...projectVersionMapRef.current,
            [projectId]: nextVersion,
        };
        if (projectId === activeId) {
            currentProjectVersionRef.current = Math.max(
                getVersionNumber(currentProjectVersionRef.current),
                nextVersion,
            );
        }
        setProjects((prev) => prev.map((project) => (
            project.id === projectId
                ? { ...project, version: Math.max(getVersionNumber(project.version), nextVersion) }
                : project
        )));
    }, [activeId]);

    // 获取当前项目可用于保存的最新版本号
    const getCurrentProjectVersion = useCallback((projectId) => {
        if (projectId === activeId) {
            return Number(currentProjectVersionRef.current ?? 0);
        }

        const versionFromDetail = getVersionNumber(activeProjectRef.current?.id === projectId
            ? activeProjectRef.current?.version
            : 0);
        const versionFromList = getVersionNumber(projectVersionMapRef.current[projectId]);

        return Math.max(versionFromDetail, versionFromList, 0);
    }, [activeId]);

    // 重置画布视口到默认位置和缩放
    const resetCanvasViewport = useCallback(() => {
        viewportRef.current = { ...DEFAULT_VIEWPORT };
        persistedViewportRef.current = { ...DEFAULT_VIEWPORT };
        reactFlowRef.current?.setViewport?.(DEFAULT_VIEWPORT, { duration: 0 });
        setZoom(100);
    }, []);

    // 停止当前页面上的所有执行轮询请求
    const stopAllExecutionPolling = useCallback(() => {
        Object.values(executionPollerRef.current).forEach((timer) => clearTimeout(timer));
        executionPollerRef.current = {};
        Object.values(executionRequestAbortRef.current).forEach((controller) => controller?.abort?.());
        executionRequestAbortRef.current = {};
    }, []);

    // 将本次画布变更批量持久化到后端
    const persistCanvasBatch = useCallback(({
        beforeNodes = nodesRef.current,
        beforeEdges = edgesRef.current,
        afterNodes = nodesRef.current,
        afterEdges = edgesRef.current,
        beforeViewport = viewportRef.current,
        afterViewport = viewportRef.current,
        projectId = activeId,
    } = {}) => {
        if (!projectId) return Promise.resolve(null);

        const { nodeOps, edgeOps } = buildCanvasBatchOps({
            beforeNodes,
            beforeEdges,
            afterNodes,
            afterEdges,
            beforeViewport,
            afterViewport,
        });

        if (nodeOps.length === 0 && edgeOps.length === 0) {
            return Promise.resolve(null);
        }

        batchUpdateQueueRef.current = batchUpdateQueueRef.current
            .catch(() => null)
            .then(async () => {
                const submitBatchUpdate = (version) => batchUpdateFlowCanvas({
                    projectId,
                    version,
                    nodeOps,
                    edgeOps,
                });
                let result;

                try {
                    result = await submitBatchUpdate(getCurrentProjectVersion(projectId));
                } catch (error) {
                    if (!isCanvasVersionConflictError(error)) {
                        throw error;
                    }

                    const latestProjectDetail = await getFlowProjectDetail(projectId);
                    const latestVersion = getVersionNumber(latestProjectDetail?.version);
                    syncProjectVersion(projectId, latestVersion);
                    result = await submitBatchUpdate(latestVersion);
                }

                const nextVersion = getVersionNumber(
                    result?.version
                    ?? result?.canvasVersion
                    ?? result?.projectVersion
                    ?? (getCurrentProjectVersion(projectId) + 1),
                );

                syncProjectVersion(projectId, nextVersion);
                persistedViewportRef.current = { ...afterViewport };
                return result;
            })
            .catch((error) => {
                console.error(error);
                message.error(
                    isCanvasVersionConflictError(error)
                        ? CANVAS_VERSION_CONFLICT_MESSAGE
                        : getErrorMessage(error, '更新画布失败'),
                );
                return null;
            });

        return batchUpdateQueueRef.current;
    }, [activeId, getCurrentProjectVersion, syncProjectVersion]);

    // 切换项目或新建项目时重置本地画布状态
    const resetCanvasState = useCallback(() => {
        setNodes([]);
        setEdges([]);
        setActiveGroupId(null);
        setSelectedNodeIds([]);
        setContextMenu({
            open: false,
            scope: 'pane',
            x: 0,
            y: 0,
            nodeId: null,
            flowPosition: null,
        });
        setNextImageNodeNumber(1);
        setNextGroupNodeNumber(1);
        nextImageNodeNumberRef.current = 1;
        nextGroupNodeNumberRef.current = 1;
        currentProjectVersionRef.current = 0;
        manualAddCountRef.current = 0;
        pasteCountRef.current = 0;
        clipboardRef.current = null;
        historyRef.current = [];
        redoHistoryRef.current = [];
        dragSnapshotRef.current = null;
        batchUpdateQueueRef.current = Promise.resolve();
        stopAllExecutionPolling();
        resetCanvasViewport();
    }, [resetCanvasViewport, setEdges, setNodes, stopAllExecutionPolling]);

    // 根据 executionId 定位并高亮对应节点
    const focusNodeByExecutionId = useCallback((executionId) => {
        const normalizedExecutionId = String(executionId || '');
        if (!normalizedExecutionId) return false;

        const targetNode = nodesRef.current.find((node) => (
            String(
                node?.data?.latestExecution?.executionId
                || node?.latestExecution?.executionId
                || '',
            ) === normalizedExecutionId
        ));

        if (!targetNode?.id) return false;

        setSelectedNodeIds([targetNode.id]);
        setNodes((prev) => prev.map((node) => ({
            ...node,
            selected: node.id === targetNode.id,
        })));

        requestAnimationFrame(() => {
            reactFlowRef.current?.fitView?.({
                nodes: [{ id: targetNode.id }],
                duration: 300,
                padding: 0.4,
                maxZoom: 1.2,
            });
        });

        return true;
    }, [setNodes]);

    // 根据项目详情恢复整张画布
    const restoreProjectCanvas = useCallback((projectDetail) => {
        const projectNodes = Array.isArray(projectDetail?.nodes) ? projectDetail.nodes : [];
        const activeGroupExecutionMetaMap = buildActiveGroupExecutionMetaMap(projectNodes);
        const restoredBaseNodes = normalizeNodeImageSequences(sortNodesByParentDepth(
            projectNodes.map((node) => restoreNodeFromProject(node)),
        ));
        const nodeMap = new Map(projectNodes.map((node) => [String(node?.id ?? ''), node]));
        const restoredEdges = (projectDetail?.edges || []).map((edge) => restoreEdgeFromProject(edge, nodeMap));
        const restoredNodes = syncConnectedTargetNodes(restoredBaseNodes, restoredEdges).map((node) => {
            const nodeId = String(node.id || '');
            const parentId = String(node.parentId || '');
            const activeGroupMeta = activeGroupExecutionMetaMap.get(nodeId) || activeGroupExecutionMetaMap.get(parentId);

            if (!activeGroupMeta) return node;

            const isGroupNode = String(node.type || '') === 'groupNode';
            const currentExecutionId = String(node.data?.latestExecution?.executionId || '');
            const isFunctionNode = String(node.type || '') === 'functionNode';
            const isStaleExecutionResult = (
                isFunctionNode
                && currentExecutionId
                && currentExecutionId !== String(activeGroupMeta.executionId)
            );

            return {
                ...node,
                data: {
                    ...node.data,
                    groupExecutionLocked: true,
                    ...(isGroupNode
                        ? {}
                        : (
                            isStaleExecutionResult
                                ? {
                                    latestExecution: null,
                                    executionStatus: 'idle',
                                    activeExecutionId: '',
                                    selectedOutputId: '',
                                }
                                : {}
                        )),
                },
            };
        });
        const restoredViewport = getProjectViewport(projectDetail);

        setNodes(restoredNodes);
        setEdges(restoredEdges);
        setActiveGroupId(null);
        setSelectedNodeIds([]);
        setNextImageNodeNumber(getNextNodeImageSequenceNumber(restoredNodes));
        setNextGroupNodeNumber(getRestoredGroupNodeNumber(restoredNodes));
        nextImageNodeNumberRef.current = getNextNodeImageSequenceNumber(restoredNodes);
        nextGroupNodeNumberRef.current = getRestoredGroupNodeNumber(restoredNodes);
        manualAddCountRef.current = 0;
        pasteCountRef.current = 0;
        clipboardRef.current = null;
        historyRef.current = [];
        redoHistoryRef.current = [];
        dragSnapshotRef.current = null;
        batchUpdateQueueRef.current = Promise.resolve();
        stopAllExecutionPolling();
        viewportRef.current = restoredViewport || { ...DEFAULT_VIEWPORT };
        persistedViewportRef.current = restoredViewport || { ...DEFAULT_VIEWPORT };
        setContextMenu({
            open: false,
            scope: 'pane',
            x: 0,
            y: 0,
            nodeId: null,
            flowPosition: null,
        });
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (!reactFlowRef.current) return;

                if (restoredViewport) {
                    reactFlowRef.current.setViewport(restoredViewport, { duration: 0 });
                    setZoom(Math.round(restoredViewport.zoom * 100));
                    return;
                }

                if (restoredNodes.length === 0) {
                    resetCanvasViewport();
                    return;
                }

                reactFlowRef.current.fitView({
                    duration: 0,
                    padding: 0.18,
                });
                setZoom(Math.round((reactFlowRef.current.getZoom?.() ?? 1) * 100));
            });
        });
    }, [resetCanvasViewport, setEdges, setNodes, stopAllExecutionPolling]);

    // 关闭右键菜单
    const closeContextMenu = useCallback(() => {
        setContextMenu((prev) => ({ ...prev, open: false }));
    }, []);

    // 停止指定轮询任务及其请求
    const stopExecutionPolling = useCallback((pollerKey) => {
        const timer = executionPollerRef.current[pollerKey];
        if (timer) {
            clearTimeout(timer);
            delete executionPollerRef.current[pollerKey];
        }

        const controller = executionRequestAbortRef.current[pollerKey];
        if (controller) {
            controller.abort();
            delete executionRequestAbortRef.current[pollerKey];
        }
    }, []);

    // 停止单个节点的执行轮询
    const stopNodeExecutionPolling = useCallback((nodeId) => {
        stopExecutionPolling(nodeId);
    }, [stopExecutionPolling]);

    // 局部更新节点执行态相关字段
    const updateNodeExecutionState = useCallback((nodeId, patch) => {
        setNodes((prev) => prev.map((node) => (
            node.id === nodeId
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        ...patch,
                    },
                }
                : node
        )));
    }, [setNodes]);

    // 延迟保存视口变化，避免拖动画布时频繁请求
    const scheduleViewportPersist = useCallback((nextViewport) => {
        viewportRef.current = { ...nextViewport };
        setZoom(Math.round(nextViewport.zoom * 100));

        if (isPmView) return;
        if (!activeId || nodesRef.current.length === 0) return;

        if (viewportPersistTimerRef.current) {
            clearTimeout(viewportPersistTimerRef.current);
        }

        viewportPersistTimerRef.current = setTimeout(() => {
            const beforeViewport = persistedViewportRef.current;
            const afterViewport = viewportRef.current;
            const changed = (
                beforeViewport.x !== afterViewport.x
                || beforeViewport.y !== afterViewport.y
                || beforeViewport.zoom !== afterViewport.zoom
            );

            if (!changed) return;

            persistCanvasBatch({
                beforeNodes: nodesRef.current,
                beforeEdges: edgesRef.current,
                afterNodes: nodesRef.current,
                afterEdges: edgesRef.current,
                beforeViewport,
                afterViewport,
            });
        }, 240);
    }, [activeId, isPmView, persistCanvasBatch]);

    // 轮询单个节点的执行进度并回写结果
    const pollNodeExecution = useCallback(async (nodeId, executionId) => {
        console.log('[FreeCanvas][pollNodeExecution] called, nodeId:', nodeId, 'executionId:', executionId, 'pageActive:', pageActiveRef.current);
        if (!pageActiveRef.current) return;

        const controller = new AbortController();
        executionRequestAbortRef.current[nodeId]?.abort?.();
        executionRequestAbortRef.current[nodeId] = controller;

        try {
            const executionBatch = await getFlowExecutionBatch(executionId, controller.signal);
            if (!pageActiveRef.current || executionRequestAbortRef.current[nodeId] !== controller) return;
            delete executionRequestAbortRef.current[nodeId];

            const executionList = Array.isArray(executionBatch)
                ? executionBatch
                : Array.isArray(executionBatch?.items)
                    ? executionBatch.items
                    : Array.isArray(executionBatch?.executions)
                        ? executionBatch.executions
                        : [executionBatch].filter(Boolean);
            const executionDetail = executionList.find((item) => String(item?.id ?? '') === String(executionId)) || executionList[0] || {};
            const nodeExecution = executionDetail?.nodeExecutions?.find((item) => item?.nodeId === nodeId) || null;
            const nextStatus = nodeExecution?.status || executionDetail?.status || 'processing';

            if (!pageActiveRef.current) return;

            const beforeNodes = nodesRef.current;
            const beforeEdges = edgesRef.current;
            const targetNodeLabel = beforeNodes.find((node) => node.id === nodeId)?.data?.label || '节点';
            const updatedNodes = beforeNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            latestExecution: nodeExecution,
                            executionStatus: nextStatus,
                            activeExecutionId: nodeExecution?.executionId || executionId || node.data?.activeExecutionId || '',
                            selectedOutputId: resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                node.data?.selectedOutputId,
                            ),
                        },
                    }
                    : node
            ));
            const shouldSyncTargets = String(nextStatus).toLowerCase() === 'succeeded';
            const nextNodes = shouldSyncTargets
                ? syncConnectedTargetNodes(updatedNodes, beforeEdges, { sourceNodeId: nodeId })
                : updatedNodes;

            setNodes(nextNodes);

            if (shouldSyncTargets && JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes)) {
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: nextNodes,
                    afterEdges: beforeEdges,
                });
            }

            if (executionDetail?.projectVersion !== undefined && executionDetail?.projectVersion !== null) {
                syncProjectVersion(activeId, Number(executionDetail.projectVersion));
            }

            if (EXECUTION_STATUS_TERMINAL.has(String(nextStatus).toLowerCase())) {
                stopNodeExecutionPolling(nodeId);
                if (String(nextStatus).toLowerCase() === 'failed') {
                    message.warning(
                        nodeExecution?.errMsg
                        || executionDetail?.stopReason
                        || `${targetNodeLabel}执行失败`,
                    );
                }
                return;
            }

            if (pageActiveRef.current) {
                executionPollerRef.current[nodeId] = setTimeout(() => {
                    pollNodeExecution(nodeId, executionId);
                }, 5000);
            }
        } catch (error) {
            if (executionRequestAbortRef.current[nodeId] === controller) {
                delete executionRequestAbortRef.current[nodeId];
            }
            if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled' || !pageActiveRef.current) {
                return;
            }
            console.error(error);
            message.error(getErrorMessage(error, '查询执行进度失败'));
            stopNodeExecutionPolling(nodeId);
            updateNodeExecutionState(nodeId, {
                executionStatus: 'failed',
            });
        }
    }, [activeId, persistCanvasBatch, setNodes, stopNodeExecutionPolling, syncProjectVersion, updateNodeExecutionState]);

    // 执行单个功能节点
    const handleExecuteNode = useCallback(async (nodeId) => {
        if (!activeId) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        stopNodeExecutionPolling(nodeId);
        updateNodeExecutionState(nodeId, {
            executionStatus: 'processing',
            latestExecution: null,
            activeExecutionId: '',
            selectedOutputId: '',
        });

        try {
            const execution = await executeFlowNode({
                projectId: activeId,
                nodeId,
            });
            console.log('[FreeCanvas][handleExecuteNode] executeFlowNode response:', execution);
            const executionId = resolveExecutionIdentifier(execution);
            console.log('[FreeCanvas][handleExecuteNode] resolved executionId:', executionId);
            if (!executionId) {
                throw new Error('执行节点失败，未返回 executionId');
            }

            eventBus.emit('updatePower');

            const currentNodes = nodesRef.current;
            const currentEdges = edgesRef.current;
            const nextNodes = currentNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            latestExecution: null,
                            executionStatus: 'processing',
                            activeExecutionId: String(executionId),
                            selectedOutputId: '',
                        },
                    }
                    : node
            ));
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });
            console.log('[FreeCanvas][handleExecuteNode] starting pollNodeExecution, nodeId:', nodeId, 'executionId:', executionId);
            pollNodeExecution(nodeId, executionId);
        } catch (error) {
            console.error('[FreeCanvas][handleExecuteNode] error:', error);
            message.error(getErrorMessage(error, '执行节点失败'));
            const currentNodes = nodesRef.current;
            setNodes(currentNodes.map((node) => (
                node.id === nodeId
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            executionStatus: 'failed',
                            activeExecutionId: '',
                        },
                    }
                    : node
            )));
        }
    }, [activeId, persistCanvasBatch, pollNodeExecution, setNodes, stopNodeExecutionPolling, updateNodeExecutionState]);

    // 轮询分组执行状态，并同步组内节点结果
    const pollGroupExecution = useCallback(async (groupNodeId, executionId, groupFunctionNodeIds, groupChildNodeIds) => {
        if (!pageActiveRef.current) return;

        const pollerKey = `group:${groupNodeId}`;
        const groupChildNodeIdSet = new Set((groupChildNodeIds || []).map((item) => String(item)));
        const controller = new AbortController();
        executionRequestAbortRef.current[pollerKey]?.abort?.();
        executionRequestAbortRef.current[pollerKey] = controller;

        try {
            const executionBatch = await getFlowExecutionBatch(executionId, controller.signal);
            if (!pageActiveRef.current || executionRequestAbortRef.current[pollerKey] !== controller) return;
            delete executionRequestAbortRef.current[pollerKey];

            const executionList = Array.isArray(executionBatch)
                ? executionBatch
                : Array.isArray(executionBatch?.items)
                    ? executionBatch.items
                    : Array.isArray(executionBatch?.executions)
                        ? executionBatch.executions
                        : [executionBatch].filter(Boolean);
            const executionDetail = executionList.find((item) => String(item?.id ?? '') === String(executionId)) || executionList[0] || {};
            const executionStatusLower = String(executionDetail?.status || 'processing').toLowerCase();
            const isGroupExecutionTerminal = EXECUTION_STATUS_TERMINAL.has(executionStatusLower);
            const shouldContinuePolling = !isGroupExecutionTerminal && pageActiveRef.current;
            const nodeExecutionMap = new Map(
                (executionDetail?.nodeExecutions || []).map((item) => [String(item?.nodeId ?? ''), {
                    ...item,
                    executionId: item?.executionId || executionId,
                }]),
            );
            const targetNodeIdSet = new Set((groupFunctionNodeIds || []).map((item) => String(item)));

            const beforeNodes = nodesRef.current;
            const beforeEdges = edgesRef.current;
            let hasFailedNode = false;
            const failedLabels = [];
            const succeededSourceNodeIds = [];

            const updatedNodes = beforeNodes.map((node) => {
                if (String(node.id) === String(groupNodeId)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: true,
                            activeExecutionId: String(executionId),
                        },
                    };
                }

                if (!targetNodeIdSet.has(String(node.id))) return node;

                const nodeExecution = nodeExecutionMap.get(String(node.id)) || null;
                const nextStatus = nodeExecution?.status || 'idle';
                const isSucceeded = String(nextStatus).toLowerCase() === 'succeeded';
                const isFailed = String(nextStatus).toLowerCase() === 'failed';

                if (isSucceeded) {
                    succeededSourceNodeIds.push(String(node.id));
                }

                if (isFailed) {
                    hasFailedNode = true;
                    failedLabels.push(node.data?.label || '节点');
                }

                return {
                    ...node,
                    data: {
                        ...node.data,
                        latestExecution: nodeExecution || null,
                        executionStatus: nextStatus,
                        groupExecutionLocked: true,
                        activeExecutionId: nodeExecution?.executionId || executionId || node.data?.activeExecutionId || '',
                        selectedOutputId: nodeExecution
                            ? resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                '',
                            )
                            : '',
                    },
                };
            });

            const nextNodes = succeededSourceNodeIds.reduce(
                (acc, sourceNodeId) => syncConnectedTargetNodes(acc, beforeEdges, { sourceNodeId }),
                updatedNodes,
            );

            if (shouldContinuePolling) {
                executionPollerRef.current[pollerKey] = setTimeout(() => {
                    pollGroupExecution(groupNodeId, executionId, groupFunctionNodeIds, groupChildNodeIds);
                }, 5000);
            }

            setNodes(nextNodes);

            if (JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes)) {
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: nextNodes,
                    afterEdges: beforeEdges,
                });
            }

            if (executionDetail?.projectVersion !== undefined && executionDetail?.projectVersion !== null) {
                syncProjectVersion(activeId, Number(executionDetail.projectVersion));
            }

            const allTerminal = isGroupExecutionTerminal;

            if (allTerminal) {
                stopExecutionPolling(pollerKey);
                setNodes((prev) => prev.map((node) => (
                    String(node.id) === String(groupNodeId) || groupChildNodeIdSet.has(String(node.id))
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                groupExecutionLocked: false,
                                ...(String(node.id) === String(groupNodeId) ? { activeExecutionId: String(executionId) } : {}),
                            },
                        }
                        : node
                )));
                if (hasFailedNode) {
                    const failedLabelText = failedLabels.length > 0 ? failedLabels.join('、') : '某节点';
                    message.warning(
                        executionDetail?.nodeExecutions?.find((item) => String(item?.status).toLowerCase() === 'failed')?.errMsg
                        || executionDetail?.stopReason
                        || `${failedLabelText}执行失败`,
                    );
                }
                return;
            }

        } catch (error) {
            if (executionRequestAbortRef.current[pollerKey] === controller) {
                delete executionRequestAbortRef.current[pollerKey];
            }
            if (error?.code === 'ERR_CANCELED' || error?.message === 'canceled' || !pageActiveRef.current) {
                return;
            }
            console.error(error);
            message.error(getErrorMessage(error, '查询分组执行进度失败'));
            stopExecutionPolling(pollerKey);
            setNodes((prev) => prev.map((node) => (
                String(node.id) === String(groupNodeId) || groupChildNodeIdSet.has(String(node.id))
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: false,
                            ...(groupFunctionNodeIds.includes(node.id) ? { executionStatus: 'failed' } : {}),
                        },
                    }
                    : node
            )));
        }
    }, [activeId, persistCanvasBatch, setNodes, stopExecutionPolling, syncProjectVersion]);

    const resolvedProjectMember = useMemo(() => {
        if (forcePersonalMember) {
            return globalUser?.id || '';
        }
        if (member === 'team') {
            return isPmView ? (targetProjectMemberUserId || globalUser?.id || '') : null;
        }
        return member || globalUser?.id || '';
    }, [forcePersonalMember, globalUser?.id, isPmView, member, targetProjectMemberUserId]);

    // 加载项目列表，支持分页和关键词搜索
    const loadProjectList = useCallback(async ({
        pageIndex = 1,
        append = false,
        keyword = '',
    } = {}) => {
        if (!resolvedProjectMember && !(member === 'team' && !isPmView) && !forcePersonalMember) return null;

        const requestId = Date.now();
        projectRequestIdRef.current = requestId;
        projectListLoadingRef.current = true;
        setProjectListLoading(true);

        try {
            const pageData = await getFlowProjectPage({
                keyword,
                pageIndex,
                pageSize: PROJECT_PAGE_SIZE,
                member: resolvedProjectMember,
            });

            if (projectRequestIdRef.current !== requestId) return;

            projectPageIndexRef.current = pageIndex;
            projectKeywordRef.current = keyword;
            setProjects((prev) => mergeProjectCollections(prev, pageData.items, append));
            setProjectListHasMore(pageData.items.length >= PROJECT_PAGE_SIZE && pageData.pageIndex < pageData.totalPages);
            const currentActiveProject = pageData.items.find((item) => item.id === activeProjectRef.current?.id);
            if (currentActiveProject) {
                currentProjectVersionRef.current = Math.max(
                    getVersionNumber(currentProjectVersionRef.current),
                    getVersionNumber(currentActiveProject.version),
                );
            }
            if (!append) {
                setActiveId((prev) => (
                    prev && pageData.items.some((item) => item.id === prev)
                        ? prev
                        : (
                            !prev && !keyword && pageIndex === 1 && !skipAutoSelect
                                ? (
                                    pageData.items.find((item) => item.id === lastActiveProjectIdRef.current)?.id
                                    || pageData.items[0]?.id
                                    || ''
                                )
                                : prev
                        )
                ));
            }

            return pageData;
        } catch (error) {
            console.error(error);
            return null;
        } finally {
            if (projectRequestIdRef.current === requestId) {
                projectListLoadingRef.current = false;
                setProjectListLoading(false);
            }
        }
    }, [forcePersonalMember, member, resolvedProjectMember, skipAutoSelect]);

    useEffect(() => {
        const restoreLastActiveProject = async () => {
            const rememberedProjectId = lastActiveProjectIdRef.current;
            const rememberedPageIndex = Math.max(1, Number(lastActiveProjectPageIndexRef.current) || 1);
            const preservedKeyword = projectKeywordRef.current.trim();

            if (preservedKeyword) {
                return;
            }

            if (skipAutoSelect || !rememberedProjectId || rememberedPageIndex <= 1) {
                await loadProjectList({ pageIndex: 1, append: false, keyword: '' });
                return;
            }

            setActiveId(rememberedProjectId);

            let aggregatedItems = [];
            for (let pageIndex = 1; pageIndex <= rememberedPageIndex; pageIndex += 1) {
                const pageData = await loadProjectList({
                    pageIndex,
                    append: pageIndex > 1,
                    keyword: '',
                });

                if (!pageData) return;

                aggregatedItems = mergeProjectCollections(aggregatedItems, pageData.items, pageIndex > 1);

                if (aggregatedItems.some((item) => item.id === rememberedProjectId)) {
                    return;
                }

                if (pageData.items.length < PROJECT_PAGE_SIZE || pageData.pageIndex >= pageData.totalPages) {
                    break;
                }
            }

            setActiveId((currentActiveId) => (
                currentActiveId === rememberedProjectId
                    ? (aggregatedItems[0]?.id || '')
                    : currentActiveId
            ));
        };

        restoreLastActiveProject();
    }, [loadProjectList, skipAutoSelect]);

    useEffect(() => {
        if (!targetProjectId) {
            targetProjectHandledRef.current = '';
            return;
        }
        if (targetProjectHandledRef.current === targetProjectId) return;

        const locateProject = async () => {
            try {
                let keyword = targetProjectName;
                if (!keyword) {
                    const detail = await getFlowProjectDetail(targetProjectId);
                    keyword = detail?.flowSerial || '';
                }

                if (keyword) {
                    await loadProjectList({ pageIndex: 1, append: false, keyword });
                }

                setActiveId(targetProjectId);
                targetProjectHandledRef.current = targetProjectId;
            } catch (error) {
                console.error(error);
            }
        };

        locateProject();
    }, [targetProjectId, targetProjectName, loadProjectList]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        if (!hasHydratedLastActiveProjectRef.current) {
            hasHydratedLastActiveProjectRef.current = true;
            if (!activeId) return;
        }

        const activeProjectIndex = projects.findIndex((item) => item.id === activeId);
        const nextPageIndex = activeProjectIndex >= 0
            ? Math.floor(activeProjectIndex / PROJECT_PAGE_SIZE) + 1
            : Math.max(1, Number(lastActiveProjectPageIndexRef.current) || 1);

        lastActiveProjectIdRef.current = activeId || '';
        lastActiveProjectPageIndexRef.current = nextPageIndex;

        if (activeId) {
            window.localStorage.setItem(LAST_ACTIVE_PROJECT_STORAGE_KEY, JSON.stringify({
                projectId: activeId,
                pageIndex: nextPageIndex,
            }));
            return;
        }

        window.localStorage.removeItem(LAST_ACTIVE_PROJECT_STORAGE_KEY);
    }, [activeId, projects]);

    useEffect(() => {
        if (!activeId) {
            resetCanvasState();
            return undefined;
        }

        if (skipProjectDetailLoadRef.current === activeId) {
            skipProjectDetailLoadRef.current = '';
            return undefined;
        }

        const requestId = Date.now();
        projectDetailRequestIdRef.current = requestId;
        resetCanvasState();

        const loadProjectDetail = async () => {
            try {
                const projectDetail = await getFlowProjectDetail(activeId);
                if (projectDetailRequestIdRef.current !== requestId) return;

                setProjects((prev) => prev.map((project) => (
                    project.id === activeId
                        ? mergeProjectRecord(project, {
                            ...project,
                            name: projectDetail.name || project.name,
                            cover: projectDetail.cover,
                            studioId: projectDetail.studioId,
                            version: projectDetail.version,
                        })
                        : project
                )));
                currentProjectVersionRef.current = Math.max(
                    getVersionNumber(currentProjectVersionRef.current),
                    getVersionNumber(projectDetail.version),
                );
                syncProjectVersion(activeId, projectDetail.version);
                restoreProjectCanvas(projectDetail);
                (projectDetail.nodes || []).forEach((node) => {
                    const latestExecution = node?.latestExecution;
                    const renderNodeData = node?.renderData?.data || {};
                    const latestStatus = String(
                        latestExecution?.status
                        || renderNodeData?.executionStatus
                        || '',
                    ).toLowerCase();
                    const activeExecutionId = String(
                        latestExecution?.executionId
                        || renderNodeData?.activeExecutionId
                        || '',
                    );
                    if (
                        !node?.parentId
                        && (latestStatus === 'processing' || latestStatus === 'manual')
                        && activeExecutionId
                        && node?.id
                    ) {
                        pollNodeExecution(String(node.id), activeExecutionId);
                    }
                });
                const restoredNodes = Array.isArray(projectDetail?.nodes) ? projectDetail.nodes : [];
                const activeGroupExecutionMetaMap = buildActiveGroupExecutionMetaMap(restoredNodes);

                activeGroupExecutionMetaMap.forEach((meta) => {
                    if (!meta?.executionId || !meta?.groupFunctionNodeIds?.length) return;

                    pollGroupExecution(
                        String(meta.groupNodeId),
                        String(meta.executionId),
                        meta.groupFunctionNodeIds,
                        meta.groupChildNodeIds,
                    );
                });
            } catch (error) {
                console.error(error);
                message.error(getErrorMessage(error, '加载项目详情失败'));
            }
        };

        loadProjectDetail();

        return () => {
            if (projectDetailRequestIdRef.current === requestId) {
                projectDetailRequestIdRef.current = 0;
            }
        };
    }, [activeId, pollGroupExecution, pollNodeExecution, resetCanvasState, restoreProjectCanvas, syncProjectVersion]);

    useEffect(() => {
        if (!targetExecutionId) {
            targetExecutionHandledRef.current = '';
            return;
        }

        if (!targetProjectId || activeId !== targetProjectId) return;

        const handledKey = `${targetProjectId}:${targetExecutionId}`;
        if (targetExecutionHandledRef.current === handledKey) return;
        if (!nodes.length) return;

        if (focusNodeByExecutionId(targetExecutionId)) {
            targetExecutionHandledRef.current = handledKey;
        }
    }, [activeId, focusNodeByExecutionId, nodes.length, targetExecutionId, targetProjectId]);

    // 根据关键词刷新项目列表
    const handleProjectKeywordChange = useCallback((keyword) => {
        loadProjectList({
            pageIndex: 1,
            append: false,
            keyword: keyword.trim(),
        });
    }, [loadProjectList]);

    // 加载更多项目列表数据
    const handleLoadMoreProjects = useCallback(() => {
        if (!projectListHasMore || projectListLoading) return;

        loadProjectList({
            pageIndex: projectPageIndexRef.current + 1,
            append: true,
            keyword: projectKeywordRef.current,
        });
    }, [loadProjectList, projectListHasMore, projectListLoading]);

    // 切换当前项目，并清理引导参数
    const handleSelectProject = useCallback((projectId) => {
        stopAllExecutionPolling();
        setActiveId(projectId);
        const searchParams = new URLSearchParams(location.search);
        let changed = false;

        [
            SKIP_AUTO_SELECT_QUERY_KEY,
            CREATE_PROJECT_QUERY_KEY,
            TARGET_PROJECT_ID_QUERY_KEY,
            TARGET_PROJECT_NAME_QUERY_KEY,
            TARGET_EXECUTION_ID_QUERY_KEY,
        ].forEach((key) => {
            if (searchParams.has(key)) {
                searchParams.delete(key);
                changed = true;
            }
        });

        if (changed) {
            navigate({
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            }, { replace: true });
        }
    }, [location.pathname, location.search, navigate, stopAllExecutionPolling]);

    // 清理 URL 中的项目引导相关参数
    const clearGuideQueryParams = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        let changed = false;

        [
            SKIP_AUTO_SELECT_QUERY_KEY,
            CREATE_PROJECT_QUERY_KEY,
            TARGET_PROJECT_ID_QUERY_KEY,
            TARGET_PROJECT_NAME_QUERY_KEY,
            TARGET_EXECUTION_ID_QUERY_KEY,
        ].forEach((key) => {
            if (searchParams.has(key)) {
                searchParams.delete(key);
                changed = true;
            }
        });

        if (changed) {
            navigate({
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : '',
            }, { replace: true });
        }
    }, [location.pathname, location.search, navigate]);

    // 更新节点数据，并同步影响到的下游节点
    const handleNodeDataChange = useCallback((nodeId, patch) => {
        if (isNodeLocked(nodeId)) return;
        const now = Date.now();
        const pendingSwitch = pendingFunctionSwitchRef.current.get(nodeId);
        if (pendingSwitch && pendingSwitch.expiresAt <= now) {
            pendingFunctionSwitchRef.current.delete(nodeId);
        }
        const activePendingSwitch = pendingFunctionSwitchRef.current.get(nodeId);
        const patchFunctionKey = inferFunctionKeyFromNodePatch(patch);
        if (
            activePendingSwitch
            && patchFunctionKey
            && patchFunctionKey !== activePendingSwitch.functionKey
        ) {
            return;
        }
        if (
            activePendingSwitch
            && patchFunctionKey
            && patchFunctionKey === activePendingSwitch.functionKey
        ) {
            pendingFunctionSwitchRef.current.delete(nodeId);
        }
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const nextLinkedSlotSources = (
            patch?.panelState?.linkedSlotSources
            && typeof patch.panelState.linkedSlotSources === 'object'
            && !Array.isArray(patch.panelState.linkedSlotSources)
        )
            ? patch.panelState.linkedSlotSources
            : null;
        const keptIncomingEdgeIds = nextLinkedSlotSources
            ? new Set(
                Object.values(nextLinkedSlotSources)
                    .map((item) => String(item?.edgeId || ''))
                    .filter(Boolean),
            )
            : null;
        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? { ...node, data: { ...node.data, ...patch } }
                : node
        ));
        const nextEdges = keptIncomingEdgeIds
            ? beforeEdges.filter((edge) => (
                edge.target !== nodeId || keptIncomingEdgeIds.has(String(edge.id))
            ))
            : beforeEdges;
        const nextNodes = syncConnectedTargetNodes(updatedNodes, nextEdges, {
            sourceNodeId: nodeId,
            targetNodeId: keptIncomingEdgeIds ? nodeId : null,
        });
        if (
            JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
            && JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
        ) {
            return;
        }

        pushHistory();
        setNodes(nextNodes);
        if (nextEdges !== beforeEdges) {
            setEdges(nextEdges);
        }
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 切换功能节点配置时，清理不再适用的输入连线
    const handleSwitchFunctionNode = useCallback((nodeId, patch) => {
        if (isNodeLocked(nodeId)) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const nextFunctionKey = patch?.functionKey || '';
        if (nextFunctionKey) {
            pendingFunctionSwitchRef.current.set(nodeId, {
                functionKey: nextFunctionKey,
                expiresAt: Date.now() + 800,
            });
        }
        const nextLinkedSlotSources = (
            patch?.panelState?.linkedSlotSources
            && typeof patch.panelState.linkedSlotSources === 'object'
            && !Array.isArray(patch.panelState.linkedSlotSources)
        )
            ? patch.panelState.linkedSlotSources
            : {};
        const nextLinkedUploads = Array.isArray(patch?.panelState?.linkedUploads)
            ? patch.panelState.linkedUploads
            : [];
        const desiredEdgeSlotKeyMap = new Map();
        Object.entries(nextLinkedSlotSources).forEach(([slotKey, linkedState]) => {
            const edgeId = String(linkedState?.edgeId || '');
            if (!edgeId) return;
            desiredEdgeSlotKeyMap.set(edgeId, slotKey);
        });
        if (nextFunctionKey === 'templateReplace') {
            nextLinkedUploads.forEach((linkedItem) => {
                const edgeId = String(linkedItem?.edgeId || '');
                if (!edgeId) return;
                desiredEdgeSlotKeyMap.set(edgeId, 'reference');
            });
        }
        const keptIncomingEdgeIds = new Set(
            [
                ...Object.values(nextLinkedSlotSources),
                ...nextLinkedUploads,
            ]
                .map((item) => String(item?.edgeId || ''))
                .filter(Boolean),
        );

        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? { ...node, data: { ...node.data, ...patch } }
                : node
        ));
        const nextEdges = beforeEdges.reduce((acc, edge) => {
            if (edge.target !== nodeId) {
                acc.push(edge);
                return acc;
            }

            const edgeId = String(edge.id || '');
            if (!keptIncomingEdgeIds.has(edgeId)) {
                return acc;
            }

            const desiredSlotKey = desiredEdgeSlotKeyMap.get(edgeId) || '';
            const nextEdgeData = { ...(edge.data || {}) };

            if (desiredSlotKey) {
                nextEdgeData.inputSlotKey = desiredSlotKey;
            } else {
                delete nextEdgeData.inputSlotKey;
            }

            acc.push({
                ...edge,
                data: nextEdgeData,
            });
            return acc;
        }, []);
        const nextNodes = syncConnectedTargetNodes(updatedNodes, nextEdges, { targetNodeId: nodeId });

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 节点生成完成后，取消该节点的选中态
    const handleNodeGenerateComplete = useCallback((nodeId) => {
        if (!nodeId) return;

        setNodes((currentNodes) => currentNodes.map((node) => (
            node.id === nodeId
                ? { ...node, selected: false }
                : node
        )));
        setSelectedNodeIds((prev) => prev.filter((selectedId) => selectedId !== nodeId));
    }, [setNodes]);

    // 切换节点结果图，并同步下游引用
    const handleNodeResultSelect = useCallback(async (nodeId, selectedOutputId) => {
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => node.id === nodeId);
        const isManualSelectionPending = String(targetNode?.data?.latestExecution?.status || targetNode?.data?.executionStatus || '').toLowerCase() === 'manual';
        const updatedNodes = beforeNodes.map((node) => (
            node.id === nodeId
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        latestExecution: node.data?.latestExecution
                            ? {
                                ...node.data.latestExecution,
                                outputs: markExecutionPrimaryOutput(
                                    node.data.latestExecution.outputs,
                                    selectedOutputId,
                                ),
                            }
                            : node.data?.latestExecution,
                        selectedOutputId,
                    },
                }
                : node
        ));
        const nextNodes = syncConnectedTargetNodes(updatedNodes, beforeEdges, { sourceNodeId: nodeId });
        const hasLocalChanged = JSON.stringify(nextNodes) !== JSON.stringify(beforeNodes);

        if (hasLocalChanged) {
            pushHistory();
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
            });
        }

        const latestExecution = targetNode?.data?.latestExecution || {};
        const nodeExecutionId = latestExecution?.nodeExecutionId || latestExecution?.id || '';
        if (!selectedOutputId) {
            message.warning('请选择结果图');
            return;
        }
        if (!nodeExecutionId) {
            message.error('当前节点缺少执行记录，无法提交选图');
            return;
        }

        try {
            if (isManualSelectionPending) {
                const executionId = latestExecution?.executionId || '';
                if (!executionId) {
                    message.error('当前节点缺少执行批次，无法恢复执行');
                    return;
                }
                const parentGroupId = targetNode?.parentId ? String(targetNode.parentId) : '';
                const currentNodes = nodesRef.current;
                const groupChildNodes = parentGroupId
                    ? currentNodes.filter((node) => String(node.parentId || '') === parentGroupId)
                    : [];
                const groupFunctionNodeIds = groupChildNodes
                    .filter((node) => node.type === 'functionNode')
                    .map((node) => String(node.id));
                const groupChildNodeIds = groupChildNodes.map((node) => String(node.id));

                setNodes((prev) => prev.map((node) => (
                    node.id === nodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                executionStatus: 'processing',
                                activeExecutionId: executionId,
                                latestExecution: node.data?.latestExecution
                                    ? {
                                        ...node.data.latestExecution,
                                        executionId,
                                        status: 'processing',
                                    }
                                    : node.data?.latestExecution,
                            },
                        }
                        : node
                )));

                await resumeFlowExecution({
                    executionId,
                    nodeId,
                    outputId: selectedOutputId,
                });

                const processingNodes = beforeNodes.map((node) => (
                    node.id === nodeId
                        ? {
                            ...node,
                            data: {
                                ...node.data,
                                executionStatus: 'processing',
                                activeExecutionId: executionId,
                                latestExecution: node.data?.latestExecution
                                    ? {
                                        ...node.data.latestExecution,
                                        executionId,
                                        status: 'processing',
                                    }
                                    : node.data?.latestExecution,
                            },
                        }
                        : node
                ));
                persistCanvasBatch({
                    beforeNodes,
                    beforeEdges,
                    afterNodes: processingNodes,
                    afterEdges: beforeEdges,
                });

                if (parentGroupId && groupFunctionNodeIds.length > 0) {
                    pollGroupExecution(parentGroupId, String(executionId), groupFunctionNodeIds, groupChildNodeIds);
                }
                return;
            }

            await selectFlowExecutionPrimaryOutput({
                nodeExecutionId,
                outputId: selectedOutputId,
            }); 
        } catch (error) {
            console.error(error);
            if (isManualSelectionPending) {
                setNodes(beforeNodes);
                if (hasLocalChanged) {
                    persistCanvasBatch({
                        beforeNodes: nextNodes,
                        beforeEdges,
                        afterNodes: beforeNodes,
                        afterEdges: beforeEdges,
                    });
                }
            }
        }
    }, [persistCanvasBatch, pollGroupExecution, pushHistory, setNodes]);

    // 注册画布节点类型
    const nodeTypes = useMemo(() => ({
        imageNode: ImageNode,
        functionNode: FunctionNode,
        groupNode: GroupNode,
    }), []);

    // 注册画布连线类型
    const edgeTypes = useMemo(() => ({
        flowEdge: FlowEdge,
    }), []);

    // 设置默认连线配置
    const defaultEdgeOptions = useMemo(() => ({
        type: 'flowEdge',
    }), []);

    // 创建节点连线，并自动分配输入槽位
    const handleConnect = useCallback((params) => {
        if (isNodeLocked(params.source) || isNodeLocked(params.target)) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const sourceNode = beforeNodes.find((node) => node.id === params.source);
        const targetNode = nodesRef.current.find((node) => node.id === params.target);
        if (
            params.source
            && params.target
            && String(params.source) === String(params.target)
            && sourceNode?.type === 'functionNode'
            && targetNode?.type === 'functionNode'
        ) {
            return;
        }
        if (wouldCreateCycle(beforeEdges, params.source, params.target)) {
            message.warning('不能连接成环');
            return;
        }
        const slotKeys = targetNode?.type === 'functionNode'
            ? getFunctionNodeEdgeSlotKeys(targetNode.data?.functionKey, targetNode)
            : [];
        const nextSlotKey = slotKeys.length > 0
            ? getAvailableEdgeSlotKeyForTarget({
                edges: beforeEdges,
                targetNode,
                slotKeys,
            })
            : null;
        if (targetNode?.type === 'functionNode') {
            const inputLimit = targetNode.data?.functionKey === 'templateReplace'
                ? FUNCTION_NODE_INPUT_LIMITS.templateReplace
                : (
                    slotKeys.length > 0
                        ? slotKeys.length
                        : getFunctionNodeInputLimit(targetNode.data?.functionKey)
                );
            const incomingCount = beforeEdges.filter((edge) => edge.target === params.target).length;
            const occupiedCount = isMultiImageFunctionKey(targetNode.data?.functionKey)
                ? getCreativeImgOccupiedCount(targetNode, beforeEdges)
                : incomingCount;

            if ((slotKeys.length > 0 && !nextSlotKey) || occupiedCount >= inputLimit) {
                message.warning('没有可以串联的位置');
                return;
            }
        }

        pushHistory();
        const edgeId = createEdgeId();
        const addedEdges = addEdge({ ...params, id: edgeId, type: 'flowEdge' }, beforeEdges);
        const nextEdges = slotKeys.length === 0
            ? addedEdges
            : (
                targetNode?.data?.functionKey === 'templateReplace'
                    ? addedEdges.map((edge) => (
                        edge.id === edgeId
                            ? {
                                ...edge,
                                data: {
                                    ...edge.data,
                                    inputSlotKey: nextSlotKey,
                                },
                            }
                            : edge
                    ))
                    : reconcileEdgeSlotAssignment({
                        edges: addedEdges,
                        edgeId,
                        targetId: params.target,
                        slotKeys,
                        preferredSlotKey: nextSlotKey,
                    })
            );
        const nextNodes = syncConnectedTargetNodes(beforeNodes, nextEdges, {
            sourceNodeId: params.source,
            targetNodeId: params.target,
        });

        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);
 
    // 新建空白项目并切换到该项目
    const handleCreate = useCallback(async () => {
        try {
            const nextProject = await createFlowProject();

            setProjects((prev) => [nextProject, ...prev.filter((item) => item.id !== nextProject.id)]);
            skipProjectDetailLoadRef.current = nextProject.id;
            setActiveId(nextProject.id);
            resetCanvasState();
            setSidebarCollapsed(false);
            clearGuideQueryParams();
            // 新建完成后主动刷新一次列表，确保侧边栏数据与服务端同步
            loadProjectList({ pageIndex: 1, append: false, keyword: '' });
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '创建项目失败'));
        }
    }, [clearGuideQueryParams, loadProjectList, resetCanvasState]);

    useEffect(() => {
        if (isPmView) return;
        if (typeof window === 'undefined') return;
        if (createProjectRequested) return;

        const currentOrgKey = globalOrgInfo?.isPersonal
            ? 'personal'
            : `org:${globalOrgInfo?.current?.id || ''}`;
        if (!currentOrgKey || currentOrgKey === 'org:') return;

        const previousOrgKey = window.sessionStorage.getItem(LAST_ACTIVE_ORG_KEY_STORAGE_KEY) || '';
        window.sessionStorage.setItem(LAST_ACTIVE_ORG_KEY_STORAGE_KEY, currentOrgKey);

        if (!previousOrgKey || previousOrgKey === currentOrgKey) {
            return;
        }

        clearGuideQueryParams();
        handleCreate();
    }, [clearGuideQueryParams, createProjectRequested, globalOrgInfo, globalOrgInfo?.current?.id, globalOrgInfo?.isPersonal, handleCreate, isPmView]);

    useEffect(() => {
        if (!createProjectRequested || isPmView) {
            createProjectHandledRef.current = false;
            return;
        }
        if (createProjectHandledRef.current) return;

        createProjectHandledRef.current = true;
        handleCreate().finally(() => {
            clearGuideQueryParams();
        });
    }, [clearGuideQueryParams, createProjectRequested, handleCreate, isPmView]);

    useEffect(() => {
        if (isPmView || !activeId || nodes.length > 0 || edges.length > 0) return;
        if (creativeBootstrapHandledRef.current) return;
        if (!canvasRef.current || !reactFlowRef.current) return;

        const rawPayload = window.sessionStorage.getItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);
        if (!rawPayload) return;

        let payload = null;
        try {
            payload = normalizeCreativeBootstrapPayload(JSON.parse(rawPayload));
        } catch (error) {
            payload = null;
        }

        if (!payload) {
            window.sessionStorage.removeItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);
            return;
        }

        creativeBootstrapHandledRef.current = true;

        const bootstrapCreativeNode = async () => {
            try {
                const result = await handleAddNode('creativeImg2Img', {
                    autoOpenFunctionPanel: true,
                    dataPatch: buildCreativeBootstrapNodePatch(payload),
                });

                window.sessionStorage.removeItem(FREE_CANVAS_CREATIVE_BOOTSTRAP_STORAGE_KEY);

                if (payload.shouldAutoGenerate && result?.nodeId) {
                    await handleExecuteNode(String(result.nodeId));
                }
            } catch (error) {
                console.error(error);
                creativeBootstrapHandledRef.current = false;
            }
        };

        bootstrapCreativeNode();
    }, [activeId, edges.length, handleAddNode, handleExecuteNode, isPmView, nodes.length]);

    // 修改当前项目名称
    const handleNameChange = useCallback(async (newName) => {
        const currentProject = projects.find((project) => project.id === activeId);
        if (!currentProject) return false;
        const normalizedName = String(newName || '').trim().slice(0, 20);
        if (!normalizedName) return false;

        try {
            const nextProject = await saveFlowProject({
                id: currentProject.id,
                projectName: normalizedName,
                cover: currentProject.cover,
                successMessage: '重命名成功',
                studioId: currentProject.studioId,
            });

            setProjects((prev) => prev.map((project) => (
                project.id === currentProject.id ? { ...project, ...nextProject } : project
            )));
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [activeId, projects]);

    // 修改指定项目名称
    const handleRenameProject = useCallback(async (projectId, newName) => {
        const currentProject = projects.find((project) => project.id === projectId);
        if (!currentProject) return false;

        try {
            const nextProject = await saveFlowProject({
                id: currentProject.id,
                projectName: newName,
                cover: currentProject.cover,
                successMessage: '重命名成功',
                studioId: currentProject.studioId,
            });

            setProjects((prev) => prev.map((project) => (
                project.id === projectId ? { ...project, ...nextProject } : project
            )));
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [projects]);

    const handleDuplicateProject = useCallback(async (projectId) => {
        const currentProject = projects.find((project) => project.id === projectId);
        if (!currentProject) return false;

        try {
            const nextProject = mergeProjectRecord(
                {},
                await duplicateFlowProject({
                    projectId,
                    projectName: currentProject.name || '未命名项目',
                    cover: currentProject.cover || '',
                    successMessage: '副本已创建',
                }),
            );

            setProjects((prev) => [nextProject, ...prev.filter((project) => project.id !== nextProject.id)]);
            setActiveId(nextProject.id);
            setSidebarCollapsed(false);
            await loadProjectList({
                pageIndex: 1,
                append: false,
                keyword: projectKeywordRef.current || '',
            });
            setActiveId(nextProject.id);
            return true;
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '创建副本失败'));
            return false;
        }
    }, [loadProjectList, projects]);

    // 删除指定项目，并处理当前激活项目回退
    const handlePinProject = useCallback(async (projectId) => {
        if (!projectId) return false;

        try {
            await pinFlowProject(projectId);
            await loadProjectList({
                pageIndex: 1,
                append: false,
                keyword: projectKeywordRef.current || '',
            });
            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [loadProjectList]);

    const handleSortProject = useCallback(async ({
        id,
        targetProjectId,
        position,
    } = {}) => {
        if (!id || !targetProjectId || !position || id === targetProjectId) return false;

        const projectIndexMap = new Map(projects.map((item, index) => [item.id, index]));
        const oldIdx = projectIndexMap.get(id);
        const targetIdx = projectIndexMap.get(targetProjectId);

        if (oldIdx === undefined || targetIdx === undefined) return false;

        const newIdx = position === 'before'
            ? (oldIdx < targetIdx ? targetIdx - 1 : targetIdx)
            : (oldIdx < targetIdx ? targetIdx : targetIdx + 1);

        if (oldIdx === newIdx) return false;

        const previousProjects = projects;
        const reorderedProjects = reorderProjectCollections(previousProjects, oldIdx, newIdx);
        if (reorderedProjects === previousProjects) return false;

        const afterProjectId = reorderedProjects[newIdx - 1]?.id || '';
        setProjects(reorderedProjects);

        try {
            await sortFlowProject({
                id,
                afterProjectId,
            });
            return true;
        } catch (error) {
            console.error(error);
            setProjects(previousProjects);
            return false;
        }
    }, [projects]);

    const handleDeleteProject = useCallback(async (projectId) => {
        try {
            await deleteFlowProject(projectId);

            setProjects((prev) => {
                const nextProjects = prev.filter((project) => project.id !== projectId);

                setActiveId((currentActiveId) => {
                    if (currentActiveId !== projectId) return currentActiveId;
                    return nextProjects[0]?.id || '';
                });

                return nextProjects;
            });

            if (activeId === projectId) {
                resetCanvasState();
            }

            return true;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [activeId, resetCanvasState]);

    // 将当前画布内容调整到合适视野范围
    const handleFitCanvas = useCallback(() => {
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas || nodes.length === 0) return;

        const nodeMap = new Map(nodes.map((node) => [node.id, node]));
        const contentBounds = nodes.reduce((acc, node) => {
            const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
            const { width, height } = getNodeSize(node);

            return {
                minX: Math.min(acc.minX, absolutePosition.x),
                minY: Math.min(acc.minY, absolutePosition.y),
                maxX: Math.max(acc.maxX, absolutePosition.x + width),
                maxY: Math.max(acc.maxY, absolutePosition.y + height),
            };
        }, {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });

        const canvasBounds = canvas.getBoundingClientRect();
        const screenTopLeft = reactFlow.flowToScreenPosition({
            x: contentBounds.minX,
            y: contentBounds.minY,
        });
        const screenBottomRight = reactFlow.flowToScreenPosition({
            x: contentBounds.maxX,
            y: contentBounds.maxY,
        });
        const viewportPadding = 12;
        const fullyVisible = (
            screenTopLeft.x >= canvasBounds.left + viewportPadding
            && screenTopLeft.y >= canvasBounds.top + viewportPadding
            && screenBottomRight.x <= canvasBounds.right - viewportPadding
            && screenBottomRight.y <= canvasBounds.bottom - viewportPadding
        );

        if (fullyVisible) {
            setZoom(Math.round(reactFlow.getZoom() * 100));
            return;
        }

        reactFlow.fitView({ duration: 200, padding: 0.24 });
        setTimeout(() => {
            setZoom(Math.round(reactFlow.getZoom() * 100));
        }, 220);
    }, [nodes]);

    // 确保当前画布操作有可用项目承载
    const ensureCanvasProject = useCallback(async () => {
        const currentProjectId = activeId || activeProjectRef.current?.id || '';

        if (currentProjectId) {
            return {
                projectId: currentProjectId,
                created: false,
            };
        }

        const nextProject = await createFlowProject();
        setProjects((prev) => [nextProject, ...prev.filter((item) => item.id !== nextProject.id)]);
        skipProjectDetailLoadRef.current = nextProject.id;
        setActiveId(nextProject.id);
        await loadProjectList({
            pageIndex: 1,
            append: false,
            keyword: projectKeywordRef.current || '',
        });

        return {
            projectId: nextProject.id,
            created: true,
        };
    }, [activeId, loadProjectList]);

    // 批量创建图片节点
    const createImageNodes = useCallback(async (sources, options = {}) => {
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas || sources.length === 0) return;
        const projectContext = await ensureCanvasProject();
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        pushHistory();

        const bounds = canvas.getBoundingClientRect();
        const centerPosition = options.flowPosition || reactFlow.screenToFlowPosition({
            x: bounds.left + bounds.width / 2,
            y: bounds.top + bounds.height / 2,
        });
        const totalWidth = (sources.length * IMAGE_NODE_WIDTH) + ((sources.length - 1) * IMAGE_NODE_GAP);
        const startX = centerPosition.x - (totalWidth / 2);
        const baseY = centerPosition.y - (IMAGE_NODE_HEIGHT / 2);

        const appendedNodes = sources.map((src, index) => ({
            id: createNodeId('image-node'),
            type: 'imageNode',
            position: {
                x: startX + (index * (IMAGE_NODE_WIDTH + IMAGE_NODE_GAP)),
                y: baseY,
            },
            data: {
                label: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current + index}`,
                imageSequence: nextImageNodeNumberRef.current + index,
                imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current + index}`,
                src,
                inputParams: {
                    output: src,
                },
            },
        }));
        const nextNodes = beforeNodes.concat(appendedNodes);

        setNodes(nextNodes);
        setNextImageNodeNumber((prev) => prev + sources.length);
        await persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: beforeEdges,
            projectId: projectContext.projectId,
        });
    }, [ensureCanvasProject, persistCanvasBatch, pushHistory, setNodes]);

    // 将单张历史图片回填到画布
    const handleSendHistoryImage = useCallback((url) => {
        if (!url) return;
        createImageNodes([url]);
    }, [createImageNodes]);

    const handleSelectCanvasExample = useCallback(async (item) => {
        if (canvasExampleCreatingRef.current) return;

        const imageUrls = Array.isArray(item?.sampleImage)
            ? item.sampleImage
                .filter(Boolean)
                .map((name) => `${EXAMPLE_CARD_SAMPLE_IMAGE_BASE_URL}${name}`)
            : [];

        try {
            canvasExampleCreatingRef.current = true;
            await handleAddNode('creativeImg2Img', {
                autoOpenFunctionPanel: true,
                dataPatch: buildCreativeBootstrapNodePatch({
                    imageUrls,
                    prompt: item?.text || '',
                    aspectRatio: '3:4',
                    imageSize: '4K',
                    num: 4,
                    shouldAutoGenerate: false,
                }),
            });
        } catch (error) {
            console.error(error);
        } finally {
            window.setTimeout(() => {
                canvasExampleCreatingRef.current = false;
            }, 0);
        }
    }, [handleAddNode]);

    // 将多张历史图片批量回填到画布
    const handleBatchSendHistoryImages = useCallback((urls) => {
        const validUrls = (urls || []).filter(Boolean);
        if (!validUrls.length) return;
        return createImageNodes(validUrls);
    }, [createImageNodes]);

    const handleSelectMaterialLibraryImage = useCallback((value) => {
        const urls = Array.isArray(value) ? value.filter(Boolean) : [value].filter(Boolean);
        if (!urls.length) return;
        createImageNodes(urls);
    }, [createImageNodes]);

    const handleOpenSaveTemplateModal = useCallback((groupNodeId = '') => {
        setSaveTemplateGroupId(groupNodeId);
        setSaveTemplateName('');
        setPublishTemplateSync(false);
        setSaveTemplateModalOpen(true);
    }, [globalUser?.id, member]);

    const handleCloseSaveTemplateModal = useCallback(() => {
        if (saveTemplateSubmitting) return;
        setSaveTemplateModalOpen(false);
        setSaveTemplateGroupId('');
    }, [saveTemplateSubmitting]);

    const createGroupTemplateCover = useCallback(async (groupNodeId) => {
        const currentNodes = nodesRef.current;
        const groupNodes = currentNodes.filter((node) => node.parentId === groupNodeId);
        const groupNodeIds = new Set(groupNodes.map((node) => String(node.id)));
        const groupEdges = edgesRef.current.filter((edge) => (
            groupNodeIds.has(String(edge.source)) && groupNodeIds.has(String(edge.target))
        ));
        const nodeMap = new Map(currentNodes.map((node) => [String(node.id), node]));

        if (!groupNodes.length) {
            throw new Error('未找到可保存的分组内容');
        }
        const coverPayload = buildTemplateCoverPayload({
            groupNodeId: String(groupNodeId),
            nodes: groupNodes,
            edges: groupEdges,
            nodeMap,
        });
        const file = await renderTemplateCoverToFile(coverPayload, `group-template-${groupNodeId}.png`);
        const coverUrl = await uploadFlowImage(file);

        if (!coverUrl) {
            throw new Error('模板封面上传失败');
        }

        return coverUrl;
    }, []);

    const handleSaveTemplate = useCallback(async () => {
        const projectId = activeId;
        const groupNodeId = saveTemplateGroupId;
        const templateName = (saveTemplateName || '').trim();

        if (!projectId) {
            message.warning('缺少项目 ID，无法保存模板');
            return;
        }
        if (!groupNodeId) {
            message.warning('缺少组合节点 ID，无法保存模板');
            return;
        }
        if (!templateName) {
            message.warning('请填写模板名称');
            return;
        }

        setSaveTemplateSubmitting(true);
        try {
            const cover = await createGroupTemplateCover(groupNodeId);
            await saveFlowTemplate({
                projectId,
                groupNodeId,
                templateName,
                cover,
                toPublic: publishTemplateSync,
            });
            setSaveTemplateModalOpen(false);
            setSaveTemplateGroupId('');
        } catch (error) {
            console.error(error);
        } finally {
            setSaveTemplateSubmitting(false);
        }
    }, [activeId, createGroupTemplateCover, publishTemplateSync, saveTemplateGroupId, saveTemplateName]);

    // 上传本地图片后，生成对应的画布节点
    const uploadAndCreateImageNodes = useCallback(async (files, options = {}) => {
        if (!files.length) return;

        const sources = await Promise.all(files.map(async (file) => {
            try {
                return await uploadFlowImage(file);
            } catch (error) {
                return '';
            }
        }));
        const uploadedSources = sources.filter(Boolean);
        if (uploadedSources.length > 0) {
            await createImageNodes(uploadedSources, options);
        }
    }, [createImageNodes]);

    // 处理本地文件选择上传
    const handleUploadFiles = useCallback(async (event) => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) return;

        await uploadAndCreateImageNodes(files);
    }, [uploadAndCreateImageNodes]);

    // 处理文件拖入画布时的交互反馈
    const handleCanvasDragOver = useCallback((event) => {
        if (isPmView) return;
        const hasFile = Array.from(event.dataTransfer?.types || []).includes('Files');
        if (!hasFile) return;

        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
    }, [isPmView]);

    // 处理文件拖拽到画布后的上传与落点创建
    const handleCanvasDrop = useCallback(async (event) => {
        if (isPmView) return;
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas) return;

        const files = Array.from(event.dataTransfer?.files || []);
        if (files.length === 0) return;

        event.preventDefault();
        event.stopPropagation();

        const flowPosition = reactFlow.screenToFlowPosition({
            x: event.clientX,
            y: event.clientY,
        });

        await uploadAndCreateImageNodes(files, { flowPosition });
    }, [isPmView, uploadAndCreateImageNodes]);

    // 处理画布工具栏的快捷动作
    const handleAction = useCallback((key) => {
        if (isPmView) return;
        if (key === 'upload') {
            uploadInputRef.current?.click();
            return;
        }

        if (key === 'record') {
            setHistoryModalOpen(true);
            return;
        }

        if (key === 'folder') {
            setMaterialLibraryOpen(true);
            return;
        }

        if (key === 'template') {
            setTemplateLibraryOpen(true);
        }
    }, [isPmView]);

    // 手动向画布中新增节点
    const handleAddNode = useCallback(async (nodeType, options = {}) => {
        if (isPmView) return;
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        if (!reactFlow || !canvas) return;
        const {
            autoOpenFunctionPanel = false,
            dataPatch = null,
        } = options;
        const projectContext = await ensureCanvasProject();
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        pushHistory();

        const bounds = canvas.getBoundingClientRect();
        const centerPosition = reactFlow.screenToFlowPosition({
            x: bounds.left + bounds.width / 2,
            y: bounds.top + bounds.height / 2,
        });
        const addIndex = manualAddCountRef.current;
        const offsetIndex = addIndex % MANUAL_ADD_OFFSET_WRAP;
        const rowIndex = Math.floor(addIndex / MANUAL_ADD_OFFSET_WRAP);
        const manualOffset = {
            x: offsetIndex * MANUAL_ADD_OFFSET_STEP,
            y: rowIndex * MANUAL_ADD_OFFSET_STEP,
        };
        manualAddCountRef.current += 1;

        if (nodeType === 'image') {
            const nodeId = createNodeId('image-node');
            const nextNodes = beforeNodes.concat({
                id: nodeId,
                type: 'imageNode',
                position: {
                    x: centerPosition.x - IMAGE_NODE_WIDTH / 2 + manualOffset.x,
                    y: centerPosition.y - IMAGE_NODE_HEIGHT / 2 + manualOffset.y,
                },
                data: {
                    label: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    imageSequence: nextImageNodeNumberRef.current,
                    imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    src: '',
                    inputParams: {
                        output: '',
                    },
                },
            });
            setNodes(nextNodes);
            nodesRef.current = nextNodes;
            setNextImageNodeNumber((prev) => prev + 1);
            await persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
                projectId: projectContext.projectId,
            });
            return {
                nodeId,
                projectId: projectContext.projectId,
            };
        } else if (FUNCTION_LABELS[nodeType]) {
            const nodeId = createNodeId('func');
            const nextNodes = (autoOpenFunctionPanel
                ? beforeNodes.map((node) => ({
                    ...node,
                    selected: false,
                }))
                : beforeNodes
            ).concat({
                id: nodeId,
                type: 'functionNode',
                selected: autoOpenFunctionPanel,
                position: {
                    x: centerPosition.x - IMAGE_NODE_WIDTH / 2 + manualOffset.x,
                    y: centerPosition.y - IMAGE_NODE_HEIGHT / 2 + manualOffset.y,
                },
                data: {
                    label: FUNCTION_LABELS[nodeType],
                    functionKey: nodeType,
                    imageSequence: nextImageNodeNumberRef.current,
                    imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
                    ...(dataPatch && typeof dataPatch === 'object' ? dataPatch : {}),
                },
            });
            setNodes(nextNodes);
            nodesRef.current = nextNodes;
            if (autoOpenFunctionPanel) {
                setSelectedNodeIds([nodeId]);
            }
            setNextImageNodeNumber((prev) => prev + 1);
            await persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: nextNodes,
                afterEdges: beforeEdges,
                projectId: projectContext.projectId,
            });
            return {
                nodeId,
                projectId: projectContext.projectId,
            };
        }
    }, [ensureCanvasProject, isPmView, persistCanvasBatch, pushHistory, setNodes]);

    // 从现有节点快速追加下游功能节点
    const handleQuickAddNode = useCallback((sourceNodeId, nodeType) => {
        if (isPmView) return;
        if (isNodeLocked(sourceNodeId)) return;
        if (!FUNCTION_LABELS[nodeType]) return;

        const sourceNode = nodesRef.current.find((node) => node.id === sourceNodeId);
        if (!sourceNode) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;

        pushHistory();

        const nodeId = createNodeId('func');
        const createdNodes = beforeNodes.map((node) => ({ ...node, selected: false })).concat({
            id: nodeId,
            type: 'functionNode',
            selected: true,
            position: {
                x: sourceNode.position.x + CONNECTED_NODE_OFFSET_X,
                y: sourceNode.position.y + CONNECTED_NODE_OFFSET_Y,
            },
            data: {
                label: FUNCTION_LABELS[nodeType],
                functionKey: nodeType,
                imageSequence: nextImageNodeNumberRef.current,
                imageSequenceLabel: `${IMAGE_NODE_PREFIX}${nextImageNodeNumberRef.current}`,
            },
        });
        const nextEdges = beforeEdges.map((edge) => ({ ...edge, selected: false })).concat({
            id: createEdgeId(),
            source: sourceNodeId,
            target: nodeId,
            type: 'flowEdge',
            data: (() => {
                const slotKeys = getFunctionNodeEdgeSlotKeys(nodeType);
                return slotKeys.length > 0
                    ? { inputSlotKey: slotKeys[0] }
                    : undefined;
            })(),
        });
        const nextNodes = syncConnectedTargetNodes(createdNodes, nextEdges, {
            sourceNodeId,
            targetNodeId: nodeId,
        });

        setNodes(nextNodes);
        setEdges(nextEdges);
        setSelectedNodeIds([nodeId]);
        setNextImageNodeNumber((prev) => prev + 1);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, isPmView, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 生成复制/粘贴使用的节点与连线快照
    const buildClipboardPayloadFromState = useCallback((nodeIds, sourceNodes, sourceEdges) => {
        const targetNodeIds = getDescendantNodeIds(nodeIds, sourceNodes);
        const copiedNodes = sortNodesByParentDepth(
            sourceNodes.filter((node) => targetNodeIds.has(node.id)),
        );
        const copiedEdges = sourceEdges.filter((edge) => (
            targetNodeIds.has(edge.source) && targetNodeIds.has(edge.target)
        ));

        return cloneValue({
            nodes: copiedNodes,
            edges: copiedEdges,
        });
    }, []);

    // 生成复制/粘贴使用的节点与连线快照
    const buildClipboardPayload = useCallback((nodeIds) => (
        buildClipboardPayloadFromState(nodeIds, nodesRef.current, edgesRef.current)
    ), [buildClipboardPayloadFromState]);

    // 将剪贴板中的节点克隆到当前画布
    const cloneClipboardToCanvas = useCallback((clipboard, options = {}) => {
        if (!clipboard?.nodes?.length) return false;
        const beforeNodes = options.baseNodes || nodesRef.current;
        const beforeEdges = options.baseEdges || edgesRef.current;
        const containsGroupNode = clipboard.nodes.some((node) => node?.type === 'groupNode');

        if (!options.skipHistory) {
            pushHistory(options.historySnapshot || undefined);
        }

        const nodeIdMap = new Map();
        const clipboardNodeMap = new Map(clipboard.nodes.map((node) => [node.id, node]));
        const absolutePositions = clipboard.nodes.map((node) => getNodeAbsolutePosition(node, clipboardNodeMap));
        const minX = Math.min(...absolutePositions.map((position) => position.x));
        const minY = Math.min(...absolutePositions.map((position) => position.y));

        let deltaX = 0;
        let deltaY = 0;

        if (options.positionDelta) {
            deltaX = Number(options.positionDelta.x ?? 0);
            deltaY = Number(options.positionDelta.y ?? 0);
            pasteCountRef.current = 0;
        } else if (options.flowPosition) {
            deltaX = options.flowPosition.x - minX;
            deltaY = options.flowPosition.y - minY;
            pasteCountRef.current = 0;
        } else {
            pasteCountRef.current += 1;
            deltaX = PASTE_OFFSET_STEP * pasteCountRef.current;
            deltaY = PASTE_OFFSET_STEP * pasteCountRef.current;
        }

        clipboard.nodes.forEach((node) => {
            nodeIdMap.set(node.id, createDuplicatedNodeId(node));
        });

        const edgeIdMap = new Map();
        clipboard.edges.forEach((edge) => {
            edgeIdMap.set(edge.id, createEdgeId());
        });

        let nextImageSequence = nextImageNodeNumberRef.current;
        const nextNodes = clipboard.nodes.map((node) => {
            const nextNode = cloneValue(node);
            const absolutePosition = getNodeAbsolutePosition(node, clipboardNodeMap);
            const parentIsCopied = !!(nextNode.parentId && nodeIdMap.has(nextNode.parentId));

            nextNode.id = nodeIdMap.get(node.id);
            nextNode.selected = typeof options.selectDuplicatedNodes === 'boolean'
                ? options.selectDuplicatedNodes
                : (
                    containsGroupNode
                        ? nextNode.type === 'groupNode'
                        : true
                );
            nextNode.data = normalizeDuplicatedNodeData(node.data, { nodeIdMap, edgeIdMap });
            if (nextNode.type === 'groupNode') {
                nextNode.data = {
                    ...(nextNode.data || {}),
                    label: appendDuplicateSuffix(node.data?.label || node.data?.nodeName || '', '组'),
                };
            } else if (nextNode.type === 'imageNode' || nextNode.type === 'functionNode') {
                const duplicatedTitle = appendDuplicateSuffix(
                    node.data?.imageSequenceLabel || node.data?.label || '',
                    nextNode.type === 'imageNode' ? '图片' : '节点',
                );
                nextNode.data = {
                    ...(nextNode.data || {}),
                    imageSequenceLabel: duplicatedTitle,
                    ...(nextNode.type === 'imageNode' ? { label: duplicatedTitle } : {}),
                };
            }
            if (nextNode.type !== 'groupNode') {
                nextNode.data = withNodeImageSequence({
                    ...nextNode,
                    data: nextNode.data,
                }, nextImageSequence).data;
                nextImageSequence += 1;
            }

            if (parentIsCopied) {
                nextNode.parentId = nodeIdMap.get(nextNode.parentId);
            } else {
                delete nextNode.parentId;
                delete nextNode.extent;
                delete nextNode.expandParent;
                nextNode.position = {
                    x: absolutePosition.x + deltaX,
                    y: absolutePosition.y + deltaY,
                };
            }

            return nextNode;
        });

        const nextEdges = clipboard.edges.map((edge) => ({
            ...cloneValue(edge),
            id: edgeIdMap.get(edge.id),
            source: nodeIdMap.get(edge.source),
            target: nodeIdMap.get(edge.target),
            selected: false,
        }));

        const mergedNodes = beforeNodes.map((node) => ({ ...node, selected: false })).concat(nextNodes);
        const mergedEdges = beforeEdges.map((edge) => ({ ...edge, selected: false })).concat(nextEdges);
        const syncedMergedNodes = syncConnectedTargetNodes(mergedNodes, mergedEdges);

        setNodes(syncedMergedNodes);
        setEdges(mergedEdges);
        if (!options.skipNextImageSequenceUpdate) {
            setNextImageNodeNumber(nextImageSequence);
        }
        if (!options.skipPersist) {
            persistCanvasBatch({
                beforeNodes,
                beforeEdges,
                afterNodes: syncedMergedNodes,
                afterEdges: mergedEdges,
                projectId: options.projectId,
            });
        }

        return true;
    }, [persistCanvasBatch, pushHistory, setEdges, setNodes]);

    const handleApplyTemplateToCanvas = useCallback(async (templateItem) => {
        const templateId = String(templateItem?.id || '');
        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;

        if (!templateId) {
            message.warning('缺少模板 ID');
            return false;
        }
        if (!reactFlow || !canvas) return false;

        const projectContext = await ensureCanvasProject();

        try {
            const templateDetail = await getFlowTemplateDetail(templateId);
            const clipboard = buildTemplateClipboardFromDetail(templateDetail);
            const bounds = canvas.getBoundingClientRect();
            const flowPosition = reactFlow.screenToFlowPosition({
                x: bounds.left + bounds.width / 2,
                y: bounds.top + bounds.height / 2,
            });
            const applied = cloneClipboardToCanvas(clipboard, {
                flowPosition,
                projectId: projectContext?.projectId,
            });

            if (applied) {
                setTemplateLibraryOpen(false);
            }

            return applied;
        } catch (error) {
            console.error(error);
            message.error(error?.message || '应用模板失败');
            throw error;
        }
    }, [cloneClipboardToCanvas, ensureCanvasProject]);

    // 执行节点或连线删除，并修正受影响的关联数据
    const performDelete = useCallback((nodeIds, edgeIds = [], options = {}) => {
        const { forceNodeIds = [] } = options;
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const forcedNodeIdSet = new Set(forceNodeIds || []);
        const deletableNodeIds = new Set((nodeIds || []).filter((nodeId) => (
            forcedNodeIdSet.has(nodeId) || isNodeDeletable(nodeId)
        )));
        const deletableEdgeIds = new Set((edgeIds || []).filter((edgeId) => !isEdgeLocked(edgeId)));

        if (deletableNodeIds.size === 0 && deletableEdgeIds.size === 0) return;

        if (activeGroupId && deletableNodeIds.has(activeGroupId)) {
            setActiveGroupId(null);
        }

        let nextNodes = currentNodes.filter((node) => !deletableNodeIds.has(node.id));
        const nextEdges = currentEdges.filter((edge) => (
            !deletableEdgeIds.has(edge.id)
            && !deletableNodeIds.has(edge.source)
            && !deletableNodeIds.has(edge.target)
        ));
        const affectedTargetNodeIds = Array.from(new Set(
            currentEdges
                .filter((edge) => (
                    !nextEdges.some((nextEdge) => nextEdge.id === edge.id)
                    && !deletableNodeIds.has(edge.target)
                ))
                .map((edge) => edge.target),
        ));

        affectedTargetNodeIds.forEach((targetNodeId) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, nextEdges, { targetNodeId });
        });

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [activeGroupId, isEdgeLocked, isNodeDeletable, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 删除分组前弹出二次确认
    const confirmDeleteGroupsIfNeeded = useCallback((groupNodeIds, onConfirm) => {
        if (!groupNodeIds.length) {
            onConfirm?.();
            return;
        }

        const groupNodes = nodesRef.current.filter((node) => groupNodeIds.includes(node.id));
        const groupLabels = groupNodes.map((node) => node.data?.label || '组合');
        const title = groupLabels.length === 1 ? `确认删除${groupLabels[0]}吗？` : `确认删除这${groupLabels.length}个组合吗？`;
        const content = '删除组合会同时删除组内节点和连线，此操作不可恢复。';

        Modal.confirm({
            title,
            content,
            okText: '删除',
            cancelText: '取消',
            okButtonProps: { danger: true },
            onOk: () => onConfirm?.(),
        });
    }, []);

    // 删除当前选中的节点或连线
    const handleDeleteSelection = useCallback(() => {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const selectedNodeIds = getDescendantNodeIds(
            currentNodes.filter((node) => node.selected).map((node) => node.id),
            currentNodes,
        );
        const deletableNodeIds = Array.from(selectedNodeIds).filter((nodeId) => isNodeDeletable(nodeId));
        const selectedEdgeIds = Array.from(
            currentEdges
                .filter((edge) => edge.selected && !isEdgeLocked(edge.id))
                .map((edge) => edge.id),
        );

        if (deletableNodeIds.length === 0 && selectedEdgeIds.length === 0) return;

        const groupNodeIds = deletableNodeIds.filter((nodeId) => {
            const node = currentNodes.find((item) => item.id === nodeId);
            return isGroupNode(node);
        });
        const forcedGroupDescendantNodeIds = Array.from(getDescendantNodeIds(groupNodeIds, currentNodes));
        const finalNodeIds = Array.from(new Set(deletableNodeIds.concat(forcedGroupDescendantNodeIds)));

        confirmDeleteGroupsIfNeeded(groupNodeIds, () => {
            performDelete(finalNodeIds, selectedEdgeIds, {
                forceNodeIds: forcedGroupDescendantNodeIds,
            });
        });
    }, [confirmDeleteGroupsIfNeeded, isEdgeLocked, isNodeDeletable, performDelete]);

    // 撤销上一步画布操作
    const handleUndo = useCallback(() => {
        const snapshot = historyRef.current.pop();
        if (!snapshot) return;
        const currentSnapshot = getSnapshot();
        redoHistoryRef.current.push(currentSnapshot);
        restoreSnapshot(snapshot);
        persistCanvasBatch({
            beforeNodes: currentSnapshot.nodes,
            beforeEdges: currentSnapshot.edges,
            afterNodes: snapshot.nodes,
            afterEdges: snapshot.edges,
        });
        pasteCountRef.current = 0;
    }, [getSnapshot, persistCanvasBatch, restoreSnapshot]);

    // 重做刚刚撤销的画布操作
    const handleRedo = useCallback(() => {
        const snapshot = redoHistoryRef.current.pop();
        if (!snapshot) return;
        const currentSnapshot = getSnapshot();
        pushHistory(currentSnapshot, { clearRedo: false });
        restoreSnapshot(snapshot);
        persistCanvasBatch({
            beforeNodes: currentSnapshot.nodes,
            beforeEdges: currentSnapshot.edges,
            afterNodes: snapshot.nodes,
            afterEdges: snapshot.edges,
        });
        pasteCountRef.current = 0;
    }, [getSnapshot, persistCanvasBatch, pushHistory, restoreSnapshot]);

    // 复制当前选中的节点集合
    const handleCopy = useCallback(() => {
        const currentNodes = nodesRef.current;
        const copiedNodes = currentNodes.filter((node) => node.selected);
        if (copiedNodes.length === 0) return;

        clipboardRef.current = stripGroupedClipboardExecution(
            buildClipboardPayload(copiedNodes.map((node) => node.id)),
        );
        pasteCountRef.current = 0;
    }, [buildClipboardPayload]);

    // 将剪贴板内容粘贴到画布
    const handlePaste = useCallback((flowPosition = null) => {
        cloneClipboardToCanvas(clipboardRef.current, { flowPosition });
    }, [cloneClipboardToCanvas]);

    // 删除指定节点，分组节点会连带处理子节点
    const handleDeleteNode = useCallback((nodeId) => {
        const targetNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!targetNode) return;
        if (!isNodeDeletable(nodeId)) return;

        const selectedNodeIds = Array.from(getDescendantNodeIds([nodeId], nodesRef.current));
        const confirmAndDelete = () => performDelete(selectedNodeIds, [], {
            forceNodeIds: isGroupNode(targetNode) ? selectedNodeIds : [],
        });

        if (isGroupNode(targetNode)) {
            confirmDeleteGroupsIfNeeded([nodeId], confirmAndDelete);
            return;
        }

        confirmAndDelete();
    }, [confirmDeleteGroupsIfNeeded, isNodeDeletable, performDelete]);

    // 复制单个节点到剪贴板
    const handleCopyNode = useCallback((nodeId) => {
        clipboardRef.current = stripGroupedClipboardExecution(
            buildClipboardPayload([nodeId]),
        );
        pasteCountRef.current = 0;
    }, [buildClipboardPayload]);

    // 基于单个节点快速创建副本
    const handleDuplicateNode = useCallback((nodeId) => {
        const sourceNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!sourceNode) return;

        const clipboard = stripGroupedClipboardExecution(
            buildClipboardPayload([nodeId]),
        );

        cloneClipboardToCanvas(clipboard, {
            flowPosition: {
                x: sourceNode.position.x + DUPLICATE_OFFSET_STEP,
                y: sourceNode.position.y + DUPLICATE_OFFSET_STEP,
            },
        });
    }, [buildClipboardPayload, cloneClipboardToCanvas]);

    // 基于选中节点批量创建副本
    const handleDuplicateSelection = useCallback((nodeIds) => {
        const normalizedNodeIds = Array.from(new Set((nodeIds || []).filter(Boolean)));
        if (normalizedNodeIds.length === 0 || areNodesLocked(normalizedNodeIds)) return;

        const sourceNodes = nodesRef.current.filter((node) => normalizedNodeIds.includes(node.id));
        if (!sourceNodes.length) return;

        const clipboard = buildClipboardPayload(normalizedNodeIds);
        const minX = Math.min(...sourceNodes.map((node) => node.position.x));
        const minY = Math.min(...sourceNodes.map((node) => node.position.y));

        cloneClipboardToCanvas(clipboard, {
            flowPosition: {
                x: minX + DUPLICATE_OFFSET_STEP,
                y: minY + DUPLICATE_OFFSET_STEP,
            },
        });
    }, [areNodesLocked, buildClipboardPayload, cloneClipboardToCanvas]);

    // 解析当前拖拽场景下应该复制哪些顶层节点
    const getDragDuplicateSourceNodeIds = useCallback((draggedNodeId) => {
        const currentNodes = nodesRef.current;
        const draggedNode = currentNodes.find((node) => node.id === draggedNodeId);
        if (!draggedNode) return [];

        const selectedTopLevelNodeIds = currentNodes
            .filter((node) => node.selected && !node.parentId)
            .map((node) => node.id);

        if (draggedNode.selected && selectedTopLevelNodeIds.length > 0) {
            return selectedTopLevelNodeIds;
        }

        return [draggedNodeId];
    }, []);

    // 复制节点调试所需的任务信息
    const handleCopyTaskId = useCallback(async (nodeId) => {
        const targetNode = nodesRef.current.find((node) => node.id === nodeId);
        if (!targetNode) {
            message.error('未找到当前节点');
            return;
        }

        const executionId = targetNode.data?.latestExecution?.executionId
            || targetNode.latestExecution?.executionId
            || '';
        const taskText = [
            `projectId: ${activeId || ''}`,
            `nodeId: ${targetNode.id || ''}`,
            `executionId: ${executionId}`,
        ].join('\n');

        try {
            const copied = await copyTextToClipboard(taskText);
            if (!copied) {
                message.error('复制失败');
                return;
            }
 
            message.success('复制成功！');
        } catch (error) {
            message.error('复制失败');
        }
    }, [activeId]);

    // 从画布移除连线，并同步清理目标节点引用
    const removeEdgesFromCanvas = useCallback((edgeIds, options = {}) => {
        const normalizedEdgeIds = Array.from(new Set((edgeIds || []).filter(Boolean)));
        if (normalizedEdgeIds.length === 0) return;
        if (normalizedEdgeIds.some((edgeId) => isEdgeLocked(edgeId))) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const removedEdgeIdSet = new Set(normalizedEdgeIds);
        const removedEdges = beforeEdges.filter((edge) => removedEdgeIdSet.has(edge.id));
        const nextEdges = beforeEdges.filter((edge) => !removedEdgeIdSet.has(edge.id));

        if (nextEdges.length === beforeEdges.length) return;

        let nextNodes = beforeNodes;

        removedEdges.forEach((targetEdge) => {
            nextNodes = syncConnectedTargetNodes(nextNodes, nextEdges, {
                sourceNodeId: targetEdge.source,
                targetNodeId: targetEdge.target,
            });

            if (!targetEdge?.target) return;

            const targetNodeId = String(targetEdge.target);
            const fallbackSlotKey = targetEdge.data?.inputSlotKey || '';
            const previousTargetNode = beforeNodes.find((node) => String(node.id) === targetNodeId);
            const previousLinkedSlotSources = previousTargetNode?.data?.panelState?.linkedSlotSources
                && typeof previousTargetNode.data.panelState.linkedSlotSources === 'object'
                && !Array.isArray(previousTargetNode.data.panelState.linkedSlotSources)
                ? previousTargetNode.data.panelState.linkedSlotSources
                : {};
            const isTemplateReplaceNode = previousTargetNode?.data?.functionKey === 'templateReplace';
            const matchedSlotKeys = Object.entries(previousLinkedSlotSources)
                .filter(([, linkedState]) => String(linkedState?.edgeId || '') === String(targetEdge.id))
                .map(([linkedSlotKey]) => linkedSlotKey);
            const slotKeysToClear = isTemplateReplaceNode
                ? []
                : (
                    matchedSlotKeys.length > 0
                        ? matchedSlotKeys
                        : (fallbackSlotKey ? [fallbackSlotKey] : [])
                );

            nextNodes = nextNodes.map((node) => {
                if (String(node.id) !== targetNodeId || node.type !== 'functionNode') {
                    return node;
                }

                const previousPanelState = node.data?.panelState || {};
                const previousUploads = previousPanelState.uploads && typeof previousPanelState.uploads === 'object'
                    ? previousPanelState.uploads
                    : {};
                const currentLinkedSlotSources = previousPanelState.linkedSlotSources
                    && typeof previousPanelState.linkedSlotSources === 'object'
                    && !Array.isArray(previousPanelState.linkedSlotSources)
                    ? previousPanelState.linkedSlotSources
                    : {};
                if (slotKeysToClear.length === 0) {
                    return node;
                }

                const nextUploads = {
                    ...previousUploads,
                };
                const nextInputParams = {
                    ...(node.data?.inputParams || {}),
                };
                const nextLinkedSlotSources = {
                    ...currentLinkedSlotSources,
                };

                slotKeysToClear.forEach((linkedSlotKey) => {
                    const paramKey = getSlotParamKey(node.data?.functionKey, linkedSlotKey);
                    nextUploads[linkedSlotKey] = '';
                    if (paramKey) {
                        nextInputParams[paramKey] = '';
                    }
                    delete nextLinkedSlotSources[linkedSlotKey];
                });

                return {
                    ...node,
                    data: {
                        ...node.data,
                        inputParams: nextInputParams,
                        panelState: {
                            ...previousPanelState,
                            uploads: nextUploads,
                            linkedSlotSources: nextLinkedSlotSources,
                        },
                    },
                };
            });
        });

        pushHistory();
        setNodes(nextNodes);
        setEdges(options.edgeChanges
            ? applyEdgeChanges(options.edgeChanges, nextEdges)
            : nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isEdgeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 删除单条连线
    const handleDeleteEdge = useCallback((edgeId) => {
        removeEdgesFromCanvas([edgeId]);
    }, [removeEdgesFromCanvas]);

    // 统一处理 ReactFlow 的连线变更事件
    const handleEdgesChange = useCallback((changes) => {
        const removeIds = changes
            .filter((change) => change.type === 'remove')
            .map((change) => change.id)
            .filter(Boolean);
        const nonRemoveChanges = changes.filter((change) => change.type !== 'remove');

        if (removeIds.length > 0) {
            removeEdgesFromCanvas(removeIds, { edgeChanges: nonRemoveChanges });
            return;
        }

        onEdgesChange(changes);
    }, [onEdgesChange, removeEdgesFromCanvas]);

    // 删除功能节点槽位上通过连线关联的图片
    const handleRemoveFunctionLinkedUpload = useCallback((targetNodeId, slotKey, linkedState) => {
        if (!targetNodeId || !slotKey) return;
        const targetNode = nodesRef.current.find((node) => node.id === targetNodeId);
        if (!targetNode || isNodeLocked(targetNodeId)) return;

        const fallbackEdge = edgesRef.current.find((edge) => (
            String(edge.target) === String(targetNodeId)
            && String(edge.data?.inputSlotKey || '') === String(slotKey)
            && (
                !linkedState?.sourceNodeId
                || String(edge.source) === String(linkedState.sourceNodeId)
            )
        ));
        const resolvedEdgeId = linkedState?.edgeId || fallbackEdge?.id || '';
        if (!resolvedEdgeId) return;

        handleDeleteEdge(resolvedEdgeId);
    }, [handleDeleteEdge, isNodeLocked]);

    // 删除创意节点上通过连线关联的图片
    const handleRemoveCreativeLinkedUpload = useCallback((targetNodeId, linkedState) => {
        if (!targetNodeId) return;
        const targetNode = nodesRef.current.find((node) => node.id === targetNodeId);
        if (!targetNode || isNodeLocked(targetNodeId)) return;

        const resolvedEdgeId = linkedState?.edgeId || '';
        if (!resolvedEdgeId) return;

        handleDeleteEdge(resolvedEdgeId);
    }, [handleDeleteEdge, isNodeLocked]);

    // 替换创意图生图里的连线上传框图片，仅更新该位置 URL，不解除连线
    const handleReplaceCreativeLinkedUpload = useCallback((targetNodeId, linkedState, uploadedUrls = [], targetIndex = 0) => {
        if (!targetNodeId || !linkedState?.edgeId) return;
        if (isNodeLocked(targetNodeId) || isEdgeLocked(linkedState.edgeId)) return;

        const validUploadedUrls = (Array.isArray(uploadedUrls) ? uploadedUrls : [])
            .filter(Boolean)
            .slice(0, FUNCTION_NODE_INPUT_LIMITS.creativeImg2Img);
        if (validUploadedUrls.length === 0) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => String(node.id) === String(targetNodeId));
        if (!targetNode || targetNode.type !== 'functionNode' || !isMultiImageFunctionKey(targetNode.data?.functionKey)) return;

        const targetEdgeId = String(linkedState.edgeId);
        const targetEdge = beforeEdges.find((edge) => String(edge.id) === targetEdgeId);
        if (!targetEdge) return;

        const previousPanelState = targetNode.data?.panelState || {};
        const previousLinkedUploads = Array.isArray(previousPanelState.linkedUploads)
            ? previousPanelState.linkedUploads
            : [];
        const previousManualUploads = Array.isArray(previousPanelState.manualUploads)
            ? previousPanelState.manualUploads.filter(Boolean)
            : extractCreativeImgManualUploads(
                Array.isArray(previousPanelState.uploads)
                    ? previousPanelState.uploads
                    : (Array.isArray(targetNode.data?.inputParams?.imageUrls)
                        ? targetNode.data.inputParams.imageUrls
                        : []),
                previousLinkedUploads,
            );
        const linkedUploadIndex = previousLinkedUploads.findIndex((item) => String(item?.edgeId || '') === targetEdgeId);
        if (linkedUploadIndex < 0) return;

        const nextLinkedUploads = previousLinkedUploads.map((item, index) => (
            index === linkedUploadIndex
                ? {
                    ...item,
                    url: validUploadedUrls[0],
                }
                : item
        ));
        const previousOrder = Array.isArray(previousPanelState.uploadItems)
            ? previousPanelState.uploadItems
            : Array.isArray(previousPanelState.uploads)
            ? previousPanelState.uploads.filter(Boolean)
            : [];
        const fallbackOrder = mergeCreativeImgUploads(previousManualUploads, previousLinkedUploads, previousOrder);
        const replaceIndex = Math.min(
            Math.max(Number.isFinite(targetIndex) ? targetIndex : 0, 0),
            Math.max(fallbackOrder.length - 1, 0),
        );
        const nextMergedUploads = fallbackOrder.slice();
        nextMergedUploads[replaceIndex] = validUploadedUrls[0];
        const fallbackUploadItems = fallbackOrder.map((url) => {
            const linkedUpload = previousLinkedUploads.find((item) => item?.url === url);
            return linkedUpload?.edgeId
                ? { type: 'linked', edgeId: linkedUpload.edgeId, url }
                : { type: 'manual', url };
        });
        const nextUploadItems = (Array.isArray(previousPanelState.uploadItems)
            ? previousPanelState.uploadItems.slice()
            : fallbackUploadItems
        );
        nextUploadItems[replaceIndex] = {
            type: 'linked',
            edgeId: targetEdgeId,
            url: validUploadedUrls[0],
        };
        const updatedNodes = beforeNodes.map((node) => {
            if (String(node.id) !== String(targetNodeId)) return node;

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: {
                        ...(node.data?.inputParams || {}),
                        imageUrls: nextMergedUploads,
                        srcImage: nextMergedUploads[0] || '',
                        refImage: nextMergedUploads[1] || '',
                    },
                    panelState: {
                        ...previousPanelState,
                        manualUploads: previousManualUploads,
                        linkedUploads: nextLinkedUploads,
                        uploadItems: nextUploadItems,
                        uploads: nextMergedUploads,
                    },
                },
            };
        });

        pushHistory();
        setNodes(updatedNodes);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: updatedNodes,
            afterEdges: beforeEdges,
        });
    }, [isEdgeLocked, isNodeLocked, persistCanvasBatch, pushHistory, setNodes]);

    // 调整连线绑定到目标节点的输入槽位
    const handleEdgeSlotChange = useCallback((edgeId, nextSlotKey) => {
        if (isEdgeLocked(edgeId)) return;
        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetEdge = beforeEdges.find((edge) => edge.id === edgeId);
        if (!targetEdge) return;

        const targetNode = beforeNodes.find((node) => node.id === targetEdge.target);
        const slotKeys = getFunctionNodeEdgeSlotKeys(targetNode?.data?.functionKey, targetNode);
        if (slotKeys.length === 0) return;
        if (
            targetNode?.data?.functionKey === 'templateReplace'
            && nextSlotKey === 'reference'
            && getTemplateReplaceReferenceManualCount(targetNode) >= (FUNCTION_NODE_INPUT_LIMITS.templateReplace - 1)
        ) {
            message.warning('参考图最多支持13张，已满无法切换');
            return;
        }
        if (isSlotManuallyOccupied(targetNode, nextSlotKey)) {
            message.warning('该上传框已手动上传图片，无法连接');
            return;
        }

        const nextEdges = reconcileEdgeSlotAssignment({
            edges: beforeEdges,
            edgeId,
            targetId: targetEdge.target,
            slotKeys,
            preferredSlotKey: nextSlotKey,
        });
        const nextNodes = syncConnectedTargetNodes(beforeNodes, nextEdges, {
            sourceNodeId: targetEdge.source,
            targetNodeId: targetEdge.target,
        });

        if (
            JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
            && JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
        ) return;

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isEdgeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    const handleSwapFunctionInputSlots = useCallback((targetNodeId, fromSlotKey, toSlotKey) => {
        if (!targetNodeId || !fromSlotKey || !toSlotKey || fromSlotKey === toSlotKey) return;
        if (isNodeLocked(targetNodeId)) return;

        const beforeNodes = nodesRef.current;
        const beforeEdges = edgesRef.current;
        const targetNode = beforeNodes.find((node) => String(node.id) === String(targetNodeId));
        if (!targetNode || targetNode.type !== 'functionNode') return;

        const fromParamKey = getSlotParamKey(targetNode.data?.functionKey, fromSlotKey);
        const toParamKey = getSlotParamKey(targetNode.data?.functionKey, toSlotKey);

        const nextEdges = beforeEdges.map((edge) => {
            if (String(edge.target) !== String(targetNodeId)) return edge;

            const currentSlotKey = String(edge.data?.inputSlotKey || '');
            if (currentSlotKey !== String(fromSlotKey) && currentSlotKey !== String(toSlotKey)) {
                return edge;
            }

            const nextSlotKey = currentSlotKey === String(fromSlotKey)
                ? String(toSlotKey)
                : String(fromSlotKey);

            return {
                ...edge,
                data: {
                    ...(edge.data || {}),
                    inputSlotKey: nextSlotKey,
                },
            };
        });

        const nextNodesDraft = beforeNodes.map((node) => {
            if (String(node.id) !== String(targetNodeId) || node.type !== 'functionNode') return node;

            const previousPanelState = node.data?.panelState || {};
            const previousUploads = previousPanelState.uploads && typeof previousPanelState.uploads === 'object'
                ? previousPanelState.uploads
                : {};
            const previousLinkedSlotSources = (
                previousPanelState.linkedSlotSources
                && typeof previousPanelState.linkedSlotSources === 'object'
                && !Array.isArray(previousPanelState.linkedSlotSources)
            ) ? previousPanelState.linkedSlotSources : {};
            const previousInputParams = node.data?.inputParams || {};

            const nextUploads = {
                ...previousUploads,
                [fromSlotKey]: previousUploads[toSlotKey] ?? '',
                [toSlotKey]: previousUploads[fromSlotKey] ?? '',
            };

            const nextLinkedSlotSources = {
                ...previousLinkedSlotSources,
                [fromSlotKey]: previousLinkedSlotSources[toSlotKey],
                [toSlotKey]: previousLinkedSlotSources[fromSlotKey],
            };

            if (nextLinkedSlotSources[fromSlotKey] == null) {
                delete nextLinkedSlotSources[fromSlotKey];
            }
            if (nextLinkedSlotSources[toSlotKey] == null) {
                delete nextLinkedSlotSources[toSlotKey];
            }

            const nextInputParams = {
                ...previousInputParams,
            };

            if (fromParamKey || toParamKey) {
                const fromValue = fromParamKey ? previousInputParams[fromParamKey] : undefined;
                const toValue = toParamKey ? previousInputParams[toParamKey] : undefined;

                if (fromParamKey) nextInputParams[fromParamKey] = toValue ?? '';
                if (toParamKey) nextInputParams[toParamKey] = fromValue ?? '';
            }

            return {
                ...node,
                data: {
                    ...node.data,
                    inputParams: nextInputParams,
                    panelState: {
                        ...previousPanelState,
                        uploads: nextUploads,
                        linkedSlotSources: nextLinkedSlotSources,
                    },
                },
            };
        });

        const nextNodes = syncConnectedTargetNodes(nextNodesDraft, nextEdges, {
            targetNodeId: String(targetNodeId),
        });

        if (
            JSON.stringify(nextEdges) === JSON.stringify(beforeEdges)
            && JSON.stringify(nextNodes) === JSON.stringify(beforeNodes)
        ) return;

        pushHistory();
        setNodes(nextNodes);
        setEdges(nextEdges);
        persistCanvasBatch({
            beforeNodes,
            beforeEdges,
            afterNodes: nextNodes,
            afterEdges: nextEdges,
        });
    }, [isNodeLocked, persistCanvasBatch, pushHistory, setEdges, setNodes]);

    // 给节点注入渲染层需要的事件和状态
    const nodesWithHandlers = useMemo(() => {
        const nodeMap = new Map(nodes.map((node) => [String(node.id), node]));
        const topLevelOrderMap = new Map();
        const topLevelNodes = nodes.filter((node) => !node.parentId);
        const selectedTopLevelNodeIds = new Set(
            topLevelNodes
                .filter((node) => !!node.selected)
                .map((node) => String(node.id)),
        );

        topLevelNodes.forEach((node, index) => {
            const nodeId = String(node.id);
            const baseOrder = index;
            const elevatedOrder = selectedTopLevelNodeIds.has(nodeId)
                ? topLevelNodes.length + index
                : baseOrder;
            topLevelOrderMap.set(nodeId, elevatedOrder);
        });

        return nodes.map((node) => {
            const topLevelNodeId = getTopLevelNodeId(node, nodeMap);
            const topLevelOrder = topLevelOrderMap.get(topLevelNodeId) ?? 0;
            const zIndexBase = topLevelOrder * 10;

            return {
                ...node,
                zIndex: zIndexBase + (node.type === 'groupNode' ? 0 : 4),
                style: {
                    ...(node.style || {}),
                    zIndex: zIndexBase + (node.type === 'groupNode' ? 0 : 4),
                },
                draggable: node.type === 'groupNode'
                    ? (!isPmView && activeTool === 'select')
                    : (!isPmView && activeTool === 'select' && !isNodeLockedByGroup(node, lockedGroupIds)),
                data: {
                    ...node.data,
                    onChange: handleNodeDataChange,
                    onSwitchFunction: handleSwitchFunctionNode,
                    onExecute: handleExecuteNode,
                    onResultSelect: handleNodeResultSelect,
                    onQuickAddNode: handleQuickAddNode,
                    onGenerateComplete: handleNodeGenerateComplete,
                    onRemoveLinkedUpload: handleRemoveCreativeLinkedUpload,
                    onReplaceLinkedUpload: handleReplaceCreativeLinkedUpload,
                    onRemoveLinkedInput: handleRemoveFunctionLinkedUpload,
                    onSwapInputSlots: handleSwapFunctionInputSlots,
                    panelDismissToken,
                    projectId: activeId,
                    selectedNodeCount,
                    viewportInteracting,
                    interactionLocked: isPmView || isNodeLockedByGroup(node, lockedGroupIds),
                    readOnlyView: isPmView,
                    groupExecutionLocked: (
                        !!node.data?.groupExecutionLocked
                        || (!!node.parentId && lockedGroupIds.has(String(node.parentId)))
                    ),
                },
            };
        });
    }, [activeTool, handleExecuteNode, handleNodeDataChange, handleNodeGenerateComplete, handleNodeResultSelect, handleQuickAddNode, handleRemoveCreativeLinkedUpload, handleRemoveFunctionLinkedUpload, handleReplaceCreativeLinkedUpload, handleSwapFunctionInputSlots, handleSwitchFunctionNode, isPmView, lockedGroupIds, nodes, panelDismissToken, selectedNodeCount, viewportInteracting]);

    // 给连线注入渲染层需要的事件和状态
    const edgesWithHandlers = useMemo(() => edges.map((edge) => ({
        ...edge,
        data: {
            ...edge.data,
            onDeleteEdge: handleDeleteEdge,
            onSlotChange: handleEdgeSlotChange,
            interactionLocked: isPmView || isNodeLocked(edge.source) || isNodeLocked(edge.target),
        },
    })), [edges, handleDeleteEdge, handleEdgeSlotChange, isNodeLocked, isPmView]);

    // 获取当前选中的分组节点
    const getSelectedGroupNodes = useCallback(() => (
        nodesRef.current.filter((node) => selectedNodeIds.includes(node.id) && node.type === 'groupNode')
    ), [selectedNodeIds]);

    // 获取当前可参与编组的顶层节点
    const getSelectedGroupableNodes = useCallback(() => (
        nodesRef.current.filter((node) => selectedNodeIds.includes(node.id) && node.type !== 'groupNode' && !node.parentId)
    ), [selectedNodeIds]);

    // 判断当前选择是否满足“向已有分组追加节点”的条件
    const getSelectedAppendableGroup = useCallback(() => {
        const currentNodes = nodesRef.current;
        const selectedTopLevelNodes = currentNodes.filter((node) => (
            selectedNodeIds.includes(node.id) && !node.parentId
        ));
        const selectedGroupNodes = selectedTopLevelNodes.filter((node) => node.type === 'groupNode');
        const selectedLooseNodes = selectedTopLevelNodes.filter((node) => node.type !== 'groupNode');

        if (
            selectedGroupNodes.length !== 1
            || selectedLooseNodes.length < 1
            || selectedGroupNodes.length + selectedLooseNodes.length !== selectedTopLevelNodes.length
        ) {
            return null;
        }

        return {
            groupNode: selectedGroupNodes[0],
            looseNodes: selectedLooseNodes,
        };
    }, [selectedNodeIds]);

    // 将选中节点编组，或追加到已有分组
    const handleGroupSelection = useCallback(() => {
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const selectedTopLevelNodes = currentNodes.filter((node) => (
            selectedNodeIds.includes(node.id) && !node.parentId
        ));
        const groupableNodes = selectedTopLevelNodes.filter((node) => node.type !== 'groupNode');
        const appendableGroupSelection = getSelectedAppendableGroup();
        if (areNodesLocked(selectedTopLevelNodes.map((node) => node.id))) return false;

        if (appendableGroupSelection) {
            const { groupNode, looseNodes } = appendableGroupSelection;
            if (isGroupLocked(groupNode.id) || areNodesLocked(looseNodes.map((node) => node.id))) return false;
            const groupId = groupNode.id;
            const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
            const existingChildNodes = currentNodes.filter((node) => node.parentId === groupId);
            const mergedChildNodes = existingChildNodes.concat(looseNodes);
            const mergedStructureValidation = validateGroupFlowStructure(mergedChildNodes, currentEdges);

            if (!mergedStructureValidation.valid) {
                message.warning(mergedStructureValidation.message);
                return false;
            }

            const mergedChildIdSet = new Set(mergedChildNodes.map((node) => node.id));
            const bounds = mergedChildNodes.reduce((acc, node) => {
                const position = getNodeAbsolutePosition(node, nodeMap);
                const { width, height } = getNodeSize(node);

                return {
                    minX: Math.min(acc.minX, position.x),
                    minY: Math.min(acc.minY, position.y),
                    maxX: Math.max(acc.maxX, position.x + width),
                    maxY: Math.max(acc.maxY, position.y + height),
                };
            }, {
                minX: Number.POSITIVE_INFINITY,
                minY: Number.POSITIVE_INFINITY,
                maxX: Number.NEGATIVE_INFINITY,
                maxY: Number.NEGATIVE_INFINITY,
            });

            const nextGroupPosition = {
                x: bounds.minX - GROUP_PADDING_X,
                y: bounds.minY - GROUP_HEADER_HEIGHT,
            };
            const nextGroupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
            const nextGroupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);

            pushHistory();
            const nextNodes = [];

            currentNodes.forEach((node) => {
                if (node.id === groupId) {
                    nextNodes.push({
                        ...node,
                        selected: true,
                        position: nextGroupPosition,
                        width: nextGroupWidth,
                        height: nextGroupHeight,
                        measured: undefined,
                        style: {
                            ...(node.style || {}),
                            width: nextGroupWidth,
                            height: nextGroupHeight,
                            zIndex: 0,
                        },
                    });
                    return;
                }

                if (!mergedChildIdSet.has(node.id)) {
                    nextNodes.push({ ...node, selected: false });
                    return;
                }

                const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
                nextNodes.push({
                    ...node,
                    parentId: groupId,
                    extent: 'parent',
                    selected: false,
                    position: {
                        x: absolutePosition.x - nextGroupPosition.x,
                        y: absolutePosition.y - nextGroupPosition.y,
                    },
                });
            });

            const groupNodeIndex = nextNodes.findIndex((node) => node.id === groupId);
            if (groupNodeIndex > -1) {
                const [targetGroupNode] = nextNodes.splice(groupNodeIndex, 1);
                const childInsertIndex = nextNodes.findIndex((node) => node.parentId === groupId);
                nextNodes.splice(childInsertIndex > -1 ? childInsertIndex : nextNodes.length, 0, targetGroupNode);
            }

            setNodes(nextNodes);
            setActiveGroupId(groupId);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });
            return true;
        }

        if (groupableNodes.length < 2 || groupableNodes.length !== selectedTopLevelNodes.length) return false;
        const groupStructureValidation = validateGroupFlowStructure(groupableNodes, currentEdges);

        if (!groupStructureValidation.valid) {
            message.warning(groupStructureValidation.message);
            return false;
        }

        const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));
        const bounds = groupableNodes.reduce((acc, node) => {
            const position = getNodeAbsolutePosition(node, nodeMap);
            const { width, height } = getNodeSize(node);

            return {
                minX: Math.min(acc.minX, position.x),
                minY: Math.min(acc.minY, position.y),
                maxX: Math.max(acc.maxX, position.x + width),
                maxY: Math.max(acc.maxY, position.y + height),
            };
        }, {
            minX: Number.POSITIVE_INFINITY,
            minY: Number.POSITIVE_INFINITY,
            maxX: Number.NEGATIVE_INFINITY,
            maxY: Number.NEGATIVE_INFINITY,
        });

        const groupId = createNodeId('group-node');
        const nextGroupLabel = `组${nextGroupNodeNumberRef.current}`;
        const groupPosition = {
            x: bounds.minX - GROUP_PADDING_X,
            y: bounds.minY - GROUP_HEADER_HEIGHT,
        };
        const groupWidth = Math.max(GROUP_MIN_WIDTH, bounds.maxX - bounds.minX + (GROUP_PADDING_X * 2));
        const groupHeight = Math.max(GROUP_MIN_HEIGHT, bounds.maxY - bounds.minY + GROUP_HEADER_HEIGHT + GROUP_PADDING_BOTTOM);

        pushHistory();
        const nextNodes = (() => {
            const selectedIds = new Set(groupableNodes.map((node) => node.id));
            const nextGroupNode = {
                id: groupId,
                type: 'groupNode',
                position: groupPosition,
                selected: true,
                style: {
                    width: groupWidth,
                    height: groupHeight,
                    zIndex: 0,
                },
                data: {
                    label: nextGroupLabel,
                },
            };
            const untouchedNodes = [];
            const groupedChildNodes = [];

            currentNodes.forEach((node) => {
                if (!selectedIds.has(node.id)) {
                    untouchedNodes.push({ ...node, selected: false });
                    return;
                }

                const absolutePosition = getNodeAbsolutePosition(node, nodeMap);
                groupedChildNodes.push({
                    ...node,
                    parentId: groupId,
                    extent: 'parent',
                    selected: false,
                    position: {
                        x: absolutePosition.x - groupPosition.x,
                        y: absolutePosition.y - groupPosition.y,
                    },
                });
            });

            return untouchedNodes.concat(nextGroupNode, groupedChildNodes);
        })();

        setNodes(nextNodes);
        setActiveGroupId(groupId);
        setNextGroupNodeNumber((prev) => prev + 1);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: currentEdges,
        });
        return true;
    }, [areNodesLocked, getSelectedAppendableGroup, isGroupLocked, persistCanvasBatch, pushHistory, selectedNodeIds, setNodes]);

    // 解散选中的分组并还原子节点位置
    const handleUngroupSelection = useCallback((groupIds = null) => {
        const selectedGroupIds = groupIds
            ? Array.from(new Set(groupIds))
            : getSelectedGroupNodes().map((node) => node.id);

        if (selectedGroupIds.length === 0) return false;
        if (selectedGroupIds.some((groupId) => isGroupLocked(groupId))) return false;

        const targetGroupIds = new Set(selectedGroupIds);
        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const nodeMap = new Map(currentNodes.map((node) => [node.id, node]));

        if (activeGroupId && targetGroupIds.has(activeGroupId)) {
            setActiveGroupId(null);
        }
        pushHistory();
        const nextNodes = currentNodes
            .filter((node) => !targetGroupIds.has(node.id))
            .map((node) => {
                if (!node.parentId || !targetGroupIds.has(node.parentId)) {
                    return {
                        ...node,
                        selected: false,
                    };
                }

                const parentNode = nodeMap.get(node.parentId);
                return {
                    ...node,
                    selected: true,
                    position: {
                        x: node.position.x + (parentNode?.position.x ?? 0),
                        y: node.position.y + (parentNode?.position.y ?? 0),
                    },
                    parentId: undefined,
                    extent: undefined,
                    expandParent: undefined,
                };
            });

        setNodes(nextNodes);
        persistCanvasBatch({
            beforeNodes: currentNodes,
            beforeEdges: currentEdges,
            afterNodes: nextNodes,
            afterEdges: currentEdges,
        });
        return true;
    }, [activeGroupId, getSelectedGroupNodes, isGroupLocked, persistCanvasBatch, pushHistory, setNodes]);

    // 应用预设模板并刷新当前项目画布
    const handleSelectTemplate = useCallback(async (templateKey) => {
        const templateConfigMap = {
            faceSwap1: lookTemplate,
            lookImg: tryonTemplate,
        };
        const targetTemplate = templateConfigMap[templateKey];
        if (!targetTemplate) return;

        try {
            const { projectId } = await ensureCanvasProject();

            await applyTemplateToCanvas({
                projectId,
                version: getCurrentProjectVersion(projectId),
                templateNodes: targetTemplate.nodes,
                templateEdges: targetTemplate.edges,
            });

            // 刷新项目列表和项目详情
            loadProjectList({ pageIndex: 1, append: false, keyword: '' });
            const projectDetail = await getFlowProjectDetail(projectId);
            syncProjectVersion(projectId, projectDetail.version);
            setProjects((prev) => prev.map((project) => (
                project.id === projectId
                    ? mergeProjectRecord(project, {
                        ...project,
                        name: projectDetail.name || project.name,
                        cover: projectDetail.cover,
                        studioId: projectDetail.studioId,
                        version: projectDetail.version,
                    })
                    : project
            )));
            restoreProjectCanvas(projectDetail);
            clearGuideQueryParams();
        } catch (error) {
            console.error(error);
        }
    }, [clearGuideQueryParams, ensureCanvasProject, getCurrentProjectVersion, loadProjectList, restoreProjectCanvas, syncProjectVersion]);

    // 执行当前激活分组内的功能节点
    const handleRunGroup = useCallback(async () => {
        if (!activeId || !activeGroupId) return;
        if (isGroupLocked(activeGroupId)) return;

        const currentNodes = nodesRef.current;
        const currentEdges = edgesRef.current;
        const groupChildNodes = currentNodes.filter((node) => node.parentId === activeGroupId);
        const groupChildNodeIds = currentNodes
            .filter((node) => node.parentId === activeGroupId)
            .map((node) => node.id);
        const groupFunctionNodeIds = currentNodes
            .filter((node) => node.parentId === activeGroupId && node.type === 'functionNode')
            .map((node) => node.id);

        if (groupFunctionNodeIds.length === 0) {
            message.warning('当前分组内没有可执行的功能节点');
            return;
        }

        const validationError = groupChildNodes
            .map((node) => getNodeValidationError(node))
            .find(Boolean);
        if (validationError) {
            message.error(validationError);
            return;
        }

        const groupPollerKey = `group:${activeGroupId}`;
        stopExecutionPolling(groupPollerKey);
        groupFunctionNodeIds.forEach((nodeId) => stopNodeExecutionPolling(nodeId));

        setNodes((prev) => prev.map((node) => (
            node.id === activeGroupId || groupChildNodeIds.includes(node.id)
                ? {
                    ...node,
                    data: {
                        ...node.data,
                        groupExecutionLocked: true,
                        ...(node.id === activeGroupId ? { activeExecutionId: '' } : {}),
                        ...(groupFunctionNodeIds.includes(node.id)
                            ? {
                                executionStatus: 'idle',
                                latestExecution: null,
                                activeExecutionId: '',
                                selectedOutputId: '',
                            }
                            : {}),
                    },
                }
                : node
        )));

        try {
            const execution = await executeFlowGroup({
                projectId: activeId,
                groupNodeId: activeGroupId,
                manual: !runGroupSkipSelect,
            });
            const executionId = resolveExecutionIdentifier(execution);
            if (!executionId) {
                throw new Error('执行分组失败，未返回 executionId');
            }

            const initialNodeExecutionMap = new Map(
                (execution?.nodeExecutions || []).map((item) => [String(item?.nodeId ?? ''), {
                    ...item,
                    executionId: item?.executionId || executionId,
                }]),
            );
            const nextNodes = currentNodes.map((node) => {
                if (node.id !== activeGroupId && !groupChildNodeIds.includes(node.id)) return node;

                if (!groupFunctionNodeIds.includes(node.id)) {
                    return {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: true,
                            ...(node.id === activeGroupId ? { activeExecutionId: String(executionId) } : {}),
                        },
                    };
                }

                const nodeExecution = initialNodeExecutionMap.get(String(node.id)) || null;
                return {
                    ...node,
                    data: {
                        ...node.data,
                        groupExecutionLocked: true,
                        latestExecution: nodeExecution || null,
                        executionStatus: nodeExecution?.status || 'idle',
                        activeExecutionId: nodeExecution?.executionId || String(executionId),
                        selectedOutputId: nodeExecution
                            ? resolveNodeExecutionSelectedOutputId(
                                nodeExecution,
                                '',
                            )
                            : '',
                        },
                    };
            });
            setNodes(nextNodes);
            persistCanvasBatch({
                beforeNodes: currentNodes,
                beforeEdges: currentEdges,
                afterNodes: nextNodes,
                afterEdges: currentEdges,
            });

            pollGroupExecution(activeGroupId, executionId, groupFunctionNodeIds, groupChildNodeIds);
        } catch (error) {
            console.error(error);
            message.error(getErrorMessage(error, '执行分组失败'));
            setNodes(currentNodes.map((node) => (
                node.id === activeGroupId || groupChildNodeIds.includes(node.id)
                    ? {
                        ...node,
                        data: {
                            ...node.data,
                            groupExecutionLocked: false,
                            ...(node.id === activeGroupId ? { activeExecutionId: '' } : {}),
                            ...(groupFunctionNodeIds.includes(node.id) ? {
                                executionStatus: 'failed',
                                activeExecutionId: '',
                            } : {}),
                        },
                    }
                    : node
            )));
        }
    }, [activeGroupId, activeId, isGroupLocked, persistCanvasBatch, pollGroupExecution, runGroupSkipSelect, setNodes, stopExecutionPolling, stopNodeExecutionPolling]);

    // 打包下载当前分组内的结果图和素材图
    const handleBatchDownloadGroup = useCallback(async () => {
        if (!activeGroupId) return;
        setToolbarLoadingKey('download-group');

        const groupNodes = nodesRef.current.filter((node) => (
            node.id === activeGroupId || node.parentId === activeGroupId
        ));
        const resultItems = groupNodes
            .flatMap((node) => getNodeResultImageItems(node))
            .filter((item) => item?.url);
        const resultUrlSet = new Set(resultItems.map((item) => item.url));
        const sourceItems = groupNodes
            .flatMap((node) => getNodeSourceImageItems(node))
            .filter((item) => item?.url && !resultUrlSet.has(item.url));
        const downloadItems = resultItems.concat(sourceItems);
        const uniqueDownloadItems = Array.from(
            downloadItems.reduce((acc, item) => {
                const uniqueKey = `${item.category || 'source'}:${item.url}`;
                if (!acc.has(uniqueKey)) {
                    acc.set(uniqueKey, item);
                }
                return acc;
            }, new Map()).values(),
        );

        if (!uniqueDownloadItems.length) {
            message.warning('当前组合内暂无可下载图片');
            setToolbarLoadingKey('');
            return;
        }

        const groupNode = nodesRef.current.find((node) => node.id === activeGroupId);
        const zip = new JSZip();

        try {
            await Promise.all(uniqueDownloadItems.map(async (item, index) => {
                const response = await fetch(item.url);
                if (!response.ok) {
                    throw new Error('下载图片失败');
                }

                const blob = await response.blob();
                const fallbackName = getDownloadFileName(item.url, blob, `${item.nodeLabel || 'node'}-${index + 1}`);
                const extension = getExtensionFromBlob(blob, item.url);
                const baseName = fallbackName.replace(/\.[a-zA-Z0-9]+$/, '');
                const prefix = item.category === 'result' ? '结果图_' : '素材图_';
                const fileName = `${prefix}${baseName || `group-image-${index + 1}`}.${extension}`;
                zip.file(fileName, blob);
            }));

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            const zipName = `${groupNode?.data?.label || '组合'}-${Date.now()}.zip`;
            saveAs(zipBlob, zipName);
        } catch (error) {
            console.error(error);
            message.error('批量下载失败');
        }
        setToolbarLoadingKey('');
    }, [activeGroupId]);

    // 打包下载选中节点内的结果图和素材图
    const handleBatchDownloadSelection = useCallback(async (nodeIds = []) => {
        const normalizedNodeIds = Array.from(new Set((nodeIds || []).filter(Boolean)));
        if (!normalizedNodeIds.length) return;
        setToolbarLoadingKey('download-selection');

        const selectedNodes = nodesRef.current.filter((node) => normalizedNodeIds.includes(node.id));
        const resultItems = selectedNodes
            .flatMap((node) => getNodeResultImageItems(node))
            .filter((item) => item?.url);
        const resultUrlSet = new Set(resultItems.map((item) => item.url));
        const sourceItems = selectedNodes
            .flatMap((node) => getNodeSourceImageItems(node))
            .filter((item) => item?.url && !resultUrlSet.has(item.url));
        const downloadItems = resultItems.concat(sourceItems);
        const uniqueDownloadItems = Array.from(
            downloadItems.reduce((acc, item) => {
                const uniqueKey = `${item.category || 'source'}:${item.url}`;
                if (!acc.has(uniqueKey)) {
                    acc.set(uniqueKey, item);
                }
                return acc;
            }, new Map()).values(),
        );

        if (!uniqueDownloadItems.length) {
            message.warning('当前框选范围内暂无可下载图片');
            setToolbarLoadingKey('');
            return;
        }

        const zip = new JSZip();

        try {
            await Promise.all(uniqueDownloadItems.map(async (item, index) => {
                const response = await fetch(item.url);
                if (!response.ok) {
                    throw new Error('下载图片失败');
                }

                const blob = await response.blob();
                const fallbackName = getDownloadFileName(item.url, blob, `${item.nodeLabel || 'node'}-${index + 1}`);
                const extension = getExtensionFromBlob(blob, item.url);
                const baseName = fallbackName.replace(/\.[a-zA-Z0-9]+$/, '');
                const prefix = item.category === 'result' ? '结果图_' : '素材图_';
                const fileName = `${prefix}${baseName || `selection-image-${index + 1}`}.${extension}`;
                zip.file(fileName, blob);
            }));

            const zipBlob = await zip.generateAsync({ type: 'blob' });
            saveAs(zipBlob, `框选结果-${Date.now()}.zip`);
        } catch (error) {
            console.error(error);
            message.error('批量下载失败');
        }
        setToolbarLoadingKey('');
    }, []);

    // 调整画布缩放比例
    const handleZoomChange = useCallback((nextZoom) => {
        if (canvasEmpty) return;

        const clampedZoom = Math.max(MIN_ZOOM_PERCENT, Math.min(MAX_ZOOM_PERCENT, nextZoom));
        setZoom(clampedZoom);
        reactFlowRef.current?.zoomTo(clampedZoom / 100, { duration: 150 });
    }, [canvasEmpty]);

    // 处理滚轮缩放和平移交互
    const handleCanvasWheel = useCallback((event) => {
        const imagePreviewOpen = !!document.querySelector('.ant-image-preview-root');
        if (imagePreviewOpen) {
            setToolbarLoadingKey('');
            return;
        }

        if (canvasEmpty) return;
        if (!event.ctrlKey) {
            event.preventDefault();
            event.stopPropagation();
            const currentViewport = reactFlowRef.current?.getViewport?.() || viewportRef.current || DEFAULT_VIEWPORT;
            const nextViewport = {
                ...currentViewport,
                x: currentViewport.x - (event.deltaX * WHEEL_PAN_SPEED),
                y: currentViewport.y - (event.deltaY * WHEEL_PAN_SPEED),
            };

            reactFlowRef.current?.setViewport?.(nextViewport, { duration: 0 });
            scheduleViewportPersist(nextViewport);
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const direction = event.deltaY < 0 ? 1 : -1;
        const currentZoom = Math.round((reactFlowRef.current?.getZoom?.() ?? zoom / 100) * 100);
        const nextZoom = currentZoom + (direction * WHEEL_ZOOM_STEP);

        handleZoomChange(nextZoom);
    }, [canvasEmpty, handleZoomChange, scheduleViewportPersist, zoom]);

    useEffect(() => {
        const canvasElement = canvasRef.current;
        if (!canvasElement) return undefined;

        const nativeWheelHandler = (event) => {
            handleCanvasWheel(event);
        };

        canvasElement.addEventListener('wheel', nativeWheelHandler, { passive: false });

        return () => {
            canvasElement.removeEventListener('wheel', nativeWheelHandler);
        };
    }, [handleCanvasWheel]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (isEditableEvent(event)) return;

            if (event.code === 'Space' && !event.ctrlKey && !event.metaKey) {
                event.preventDefault();
                setIsSpacePressed(true);
                return;
            }

            const hasModifier = event.ctrlKey || event.metaKey;
            const key = event.key.toLowerCase();

            if (isPmView) {
                if (!hasModifier) return;

                const isZoomIn = event.key === '+' || event.key === '=' || event.code === 'NumpadAdd';
                const isZoomOut = event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract';

                if (!isZoomIn && !isZoomOut) return;

                event.preventDefault();
                event.stopPropagation();
                if (canvasEmpty) return;

                const currentZoom = Math.round((reactFlowRef.current?.getZoom?.() ?? zoom / 100) * 100);
                const nextZoom = currentZoom + (isZoomIn ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP);
                handleZoomChange(nextZoom);
                return;
            }

            if (!hasModifier && key === 'v') {
                if (shouldBlockToolSwitchShortcut()) return;
                event.preventDefault();
                event.stopPropagation();
                setActiveTool('select');
                return;
            }

            if (!hasModifier && key === 'h') {
                if (shouldBlockToolSwitchShortcut()) return;
                event.preventDefault();
                event.stopPropagation();
                setActiveTool('drag');
                return;
            }

            if (hasModifier && key === 'c') {
                event.preventDefault();
                event.stopPropagation();
                handleCopy();
                return;
            }

            if (hasModifier && key === 'v') {
                event.preventDefault();
                event.stopPropagation();
                handlePaste();
                return;
            }

            if (hasModifier && key === 'z') {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }
                return;
            }

            if (hasModifier && key === 'y') {
                event.preventDefault();
                event.stopPropagation();
                handleRedo();
                return;
            }

            if (hasModifier && key === 'g') {
                event.preventDefault();
                event.stopPropagation();
                if (event.shiftKey) {
                    handleUngroupSelection();
                } else {
                    handleGroupSelection();
                }
                return;
            }

            if (event.key === 'Delete' || event.key === 'Backspace') {
                event.preventDefault();
                event.stopPropagation();
                handleDeleteSelection();
                return;
            }

            if (!hasModifier) return;

            const isZoomIn = event.key === '+' || event.key === '=' || event.code === 'NumpadAdd';
            const isZoomOut = event.key === '-' || event.key === '_' || event.code === 'NumpadSubtract';

            if (!isZoomIn && !isZoomOut) return;

            event.preventDefault();
            event.stopPropagation();
            if (canvasEmpty) return;

            const currentZoom = Math.round((reactFlowRef.current?.getZoom?.() ?? zoom / 100) * 100);
            const nextZoom = currentZoom + (isZoomIn ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP);

            handleZoomChange(nextZoom);
        };

        const handleKeyUp = (event) => {
            if (event.code === 'Space') {
                setIsSpacePressed(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [canvasEmpty, handleCopy, handleDeleteSelection, handleGroupSelection, handlePaste, handleRedo, handleUndo, handleUngroupSelection, handleZoomChange, isPmView, zoom]);

    // 切换当前画布工具
    const handleToolChange = useCallback((toolKey) => {
        if (isPmView) return;
        setActiveTool(toolKey);
    }, [isPmView]);

    // 打开右键菜单并记录触发位置
    const openContextMenu = useCallback((event, scope, nodeId = null) => {
        if (isPmView) return;
        event.preventDefault();
        event.stopPropagation();

        const reactFlow = reactFlowRef.current;
        const canvas = canvasRef.current;
        const flowPosition = reactFlow && canvas
            ? reactFlow.screenToFlowPosition({ x: event.clientX, y: event.clientY })
            : null;

        setContextMenu({
            open: true,
            scope,
            x: event.clientX,
            y: event.clientY,
            nodeId,
            flowPosition,
        });
    }, [isPmView]);

    // 打开节点级右键菜单
    const handleNodeContextMenu = useCallback((event, node) => {
        openContextMenu(event, 'node', node.id);
    }, [openContextMenu]);

    // 点击节点时同步激活所属分组
    const handleNodeClick = useCallback((event, node) => {
        event.preventDefault();
        event.stopPropagation();
        closeContextMenu();

        if (node.type === 'groupNode') {
            setPanelDismissToken((prev) => prev + 1);
        }

        const groupTargetId = node.type === 'groupNode'
            ? node.id
            : node.parentId || null;

        if (!groupTargetId) {
            setActiveGroupId(null);
            return;
        }

        setActiveGroupId(groupTargetId);

        setNodes((prev) => prev.map((item) => {
            if (item.id === groupTargetId) {
                return {
                    ...item,
                    selected: true,
                };
            }

            if (item.parentId === groupTargetId) {
                return {
                    ...item,
                    selected: false,
                };
            }

            return {
                ...item,
                selected: false,
            };
        }));
    }, [closeContextMenu, setNodes]);

    // 打开画布空白区域右键菜单
    const handlePaneContextMenu = useCallback((event) => {
        openContextMenu(event, 'pane');
    }, [openContextMenu]);

    // 点击画布空白区域时收起附加状态
    const handlePaneClick = useCallback(() => {
        setSidebarCollapsed(true);
        closeContextMenu();
        setActiveGroupId(null);
        setPanelDismissToken((prev) => prev + 1);
    }, [closeContextMenu]);

    // 处理视口移动中的状态和持久化
    const handleMove = useCallback((event, viewport) => {
        setViewportInteracting(true);
        if (viewportInteractionTimerRef.current) {
            clearTimeout(viewportInteractionTimerRef.current);
        }
        viewportInteractionTimerRef.current = setTimeout(() => {
            setViewportInteracting(false);
            viewportInteractionTimerRef.current = null;
        }, 140);
        scheduleViewportPersist(viewport);
    }, [scheduleViewportPersist]);

    // 拖拽节点开始时记录快照
    const handleNodeDragStart = useCallback((event, node) => {
        const snapshot = getSnapshot();
        dragSnapshotRef.current = snapshot;

        const shouldDuplicateOnDrag = !!event?.altKey && !isPmView && !!node?.id;
        if (!shouldDuplicateOnDrag) {
            altDragDuplicateRef.current = null;
            return;
        }

        const sourceNodeIds = getDragDuplicateSourceNodeIds(node.id);
        if (sourceNodeIds.length === 0 || areNodesLocked(sourceNodeIds)) {
            altDragDuplicateRef.current = null;
            return;
        }

        altDragDuplicateRef.current = {
            anchorNodeId: String(node.id),
            sourceNodeIds,
            snapshot,
        };

        const previewClipboard = buildClipboardPayloadFromState(
            sourceNodeIds,
            snapshot.nodes,
            snapshot.edges,
        );
        cloneClipboardToCanvas(previewClipboard, {
            baseNodes: snapshot.nodes,
            baseEdges: snapshot.edges,
            positionDelta: { x: 0, y: 0 },
            skipHistory: true,
            skipPersist: true,
            skipNextImageSequenceUpdate: true,
            selectDuplicatedNodes: false,
        });
    }, [areNodesLocked, buildClipboardPayloadFromState, cloneClipboardToCanvas, getDragDuplicateSourceNodeIds, getSnapshot, isPmView]);

    // 拖拽节点结束后写入历史并保存位置
    const handleNodeDragStop = useCallback((event, node) => {
        if (!dragSnapshotRef.current) return;

        const before = dragSnapshotRef.current.nodes;
        const beforeEdges = dragSnapshotRef.current.edges;
        const after = nodesRef.current;
        const changed = before.some((node, index) => (
            node.position.x !== after[index]?.position?.x
            || node.position.y !== after[index]?.position?.y
        ));

        const altDragState = altDragDuplicateRef.current;
        if (altDragState?.anchorNodeId) {
            const beforeAnchorNode = before.find((item) => item.id === altDragState.anchorNodeId);
            const afterAnchorNode = after.find((item) => item.id === altDragState.anchorNodeId);
            const deltaX = (afterAnchorNode?.position?.x ?? 0) - (beforeAnchorNode?.position?.x ?? 0);
            const deltaY = (afterAnchorNode?.position?.y ?? 0) - (beforeAnchorNode?.position?.y ?? 0);

            altDragDuplicateRef.current = null;
            dragSnapshotRef.current = null;

            if (deltaX === 0 && deltaY === 0) {
                return;
            }

            const clipboard = buildClipboardPayloadFromState(
                altDragState.sourceNodeIds,
                before,
                beforeEdges,
            );
            cloneClipboardToCanvas(clipboard, {
                baseNodes: before,
                baseEdges: beforeEdges,
                historySnapshot: altDragState.snapshot,
                positionDelta: {
                    x: deltaX,
                    y: deltaY,
                },
            });
            return;
        }

        if (changed) {
            pushHistory(dragSnapshotRef.current);
            persistCanvasBatch({
                beforeNodes: before,
                beforeEdges,
                afterNodes: after,
                afterEdges: edgesRef.current,
            });
        }
        dragSnapshotRef.current = null;
        altDragDuplicateRef.current = null;
    }, [buildClipboardPayloadFromState, cloneClipboardToCanvas, persistCanvasBatch, pushHistory]);

    // 初始化 ReactFlow 实例引用
    const handleInit = useCallback((reactFlowInstance) => {
        reactFlowRef.current = reactFlowInstance;
        setZoom(Math.round(reactFlowInstance.getZoom() * 100));
    }, []);

    // 根据当前上下文生成右键菜单项
    const contextMenuItems = useMemo(() => {
        if (isPmView) return [];
        const pasteDisabled = !clipboardRef.current?.nodes?.length;
        const contextNode = contextMenu.nodeId
            ? nodesRef.current.find((node) => node.id === contextMenu.nodeId)
            : null;
        const contextNodeDeleteDisabled = contextNode ? !isNodeDeletable(contextNode.id) : false;
        const contextNodeModifyDisabled = contextNode ? isNodeLocked(contextNode.id) : false;

        if (contextMenu.scope === 'node' && contextMenu.nodeId) {
            return [
                { key: 'copy-node', label: '复制节点', shortcut: 'Ctrl C', disabled: false, onClick: () => handleCopyNode(contextMenu.nodeId) },
                { key: 'duplicate-node', label: '创建副本', disabled: contextNodeModifyDisabled, onClick: () => handleDuplicateNode(contextMenu.nodeId) },
                {
                    key: 'paste-node',
                    label: '粘贴节点',
                    shortcut: 'Ctrl V',
                    disabled: pasteDisabled,
                    onClick: () => handlePaste(contextMenu.flowPosition),
                },
                { key: 'divider-delete', type: 'divider' },
                { key: 'delete-node', label: '删除节点', shortcut: 'Delete', disabled: contextNodeDeleteDisabled, onClick: () => handleDeleteNode(contextMenu.nodeId) },
                { key: 'copy-task-id', label: '复制调试信息', onClick: () => handleCopyTaskId(contextMenu.nodeId) },
            ];
        }

        return [
            {
                key: 'add-node-title',
                type: 'title',
                label: '添加节点',
            },
            {
                key: 'add-image-node',
                label: '图片',
                icon: `${FREE_CANVAS_ICON_BASE_URL}/node-image.svg`,
                onClick: () => handleAddNode('image', {
                    flowPosition: contextMenu.flowPosition,
                    autoOpenFunctionPanel: true,
                }),
            },
            { key: 'divider-add-node', type: 'divider' },
            ...FUNCTION_NODE_GROUPS
                .filter((item) => FUNCTION_LABELS[item.key])
                .map((item) => ({
                    key: `function-${item.key}`,
                    label: item.label,
                    icon: item.icon,
                    onClick: () => handleAddNode(item.key, {
                        flowPosition: contextMenu.flowPosition,
                        autoOpenFunctionPanel: true,
                    }),
                })),
            { key: 'divider-paste', type: 'divider' },
            {
                key: 'paste',
                label: '粘贴',
                disabled: pasteDisabled,
                onClick: () => handlePaste(contextMenu.flowPosition),
            },
        ];
    }, [contextMenu.flowPosition, contextMenu.nodeId, contextMenu.scope, handleAddNode, handleCopyNode, handleCopyTaskId, handleDeleteNode, handleDuplicateNode, handlePaste, isNodeDeletable, isNodeLocked, isPmView]);

    // 计算分组操作浮层的展示状态和位置
    const groupActionToolbar = useMemo(() => {
        if (isPmView) {
            return {
                visible: false,
                nodeIds: [],
                position: null,
                items: [],
            };
        }
        const selectedGroupNodes = getSelectedGroupNodes();
        const selectedGroupableNodes = getSelectedGroupableNodes();
        const selectedTopLevelNodes = nodes.filter((node) => node.selected && !node.parentId);
        const appendableGroupSelection = getSelectedAppendableGroup();
        const activeGroupNode = activeGroupId
            ? nodes.find((node) => node.id === activeGroupId && node.type === 'groupNode')
            : null;

        if (appendableGroupSelection) {
            return {
                visible: false,
                nodeIds: [],
                position: null,
                items: [],
            };
        }

        if (activeGroupNode) {
            const groupNode = activeGroupNode;
            const groupLocked = isGroupLocked(groupNode.id);

            return {
                visible: true,
                nodeIds: [groupNode.id],
                position: null,
                items: [
                    {
                        key: 'run-group',
                        label: '整组执行',
                        icon: 'start',
                        popover: {
                            title: '跳过选图',
                            hint: '开启后，将直接执行全部任务，每个算法节点默认生成一张图片\n关闭后，按单节点内设置的是否选图执行',
                            checked: runGroupSkipSelect,
                            onChange: setRunGroupSkipSelect,
                        },
                        disabled: groupLocked,
                        onClick: handleRunGroup,
                    },
                    {
                        key: 'ungroup-group',
                        label: '解组',
                        icon: 'focus',
                        disabled: groupLocked,
                        onClick: () => handleUngroupSelection([groupNode.id]),
                    },
                    {
                        key: 'duplicate-group',
                        label: '创建副本',
                        icon: 'copy',
                        disabled: groupLocked,
                        onClick: () => handleDuplicateNode(groupNode.id),
                    },
                    {
                        key: 'save-group-template',
                        label: '存为模板',
                        icon: 'template',
                        disabled: groupLocked,
                        onClick: () => handleOpenSaveTemplateModal(groupNode.id),
                    },
                    {
                        key: 'download-group',
                        label: '批量下载',
                        icon: 'download',
                        loading: toolbarLoadingKey === 'download-group',
                        disabled: groupLocked,
                        onClick: handleBatchDownloadGroup,
                    },
                ],
            };
        }

        if (selectedGroupNodes.length === 0 && selectedGroupableNodes.length >= 2 && selectedGroupableNodes.length === selectedTopLevelNodes.length) {
            const createGroupDisabled = areNodesLocked(selectedGroupableNodes.map((node) => node.id));
            return {
                visible: true,
                nodeIds: selectedGroupableNodes.map((node) => node.id),
                position: (() => {
                    const reactFlow = reactFlowRef.current;
                    const canvas = canvasRef.current;
                    if (!reactFlow || !canvas) return null;

                    const bounds = selectedGroupableNodes.reduce((acc, node) => {
                        const { width, height } = getNodeSize(node);

                        return {
                            minX: Math.min(acc.minX, node.position.x),
                            minY: Math.min(acc.minY, node.position.y),
                            maxX: Math.max(acc.maxX, node.position.x + width),
                            maxY: Math.max(acc.maxY, node.position.y + height),
                        };
                    }, {
                        minX: Number.POSITIVE_INFINITY,
                        minY: Number.POSITIVE_INFINITY,
                        maxX: Number.NEGATIVE_INFINITY,
                        maxY: Number.NEGATIVE_INFINITY,
                    });

                    const canvasBounds = canvas.getBoundingClientRect();
                    const screenPosition = reactFlow.flowToScreenPosition({
                        x: (bounds.minX + bounds.maxX) / 2,
                        y: bounds.minY,
                    });

                    return {
                        x: Math.max(canvasBounds.left + 60, screenPosition.x),
                        y: screenPosition.y - 12,
                    };
                })(),
                items: [
                    {
                        key: 'duplicate-selection',
                        label: '创建副本',
                        icon: 'copy',
                        disabled: createGroupDisabled,
                        onClick: () => handleDuplicateSelection(selectedGroupableNodes.map((node) => node.id)),
                    },
                    {
                        key: 'download-selection',
                        label: '批量下载',
                        icon: 'download',
                        loading: toolbarLoadingKey === 'download-selection',
                        disabled: createGroupDisabled,
                        onClick: () => handleBatchDownloadSelection(selectedGroupableNodes.map((node) => node.id)),
                    },
                ],
            };
        }

        return {
            visible: false,
            nodeIds: [],
            position: null,
            items: [],
        };
    }, [
        activeGroupId,
        getSelectedGroupNodes,
        getSelectedGroupableNodes,
        getSelectedAppendableGroup,
        handleBatchDownloadGroup,
        handleBatchDownloadSelection,
        areNodesLocked,
        handleDuplicateSelection,
        handleDuplicateNode,
        handleGroupSelection,
        handleOpenSaveTemplateModal,
        handleRunGroup,
        handleUngroupSelection,
        isGroupLocked,
        nodes,
        runGroupSkipSelect,
        toolbarLoadingKey,
        isPmView,
    ]);

    const handleBack = useCallback(() => {
        if (typeof window !== 'undefined') {
            const referrer = document.referrer || '';
            const currentOrigin = window.location.origin || '';
            const hasInternalReferrer = !!referrer && referrer.startsWith(currentOrigin);

            if (hasInternalReferrer && window.history.length > 1) {
                navigate(-1);
                return;
            }
        }

        navigate('/home/homemain/creativeToImage', { replace: true });
    }, [navigate]);

    return (
        <div className={styles.container}>
            <ProjectHeader
                name={activeProject?.name || targetProjectName || DEFAULT_CANVAS_PROJECT_NAME}
                onNameChange={handleNameChange}
                onBack={handleBack}
                onOpenProjectList={() => setSidebarCollapsed(false)}
                onCreateProject={handleCreate}
                sidebarCollapsed={sidebarCollapsed}
                editable={!isPmView}
                createDisabled={isPmView}
            />
            <div className={styles.loggedHeadWrap}>
                <LoggedHead showPromoteButtons={false} />
            </div>
            <ZoomToolbar
                zoom={zoom}
                onZoomChange={handleZoomChange}
                onFitCanvas={handleFitCanvas}
            />
            {!isPmView ? (
                <CanvasToolbar
                    activeTool={activeTool}
                    onToolChange={handleToolChange}
                    onAction={handleAction}
                    onAddNode={(nodeType) => handleAddNode(nodeType, { autoOpenFunctionPanel: true })}
                />
            ) : null}
            <input
                ref={uploadInputRef}
                type="file"
                accept={UPLOAD_IMAGE_ACCEPT}
                multiple
                onChange={handleUploadFiles}
                style={{ display: 'none' }}
            />
            <Sidebar
                projects={projects}
                activeId={activeId}
                keyword={projectKeywordRef.current}
                onSelect={handleSelectProject}
                onCreate={handleCreate}
                onKeywordChange={handleProjectKeywordChange}
                onLoadMore={handleLoadMoreProjects}
                onPinProject={handlePinProject}
                onDuplicateProject={handleDuplicateProject}
                onSortProject={handleSortProject}
                onRenameProject={handleRenameProject}
                onDeleteProject={handleDeleteProject}
                hasMore={projectListHasMore}
                loading={projectListLoading}
                collapsed={sidebarCollapsed}
                onCollapsedChange={setSidebarCollapsed}
                readOnlyView={isPmView}
            />
            <div
                ref={canvasRef}
                className={styles.canvas}
                onDragOver={handleCanvasDragOver}
                onDrop={handleCanvasDrop}
            >
                <div className={`${styles.reactFlow} ${activeTool === 'drag' ? styles.dragMode : styles.selectMode} ${isSpacePressed ? styles.spaceMode : ''}`}>
                      <ReactFlow
                        nodes={nodesWithHandlers}
                        edges={edgesWithHandlers}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        defaultEdgeOptions={defaultEdgeOptions}
                        onNodesChange={onNodesChange}
                        onEdgesChange={handleEdgesChange}
                        onConnect={handleConnect}
                        onMove={handleMove}
                        onInit={handleInit}
                        onNodeClick={handleNodeClick}
                        onNodeContextMenu={handleNodeContextMenu}
                        onPaneClick={handlePaneClick}
                        onPaneContextMenu={handlePaneContextMenu}
                        onNodeDragStart={handleNodeDragStart}
                        onNodeDragStop={handleNodeDragStop}
                        nodesDraggable={!isPmView && activeTool === 'select'}
                        elementsSelectable={!isPmView && activeTool === 'select'}
                        nodesConnectable={!isPmView && activeTool === 'select' && !isCanvasExecutionLocked}
                        elevateNodesOnSelect={false}
                        elevateEdgesOnSelect={false}
                        deleteKeyCode={null}
                        panOnDrag={isPmView || activeTool === 'drag'}
                        zoomOnScroll={false}
                        zoomOnPinch={false}
                        preventScrolling={false}
                        selectionOnDrag={!isPmView && activeTool === 'select'}
                        selectionMode="partial"
                        connectionRadius={28}
                        connectionLineType={ConnectionLineType.Bezier}
                        connectionLineStyle={{ stroke: '#7D8DA1', strokeWidth: 2.5, opacity: 1 }}
                        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
                        minZoom={0.1}
                        maxZoom={2}
                        fitView={false}
                        proOptions={{ hideAttribution: true }}
                        className={styles.reactFlowCanvas}
                    >
                        <GroupActionToolbar
                            visible={groupActionToolbar.visible}
                            nodeIds={groupActionToolbar.nodeIds}
                            position={groupActionToolbar.position}
                            items={groupActionToolbar.items}
                        />
                        <Background
                            variant={BackgroundVariant.Dots}
                            gap={20}
                            size={1}
                            color="#DDDFE3"
                        />
                    </ReactFlow>
                </div>
                <CanvasContextMenu
                    open={!isPmView && contextMenu.open}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    items={contextMenuItems}
                    onClose={closeContextMenu}
                />
                {canvasEmpty && !isPmView ? (
                    <CanvasEmpty
                        onSelectExample={handleSelectCanvasExample}
                        onAddNode={handleAddNode}
                    />
                ) : null}
            </div>
            <GenerationHistoryModal
                open={historyModalOpen}
                projectId={activeId}
                onClose={() => setHistoryModalOpen(false)}
                onSend={handleSendHistoryImage}
                onBatchSend={handleBatchSendHistoryImages}
            />
            <MaterialLibraryModal
                open={materialLibraryOpen}
                onClose={() => setMaterialLibraryOpen(false)}
                onSelect={handleSelectMaterialLibraryImage}
                multiple
                maxSelectCount={10}
                showApplyAction={false}
            />
            <TemplateLibraryModal
                open={templateLibraryOpen}
                onClose={() => setTemplateLibraryOpen(false)}
                onApply={handleApplyTemplateToCanvas}
            />
            <Modal
                open={saveTemplateModalOpen}
                title="存为模板"
                onCancel={handleCloseSaveTemplateModal}
                footer={null}
                width={470}
                centered
                destroyOnClose={false}
                closable={!saveTemplateSubmitting}
                maskClosable={!saveTemplateSubmitting}
                className={styles.saveTemplateModal}
            >
                <div className={styles.saveTemplateContent}>
                    <div className={styles.saveTemplateFormRow}>
                        <div className={styles.saveTemplateLabel}>模板名称</div>
                        <Input
                            value={saveTemplateName}
                            onChange={(event) => setSaveTemplateName(event.target.value)}
                            placeholder="请填写模板名称"
                            maxLength={15}
                            className={styles.saveTemplateInput}
                        />
                    </div>
                    <Checkbox
                        checked={publishTemplateSync}
                        onChange={(event) => setPublishTemplateSync(event.target.checked)}
                        className={styles.saveTemplateCheckbox}
                    >
                        同步发布到公共模版库
                    </Checkbox>
                    <div className={styles.saveTemplateActions}>
                        <Button
                            className={styles.saveTemplateCancel}
                            onClick={handleCloseSaveTemplateModal}
                            disabled={saveTemplateSubmitting}
                        >
                            取消
                        </Button>
                        <Button
                            type="primary"
                            className={styles.saveTemplateConfirm}
                            loading={saveTemplateSubmitting}
                            onClick={handleSaveTemplate}
                        >
                            保存
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const FreeCanvas = () => (
    <ReactFlowProvider>
        <FreeCanvasInner />
    </ReactFlowProvider>
);

export default FreeCanvas;
